import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/api.config';
import api, { getFormData } from '../utils/api';
import DocumentHistoryButton from '../components/DocumentHistoryButton';
import {
  Plus, Save, ArrowLeft, Eye, Download, CheckCircle,
  Search, Receipt, FileText, Calculator, ChevronLeft, ChevronRight,
  DollarSign, Clock, AlertCircle, RefreshCw, CreditCard, X, Trash2,
  Upload, Paperclip, Link2
} from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import { validateForm } from '../utils/validation';
import { useAuth } from '../context/AuthContext';
import { generatePDF } from '../utils/pdfGenerator';
import LoadingSpinner, { PageLoader, TableSkeleton } from '../components/LoadingSpinner';
import { PaymentStatusBadge, calculatePaymentStatus, normalizePaymentData } from '../utils/paymentUtils';
import PaymentModal from '../components/PaymentModal';

import CancellationModal from '../components/CancellationModal';
import GSTBreakdown, { GSTSummary, CompactGSTBreakdown } from '../components/GSTBreakdown';
import { calculateTotalGSTBreakdown } from '../utils/gstUtils';
import { roundForCalculation } from '../utils/mathUtils';
import LastUpdatedIndicator from '../components/LastUpdatedIndicator';
import { paymentSync } from '../utils/paymentSync';
import { GST_STATE_CODES, getStateNameFromGST } from '../utils/gstUtils';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import AppModal, { modalTable, modalButton } from '../components/AppModal';
import InvoiceNumberField from '../components/InvoiceNumberField';
import TermsAndConditionsInput from '../components/TermsAndConditionsInput';
import FilterDropdown from '../components/FilterDropdown';
import AdvancedFilters from '../components/AdvancedFilters';
import { recalcItemsAmounts } from '../utils/itemAmounts';

// State options for dropdown
const stateOptions = Object.entries(GST_STATE_CODES).map(([code, name]) => ({
  value: name,
  label: name
})).sort((a, b) => a.label.localeCompare(b.label));

// Helper: compute qty for a woodwork item (Nos × L × B; if L or B is 0, qty = Nos)
const computeWoodWorkQty = (nos, length, breadth) => {
  const n = parseFloat(nos) || 0;
  const l = parseFloat(length) || 0;
  const b = parseFloat(breadth) || 0;
  if (l === 0 || b === 0) return n; // Tandon Boxes / hardware items
  return parseFloat((n * l * b).toFixed(4));
};


const InvoicesPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  // State for form/history toggle
  const [activeTab, setActiveTab] = useState('history'); // Default to history view
  const [isEditing, setIsEditing] = useState(false);
  const prefilledCustomer = location.state?.prefilledCustomer;

  // Form states
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [formData, setFormData] = useState({
    salesperson_id: '',
    customer: prefilledCustomer || {
      name: '',
      countryCode: '',
      phone: '',
      email: '',
      address: '',
      gstNumber: '',
      state: ''
    },
    dispatched_from: '',
    delivered_to: '',
    termsAndConditions: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [formTouched, setFormTouched] = useState(false);
  const [items, setItems] = useState([
    {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: '',
      gst_amount: 0,
      amount: 0,
      total_amount: 0,
      tax: 0,
      discount: '',
      subtotal: 0
    },
    {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: '',
      gst_amount: 0,
      amount: 0,
      total_amount: 0,
      tax: 0,
      discount: '',
      subtotal: 0
    },
    {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: '',
      gst_amount: 0,
      amount: 0,
      total_amount: 0,
      tax: 0,
      discount: '',
      subtotal: 0
    }
  ]);

  const [showDiscount, setShowDiscount] = useState(false);

  // History states
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  
  // Advanced Filters State
  const [filters, setFilters] = useState({
    dateRange: 'all',
    status: 'all',
    paymentStatus: 'all',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    isWoodWork: 'all',
    salesperson_id: 'all'
  });
  
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef(searchTerm);
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_type: 'cash',
    payment_status: 'pending',
    amount_received: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const currentPageRef = useRef(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  // Convert Estimate to Invoice states
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [estimateSearchTerm, setEstimateSearchTerm] = useState('');
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showEstimateDetailModal, setShowEstimateDetailModal] = useState(false);
  const [convertingEstimate, setConvertingEstimate] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    paymentType: 'cash',
    amountReceived: '',
    invoiceDescription: '',
    paymentStatus: 'pending',
    gstNumber: '',
    state: '',
    dispatchedFrom: '',
    deliveredTo: '',
    termsAndConditions: '',
    materialDescription: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [editMode, setEditMode] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstNumber: '',
    state: ''
  });
  const [showEditProductsModal, setShowEditProductsModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingProducts, setEditingProducts] = useState([]);
  const [editingApplyDiscount, setEditingApplyDiscount] = useState(false);
  const [isWoodWorkMode, setIsWoodWorkMode] = useState(false);
  const [includeTermsDirect, setIncludeTermsDirect] = useState(false);
  const [includeTermsEstimate, setIncludeTermsEstimate] = useState(false);
  const [includeTermsClosedOrder, setIncludeTermsClosedOrder] = useState(false);

  // Convert Closed Order to Invoice states
  const [showConvertClosedOrderModal, setShowConvertClosedOrderModal] = useState(false);
  const [closedOrders, setClosedOrders] = useState([]);
  const [filteredClosedOrders, setFilteredClosedOrders] = useState([]);
  const [closedOrderSearchTerm, setClosedOrderSearchTerm] = useState('');
  const [selectedClosedOrder, setSelectedClosedOrder] = useState(null);
  const [showClosedOrderDetailModal, setShowClosedOrderDetailModal] = useState(false);

  // Edit Invoice Modal states
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editSummaryMode, setEditSummaryMode] = useState(false);
  const [editModalContext, setEditModalContext] = useState('convert'); // 'convert' or 'edit'

  // Additional states for comprehensive conversion modal
  const [conversionEditingCustomer, setConversionEditingCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [conversionPaymentData, setConversionPaymentData] = useState({
    payment_type: 'cash',
    gst_number: '',
    description: '',
    termsAndConditions: '',
    materialDescription: '',
    date: new Date().toISOString().split('T')[0]
  });



  // Cancellation Modal states
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);

  // Payment Status Update Modal states (for cancelled invoices)
  const [showPaymentStatusUpdateModal, setShowPaymentStatusUpdateModal] = useState(false);
  const [paymentStatusUpdateLoading, setPaymentStatusUpdateLoading] = useState(false);
  const [paymentStatusUpdate, setPaymentStatusUpdate] = useState('');

  // Delete Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);

  // Invoice Number Manual Edit state
  const [invoiceNumber, setInvoiceNumber] = useState('');

  // AI PO Upload states
  const [showPOUploadModal, setShowPOUploadModal] = useState(false);
  const [poFile, setPoFile] = useState(null);
  const [poExtracting, setPoExtracting] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState(new Set());

  // Use modal scroll lock for various modals
  useModalScrollLock(showViewModal || showConvertModal || showEstimateDetailModal || showEditProductsModal || showConvertClosedOrderModal || showClosedOrderDetailModal || showCancellationModal || showPaymentStatusUpdateModal || showEditInvoiceModal);

  // Validation schema
  const validationSchema = {
    'customer.name': { type: 'name', required: true },
    'customer.phone': { type: 'phone', required: true },
    'customer.email': { type: 'email', required: false },
    'customer.address': { type: 'text', required: true },
    'customer.gstNumber': { type: 'text', required: false, minLength: 15 },
    'salesperson_id': { type: 'select', required: true }
  };

  // Effects
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const viewParam = searchParams.get('view');

    if (id) {
      setIsEditing(true);
      setActiveTab('form');
      fetchInvoiceForEdit(id);
    } else if (prefilledCustomer) {
      setActiveTab('form');
    } else if (tabParam === 'form') {
      setActiveTab('form');
    } else if (viewParam) {
      // Handle "Refer Invoice for Payment Status" navigation
      setActiveTab('history');
      // Auto-open the invoice details modal after invoices are loaded
      setTimeout(() => {
        handleViewInvoice(viewParam);
      }, 1000);
    }
  }, [id, prefilledCustomer, location.search]);

  // 1. Core Data Fetching on tab switch
  useEffect(() => {
    if (activeTab === 'history') {
      fetchInvoices(0, 1);
    } else if (activeTab === 'form') {
      fetchFormData();
    }
  }, [activeTab]);

  // 1b. Auto-apply filters (debounced) — reset to page 1 on filter changes
  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) { filterMounted.current = true; return; }
    if (activeTab !== 'history') return;
    const t = setTimeout(() => fetchInvoices(0, 1), 500);
    return () => clearTimeout(t);
  }, [filters.status, filters.paymentStatus, filters.startDate, filters.endDate, filters.minAmount, filters.maxAmount, filters.isWoodWork, filters.dateRange]);

  // 2. Debounced Search Effect
  useEffect(() => {
    if (activeTab !== 'history') return;

    const timer = setTimeout(() => {
      fetchInvoices(0, 1); // Reset to page 1 on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 3. Auto-refresh Interval Management (30s) — preserves page, search, and filters using refs
  useEffect(() => {
    if (activeTab !== 'history') return;

    const AUTO_REFRESH_ENABLED = true;
    const AUTO_REFRESH_INTERVAL = 30000;

    const isAnyModalOpen = showViewModal || showPaymentModal || showConvertModal ||
      showEstimateDetailModal || showConvertClosedOrderModal ||
      showClosedOrderDetailModal || showEditInvoiceModal ||
      showEditProductsModal || showCancellationModal || showPaymentStatusUpdateModal || showDeleteModal;

    if (AUTO_REFRESH_ENABLED && !isAnyModalOpen) {
      const interval = setInterval(() => {
        console.log('🔄 Auto-refreshing invoices (preserving page, search, and filters)...');
        fetchInvoices(0, currentPageRef.current);
      }, AUTO_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [activeTab, showViewModal, showPaymentModal, showConvertModal,
    showEstimateDetailModal, showConvertClosedOrderModal,
    showClosedOrderDetailModal, showEditInvoiceModal,
    showEditProductsModal, showCancellationModal, showPaymentStatusUpdateModal, showDeleteModal]);

  // 4. Cleanup/Company Settings
  useEffect(() => {
    fetchCompanySettings();
  }, []);

  // Listen for payment sync events
  useEffect(() => {
    const handlePaymentUpdate = (data) => {
      console.log('🔄 InvoicesPage: Received payment update', data);
      // Always refresh the invoice list after payment update preserving state
      fetchInvoices(0, currentPageRef.current);
    };

    paymentSync.on('payment_updated', handlePaymentUpdate);
    paymentSync.on('refresh_all_payments', handlePaymentUpdate);

    return () => {
      paymentSync.off('payment_updated', handlePaymentUpdate);
      paymentSync.off('refresh_all_payments', handlePaymentUpdate);
    };
  }, [activeTab]);

  const fetchCompanySettings = async () => {
    try {
      console.log('📋 InvoicesPage: Fetching company settings...');
      const response = await api.get('/admin/company-settings');
      console.log('📋 InvoicesPage: Company settings response:', response.data);
      setCompanySettings(response.data);
      console.log('✅ InvoicesPage: Company settings set successfully');
    } catch (error) {
      console.error('❌ InvoicesPage: Failed to fetch company settings:', error);
      // Use default settings if fetch fails
      setCompanySettings({
        companyName: 'IDEAL TRENDZ INTERIORS',
        addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
        addressLine2: '',
        city: 'HYDERNAGAR',
        state: 'TELANGANA',
        pincode: '500072',
        contactNumber: '9014814901',
        emailId: 'idealtrendz.int@gmail.com',
        gstNumber: '36AHZPN3524A1Z9',
        panNumber: 'AHZPN3524A',
        bankName: 'HDFC Bank',
        accountHolderName: 'IDEAL TRENDZ INTERIORS',
        accountNumber: '50200012345678',
        ifscCode: 'HDFC0001234',
        branchName: 'Nizampet'
      });
    }
  };

  // Also fetch form data on component mount if we have prefilled customer
  useEffect(() => {
    if (prefilledCustomer) {
      fetchFormData();
    }
  }, [prefilledCustomer]);

  useEffect(() => {
    filterInvoices();
  }, [invoices, searchTerm, sortConfig]);

  // Form functions
  const fetchFormData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching form data for invoice form...');

      // Try direct API calls
      try {
        const [categoriesRes, productsRes, salespersonsRes] = await Promise.all([
          api.get('/admin/categories'),
          api.get('/admin/products'),
          api.get('/admin/employees')
            .catch(() => api.get('/sales/salespersons'))
        ]);

        // Normalize products to handle MongoDB ObjectIds and nested structure
        const normalizedProducts = (productsRes.data || []).map(prod => {
          // Extract category ID from nested structure: product.categoryId._id
          const catId = prod.product?.categoryId?._id ||
            prod.product?.categoryId ||
            prod.categoryId?._id ||
            prod.categoryId ||
            prod.category_id ||
            prod.category?._id ||
            prod.category;

          return {
            id: prod.id || prod._id,
            name: prod.product?.name || prod.name || prod.productName || 'Unnamed Product',
            categoryId: catId,
            category_id: catId,
            categoryName: prod.product?.categoryName || prod.categoryName || '',
            sellingPrice: prod.product?.sellingPrice || prod.sellingPrice || prod.selling_price || prod.price || 0,
            quantity: prod.product?.quantity || prod.quantity || prod.stock || 0,
            sku: prod.product?.sku || prod.sku || '',
            description: prod.product?.description || prod.description || '',
            hsnCode: prod.product?.hsnCode || prod.hsnCode || '',
            gstPercentage: prod.product?.gstPercentage || prod.gstPercentage || 0
          };
        });

        setCategories((categoriesRes.data || []).map(cat => ({
          ...cat,
          id: cat.id || cat._id,
          isOthers: cat.isOthers || false
        })));
        setProducts(normalizedProducts);
        setSalespersons(salespersonsRes.data || []);

        // Auto-fill employee from logged-in user
        if (user && user.name && salespersonsRes.data) {
          const matchingSalesperson = salespersonsRes.data.find(sp =>
            sp.name.toLowerCase() === user.name.toLowerCase() ||
            sp.email === user.email
          );

          if (matchingSalesperson) {
            console.log('✅ Auto-filling employee:', matchingSalesperson.name);
            console.log('✅ Salesperson ID:', matchingSalesperson.id || matchingSalesperson._id);
            setFormData(prev => ({
              ...prev,
              salesperson_id: matchingSalesperson.id || matchingSalesperson._id || user.id
            }));
          } else {
            console.log('⚠️ No matching salesperson found for user:', user.name);
            // For admin users, use their user ID as fallback
            console.log('✅ Using admin user ID as fallback:', user.id);
            setFormData(prev => ({
              ...prev,
              salesperson_id: user.id
            }));
          }
        }

        if (normalizedProducts.length === 0) {
          toast.warning('No products found in database');
        }
      } catch (apiError) {
        console.error('❌ Direct API call failed:', apiError);
        throw apiError;
      }
    } catch (error) {
      console.error('❌ Failed to load form data:', error);
      toast.error('Failed to load form data. Please ensure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceForEdit = async (invoiceId) => {
    try {
      const response = await api.get(`/sales/invoices/${invoiceId}`);
      const invoice = response.data;

      setFormData({
        salesperson_id: invoice.salesperson_id || '',
        customer: {
          name: invoice.customer_name || '',
          phone: invoice.customer_phone || '',
          email: invoice.customer_email || '',
          address: invoice.customer_address || '',
          gstNumber: invoice.customer_gst || invoice.customer?.gstNumber || '',
          state: invoice.customer_state ||
            invoice.party_state_name ||
            invoice.customer?.state ||
            ((invoice.customer_gst) ? getStateNameFromGST(invoice.customer_gst) : '') ||
            ''
        },
        dispatched_from: invoice.dispatched_from || '',
        delivered_to: invoice.delivered_to || '',
        date: invoice.date ? new Date(invoice.date).toISOString().split('T')[0] : (invoice.createdAt ? new Date(invoice.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
      });

      if (invoice.items && invoice.items.length > 0) {
        setItems(invoice.items);
      }
    } catch (error) {
      console.error('Failed to load invoice:', error);
      toast.error('Failed to load invoice for editing');
    }
  };

  // History functions
  const fetchInvoices = async (retryCount = 0, page = 1) => {
    try {
      setLoading(true);
      const currentFilters = filtersRef.current || filters;
      const currentSearch = (searchTermRef.current !== undefined ? searchTermRef.current : searchTerm).trim();

      const queryParams = new URLSearchParams({
        page,
        limit: 10
      });

      if (currentFilters.status && currentFilters.status !== 'all') queryParams.append('status', currentFilters.status);
      if (currentFilters.paymentStatus && currentFilters.paymentStatus !== 'all') queryParams.append('paymentStatus', currentFilters.paymentStatus);
      if (currentFilters.startDate) queryParams.append('startDate', currentFilters.startDate);
      if (currentFilters.endDate) queryParams.append('endDate', currentFilters.endDate);
      if (currentFilters.minAmount) queryParams.append('minAmount', currentFilters.minAmount);
      if (currentFilters.maxAmount) queryParams.append('maxAmount', currentFilters.maxAmount);
      if (currentFilters.isWoodWork && currentFilters.isWoodWork !== 'all') queryParams.append('isWoodWork', currentFilters.isWoodWork);
      // For employee view, we don't allow changing the salesperson ID, but we let backend handle employee isolation

      if (currentSearch) {
        queryParams.append('search', currentSearch);
      }

      const response = await api.get(`/sales/invoices?${queryParams.toString()}`);

      console.log('📋 Raw invoices from backend:', response.data.invoices);

      // Normalize the invoices data to ensure consistent field names
      const normalizedInvoices = (response.data.invoices || []).map(invoice => {
        return {
          ...invoice,
          customer_name: invoice.customer_name ||
            invoice.customer?.name ||
            invoice.customerName ||
            invoice.client_name ||
            'Walk-in Customer',
          customer_phone: invoice.customer_phone ||
            invoice.customer?.phone ||
            invoice.customerPhone ||
            invoice.phone ||
            'N/A',
          customer_email: invoice.customer_email ||
            invoice.customer?.email ||
            invoice.customerEmail ||
            invoice.email ||
            '',
          customer_address: invoice.customer_address ||
            invoice.customer?.address ||
            invoice.customerAddress ||
            invoice.address ||
            '',
          totalAmount: invoice.totalAmount || invoice.total_amount || invoice.amount || 0,
          total_amount: invoice.totalAmount || invoice.total_amount || invoice.amount || 0,
          cancellation_status: invoice.cancellation_status || null,
          cancellation_reason: invoice.cancellation_reason || null,
          cancellation_payment_status: invoice.cancellation_payment_status || null,
          cancelled_by: invoice.cancelled_by || null,
          cancelled_at: invoice.cancelled_at || null,
          customer_gst: invoice.customer_gst || invoice.customer?.gstNumber || invoice.gstNumber || invoice.party_gst_no || '',
          customer_state: invoice.customer_state ||
            invoice.party_state_name ||
            invoice.customer?.state ||
            (invoice.customer_gst ? getStateNameFromGST(invoice.customer_gst) : '') ||
            ((invoice.customer?.gstNumber) ? getStateNameFromGST(invoice.customer?.gstNumber) : '') ||
            ''
        };
      });

      setInvoices(normalizedInvoices);
      const paginationData = response.data.pagination || {
        currentPage: page,
        totalPages: 1,
        totalItems: normalizedInvoices.length,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false
      };
      currentPageRef.current = paginationData.currentPage || page;
      setPagination(paginationData);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Failed to load invoices:', error);
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => fetchInvoices(retryCount + 1, page), 1000);
        return;
      }
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ultra-robust helper to map items for editing modals.
   * Standardizes category/product identification across all entry points.
   */
  const mapItemsForEditing = (items = [], localCategories = [], localProducts = []) => {
    return items.map((item, idx) => {
      // Robust category ID extraction
      let catId = item.category_id ||
        item.categoryId ||
        item.category_name ||
        item.categoryName ||
        (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
        '';

      // Robust product ID extraction
      let prodId = item.product_id ||
        item.productId ||
        (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
        '';

      console.log(`📦 Mapping Item [${idx}] raw:`, { catId, prodId, itemCategory: item.category, productName: item.product_name });

      // CRITICAL: If catId is still missing but we have a product_id, look up the product's category
      if (!catId && prodId) {
        const foundProd = localProducts.find(p => String(p.id || p._id) === String(prodId));
        if (foundProd) {
          catId = foundProd.category_id || foundProd.categoryId;
          console.log(`✅ [${idx}] Resolved category from product ID:`, catId);
        }
      }

      // If catId is a name or missing, try to find the actual ID
      if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
        const foundCat = localCategories.find(c =>
          (c.name || '').toLowerCase() === String(catId || '').toLowerCase() ||
          (c.categoryName || '').toLowerCase() === String(catId || '').toLowerCase() ||
          (c.id || c._id) === String(catId)
        );
        if (foundCat) {
          catId = foundCat.id || foundCat._id;
          console.log(`✅ [${idx}] Resolved category name/ID to ID: ${catId}`);
        } else if ((item.product_name || item.name) && !prodId) {
          // Infer Others if name exists but no ID
          const othersCat = localCategories.find(c =>
            c.isOthers ||
            (c.name || '').toLowerCase() === 'others' ||
            (c.name || '').toLowerCase() === 'other'
          );
          if (othersCat) {
            catId = othersCat.id || othersCat._id;
            console.log(`ℹ️ [${idx}] Inferred "Others" category for custom product`);
          }
        }
      }

      const selectedCategory = localCategories.find(c => String(c.id || c._id) === String(catId));
      const isOthers = !!(
        selectedCategory?.isOthers ||
        (selectedCategory?.name || '').toLowerCase() === 'others' ||
        (selectedCategory?.name || '').toLowerCase() === 'other' ||
        item.isOthers ||
        ((item.product_name || item.name) && !prodId) // FORCE isOthers if custom name and no product ID
      );

      return {
        ...item,
        category_id: catId,
        product_id: prodId,
        product_name: item.product_name || item.name || '',
        isOthers: isOthers,
        showFreeText: isOthers && !prodId,
        unit_type: item.unit_type || (isOthers ? 'PCS' : 'SFT'),
        hsn_code: item.hsn_code || (item.productId?.product?.hsnCode) || '',
        // Explicitly preserve Wood Work fields
        categoryLabel: item.categoryLabel || item.category_label || item.template_category || '',
        nos: item.nos !== undefined ? item.nos : null,
        length: item.length !== undefined ? item.length : null,
        breadth: item.breadth !== undefined ? item.breadth : null,
        isWoodWork: !!(item.isWoodWork || item.is_wood_work)
      };
    });
  };

  const filterInvoices = () => {
    // Backend now handles search filtering. 
    // We only apply sorting locally here.
    let filtered = invoices;

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'customer':
            aValue = (a.customer_name || '').toLowerCase();
            bValue = (b.customer_name || '').toLowerCase();
            break;
          case 'date':
            aValue = new Date(a.date || a.created_at || 0);
            bValue = new Date(b.date || b.created_at || 0);
            break;
          case 'amount':
            aValue = parseFloat(a.totalAmount || a.total_amount || 0);
            bValue = parseFloat(b.totalAmount || b.total_amount || 0);
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredInvoices(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper to handle GST change and auto-fill state
  const handleGSTFieldChange = (gstValue, updateFunction, stateUpdateFunction) => {
    updateFunction(gstValue);
    const stateName = getStateNameFromGST(gstValue);
    if (stateName && stateUpdateFunction) {
      stateUpdateFunction(stateName);
    }
  };

  const handleNewInvoice = () => {
    setIncludeTermsDirect(false);
    console.log('🆕 Creating new invoice...');
    setActiveTab('form');
    setIsEditing(false);

    // Get current date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    setFormData({
      salesperson_id: '',
      date: today,  // Auto-fill current date
      customer: {
        name: '',
        phone: '',
        email: '',
        address: '',
        gstNumber: '',
        state: ''
      }
    });
    setItems([
      {
        category_id: '',
        product_id: '',
        product_name: '',
        hsn_code: '',
        product_instructions: '',
        quantity: 1,
        unit_price: 0,
        unit_type: 'SFT',
        gst_rate: '',
        gst_amount: 0,
        amount: 0,
        total_amount: 0,
        tax: 0,
        discount: '',
        subtotal: 0
      },
      {
        category_id: '',
        product_id: '',
        product_name: '',
        hsn_code: '',
        product_instructions: '',
        quantity: 1,
        unit_price: 0,
        unit_type: 'SFT',
        gst_rate: '',
        gst_amount: 0,
        amount: 0,
        total_amount: 0,
        tax: 0,
        discount: '',
        subtotal: 0
      },
      {
        category_id: '',
        product_id: '',
        product_name: '',
        hsn_code: '',
        product_instructions: '',
        quantity: 1,
        unit_price: 0,
        unit_type: 'SFT',
        gst_rate: '',
        gst_amount: 0,
        amount: 0,
        total_amount: 0,
        tax: 0,
        discount: '',
        subtotal: 0
      }
    ]);

    setPaymentData({
      payment_type: 'cash',
      payment_status: 'pending',
      amount_received: 0
    });

    // Reset invoice number
    setInvoiceNumber('');

    // Fetch next invoice number
    const fetchNextNumber = async () => {
      try {
        const response = await api.get('/sales/next-document-number?type=invoice');
        if (response.data?.nextNumber) {
          setInvoiceNumber(response.data.nextNumber);
        }
      } catch (error) {
        console.error('Failed to fetch next invoice number:', error);
      }
    };
    fetchNextNumber();

    // Trigger form data loading
    fetchFormData();
  };

  // Fetch full invoice details for viewing
  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      console.log('🔍 Fetching full details for invoice:', invoiceId);
      const response = await api.get(`/sales/invoices/${invoiceId}`);
      console.log('📋 Full invoice details:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch invoice details:', error);
      toast.error('Failed to load invoice details');
      return null;
    }
  };

  // Handle view invoice with full details
  const handleViewInvoice = async (invoiceOrId) => {
    console.log('👁️ Viewing invoice:', invoiceOrId);

    let invoiceId;
    let invoice = null;

    // Handle both invoice object and invoice ID
    if (typeof invoiceOrId === 'string') {
      // It's an ID from query parameter
      invoiceId = invoiceOrId;
      // Try to find the invoice in the current list
      invoice = invoices.find(inv => (inv.id || inv._id) === invoiceId);
    } else {
      // It's an invoice object
      invoice = invoiceOrId;
      invoiceId = invoice.id || invoice._id;
    }

    // Try to get full details from backend
    const fullDetails = await fetchInvoiceDetails(invoiceId);

    if (fullDetails) {
      // Use full details if available
      setSelectedInvoice({
        ...fullDetails,
        // Ensure normalized fields are available
        customer_name: fullDetails.customer_name || fullDetails.customer?.name || (invoice?.customer_name) || 'Walk-in Customer',
        customer_phone: fullDetails.customer_phone || fullDetails.customer?.phone || (invoice?.customer_phone) || 'N/A',
        customer_email: fullDetails.customer_email || fullDetails.customer?.email || (invoice?.customer_email) || '',
        customer_address: fullDetails.customer_address || fullDetails.customer?.address || (invoice?.customer_address) || '',
        customer_gst: fullDetails.customer_gst || fullDetails.customer?.gstNumber || (invoice?.customer_gst) || '',
        customer_state: fullDetails.customer_state || fullDetails.party_state_name || fullDetails.customer?.state || (invoice?.customer_state) || (invoice?.party_state_name) || ((fullDetails.customer_gst || invoice?.customer_gst) ? getStateNameFromGST(fullDetails.customer_gst || invoice?.customer_gst) : '') || '',
        items: fullDetails.items || (invoice?.items) || [],
        totalAmount: fullDetails.totalAmount || fullDetails.total_amount || (invoice?.totalAmount) || (invoice?.total_amount) || 0,
        // Include salesperson info
        salesperson: fullDetails.salesperson || fullDetails.employee || (invoice?.salesperson) || (invoice?.employee)
      });
    } else if (invoice) {
      // Fallback to list data if available
      setSelectedInvoice(invoice);
    } else {
      // If no invoice found, show error
      toast.error('Invoice not found');
      return;
    }

    const isWoodWork = !!(fullDetails?.isWoodWork || fullDetails?.is_woodwork || invoice?.isWoodWork || invoice?.is_woodwork);
    setIsWoodWorkMode(isWoodWork);
    setShowViewModal(true);
  };

  // Delete Invoice Handler
  const handleDeleteInvoice = (invoice) => {
    setDeletingInvoice(invoice);
    setShowDeleteModal(true);
  };

  const confirmDeleteInvoice = async () => {
    try {
      const loadingToast = toast.loading('Deleting invoice...');
      await api.delete(`/sales/invoices/${deletingInvoice.id || deletingInvoice._id}`);
      toast.dismiss(loadingToast);
      toast.success('Invoice deleted successfully');
      setShowDeleteModal(false);
      setDeletingInvoice(null);
      await fetchInvoices(0, pagination.currentPage); // Refresh the list preserving page
    } catch (error) {
      console.error('Delete error:', error);
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Failed to delete invoice');
    }
  };

  // Edit functionality removed as requested

  const getPaymentStatusBadge = (invoice) => {
    const normalizedData = normalizePaymentData(invoice);
    return <PaymentStatusBadge paidAmount={normalizedData.paidAmount} totalAmount={normalizedData.totalAmount} />;
  };

  const isPaymentCompleted = (invoice) => {
    const normalizedData = normalizePaymentData(invoice);
    return calculatePaymentStatus(normalizedData.paidAmount, normalizedData.totalAmount) === 'completed';
  };

  const handleUpdatePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      payment_type: invoice.payment_type || 'cash',
      payment_status: invoice.payment_status || 'pending',
      amount_received: invoice.amount_received || 0
    });
    setShowPaymentModal(true);
  };

  const handlePaymentUpdate = async (paymentUpdateData) => {
    try {
      setLoading(true);

      // Get the new payment amount entered in the modal
      const newPayment = parseFloat(paymentUpdateData.amount_received) || 0;

      const updateData = {
        payment_type: paymentUpdateData.payment_type,
        // CRITICAL: Send only the new payment amount.
        // The backend will add this to the current amountReceived.
        amount_received: newPayment,
        notes: paymentUpdateData.notes
      };

      console.log('🔄 Updating payment (sending individual amount):', updateData);

      const response = await api.put(`/sales/invoices/${selectedInvoice.id}/payment`, updateData);

      // Get updated invoice data
      const updatedInvoice = response.data || { ...selectedInvoice, ...updateData };

      // Update the selected invoice for the view modal - THIS IS KEY FOR INSTANT UPDATE
      setSelectedInvoice(updatedInvoice);

      toast.success('Payment updated successfully!');
      setShowPaymentModal(false);

      // Emit sync event to update all modules
      paymentSync.notifyInvoicePaymentUpdate(updatedInvoice);

      // Refresh invoices to show updated data preserving page
      await fetchInvoices(0, pagination.currentPage);

    } catch (error) {
      console.error('Failed to update payment:', error);
      toast.error('Failed to update payment: ' + (error.response?.data?.message || error.message));
      throw error; // Re-throw to let modal handle loading state
    } finally {
      setLoading(false);
    }
  };

  // AI PO Extraction Handler
  const handlePOUpload = async () => {
    if (!poFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setPoExtracting(true);
      const formDataUpload = new FormData();
      formDataUpload.append('poFile', poFile);

      console.log('🤖 Sending PO for AI extraction...');
      const response = await api.post('/admin/extract-po', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success && response.data?.data) {
        const extracted = response.data.data;
        console.log('✅ AI Extracted Data:', extracted);

        // Track which fields were filled by AI
        const newAiFields = new Set();

        // 1. Map Customer Data
        const updatedCustomer = { ...formData.customer };
        if (extracted.customer_name) { updatedCustomer.name = extracted.customer_name; newAiFields.add('customer.name'); }
        if (extracted.phone_number) { updatedCustomer.phone = extracted.phone_number; newAiFields.add('customer.phone'); }
        if (extracted.email) { updatedCustomer.email = extracted.email; newAiFields.add('customer.email'); }
        if (extracted.address) { updatedCustomer.address = extracted.address; newAiFields.add('customer.address'); }
        if (extracted.state) { updatedCustomer.state = extracted.state; newAiFields.add('customer.state'); }
        if (extracted.gst_number) { updatedCustomer.gstNumber = extracted.gst_number; newAiFields.add('customer.gstNumber'); }

        // 2. Map Header Data
        const updatedHeader = {
          ...formData,
          customer: updatedCustomer,
          dispatched_from: extracted.dispatched_from || formData.dispatched_from,
          delivered_to: extracted.delivered_to || formData.delivered_to,
          termsAndConditions: extracted.terms_and_conditions || formData.termsAndConditions
        };

        if (extracted.dispatched_from) newAiFields.add('dispatched_from');
        if (extracted.delivered_to) newAiFields.add('delivered_to');
        if (extracted.terms_and_conditions) newAiFields.add('termsAndConditions');

        setFormData(updatedHeader);

        // 3. Map Items
        if (extracted.items && extracted.items.length > 0) {
          const newItems = extracted.items.map((item, idx) => {
            // Attempt to find matching category
            const matchedCategory = categories.find(c => 
              c.name.toLowerCase().includes(item.category.toLowerCase()) || 
              item.category.toLowerCase().includes(c.name.toLowerCase())
            );

            // Attempt to find matching product
            const matchedProduct = products.find(p => 
              p.name.toLowerCase() === item.product_name.toLowerCase()
            );

            const isOthers = !matchedProduct;
            const categoryId = matchedCategory ? matchedCategory.id : (categories.find(c => c.isOthers)?.id || '');

            newAiFields.add(`item-${idx}-category`);
            newAiFields.add(`item-${idx}-product`);
            newAiFields.add(`item-${idx}-quantity`);
            newAiFields.add(`item-${idx}-price`);
            newAiFields.add(`item-${idx}-gst`);

            const mapUnit = (u) => {
              const unit = (u || '').toUpperCase().trim();
              const map = {
                'SFT': 'SFT', 'SQFT': 'SFT', 'SQ FT': 'SFT', 'SQ.FT': 'SFT',
                'RFT': 'RFT', 'RUNNING FEET': 'RFT',
                'PCS': 'PCS', 'PIECE': 'PCS', 'PIECES': 'PCS', 'PC': 'PCS',
                'KG': 'KG', 'KILOGRAM': 'KG',
                'LTR': 'LTR', 'LITRE': 'LTR', 'LITER': 'LTR',
                'MTR': 'MTR', 'METRE': 'MTR', 'METER': 'MTR',
                'LUMPSUM': 'LUMPSUM', 'LS': 'LUMPSUM', 'L.S': 'LUMPSUM', 'LSM': 'LUMPSUM',
                'NOS': 'NOS', 'NO': 'NOS', 'NUMBER': 'NOS', 'NUMBERS': 'NOS',
                'CM': 'CM', 'CENTIMETER': 'CM',
                'INCH': 'INCH', 'INCHES': 'INCH',
                'SQ.M': 'SQ.M', 'SQM': 'SQ.M', 'SQUARE METER': 'SQ.M'
              };
              return map[unit] || 'SFT';
            };

            return {
              category_id: categoryId,
              product_id: matchedProduct ? matchedProduct.id : '',
              product_name: item.product_name || '',
              hsn_code: item.hsn_code || '',
              product_instructions: '',
              quantity: item.quantity || 1,
              unit_price: item.price || 0,
              unit_type: mapUnit(item.unit),
              gst_rate: item.gst || 18,
              gst_amount: 0,
              amount: 0,
              total_amount: 0,
              tax: 0,
              discount: 0,
              subtotal: 0,
              isOthers: isOthers,
              showFreeText: isOthers
            };
          });

          setItems(newItems);
        }

        setAiFilledFields(newAiFields);
        setShowPOUploadModal(false);
        setPoFile(null);
        toast.success('PO data extracted and filled successfully!');
      } else {
        throw new Error('Invalid response from AI extraction service');
      }
    } catch (error) {
      console.error('❌ AI PO Extraction Error:', error);
      toast.error(error.response?.data?.error || 'Failed to extract data from PO. Please try again.');
    } finally {
      setPoExtracting(false);
    }
  };

  // Helper to remove field from AI highlight Set when user modifies it
  const clearAiHighlight = (fieldName) => {
    if (aiFilledFields.has(fieldName)) {
      const updated = new Set(aiFilledFields);
      updated.delete(fieldName);
      setAiFilledFields(updated);
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormTouched(true);

    // Basic validation
    const errors = {};

    // Check required fields
    if (!invoiceNumber?.trim()) {
      errors.invoiceNumber = 'Invoice Number is required';
    }

    if (!formData.salesperson_id) {
      errors.salesperson = 'Please select a salesperson';
    }

    if (!formData.customer.name?.trim()) {
      errors.customerName = 'Customer name is required';
    }

    if (!formData.customer.phone?.trim()) {
      errors.customerPhone = 'Mobile number is required';
    } else {
      const hasCountryCode = formData.customer.countryCode?.trim();
      const cleanPhone = formData.customer.phone.replace(/\D/g, '');
      if (hasCountryCode) {
        const cleanCode = formData.customer.countryCode.replace(/\D/g, '');
        const total = cleanCode.length + cleanPhone.length;
        if (total < 12 || total > 13) {
          errors.customerPhone = 'With country code, total digits must be 12-13 (e.g. +91 9876543210)';
        }
      } else {
        if (cleanPhone.length !== 10) {
          errors.customerPhone = 'Mobile number must be exactly 10 digits (or add a country code)';
        }
      }
    }

    // GST Number is optional, but if provided, validate format
    if (formData.customer.gstNumber?.trim() && formData.customer.gstNumber.trim().length < 15) {
      errors.customerGst = 'GST Number must be at least 15 characters long if provided';
    }

    // Check if at least one item exists and is valid
    if (!items || items.length === 0) {
      errors.items = 'Please add at least one item';
    } else {
      const invalidItems = items.filter(item => {
        // If it's an "Others" category, product_id can be empty as long as product_name is present
        const selectedCategory = categories.find(c => String(c.id) === String(item.category_id));
        const isOthers = selectedCategory?.isOthers || false;

        if (isOthers) {
          return !item.category_id || (!item.product_id && !item.product_name) || !item.quantity || item.quantity <= 0;
        }

        // Standard validation for non-"Others" categories
        return !item.category_id || !item.product_id || !item.quantity || item.quantity <= 0;
      });

      if (invalidItems.length > 0) {
        errors.items = 'Please complete all item details (category, product, quantity)';
      }
    }

    // If there are validation errors, show them and stop
    if (Object.keys(errors).length > 0) {
      console.log('❌ Form validation failed:', errors);
      Object.values(errors).forEach(error => toast.error(error));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Proceed directly without stock validation

      // Proceed with invoice creation
      await proceedWithInvoiceCreation(items);

    } catch (error) {
      console.error('Invoice creation failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create invoice';
      toast.error(`Invoice creation failed: ${errorMessage}`);
      setLoading(false);
    }
  };

  // Save edited invoice
  const handleSaveInvoiceEdits = async () => {
    if (!editingInvoice) return;

    setLoading(true);
    try {
      // Calculate totals
      const itemsToSave = editingProducts.length > 0 ? editingProducts : editingInvoice.items;
      const totalAmount = calculateInvoiceTotal(itemsToSave);

      // Resolve salesperson ID
      let salespersonId = editingInvoice.salesperson_id || editingInvoice.salespersonId;

      if (editingInvoice.salesperson && typeof editingInvoice.salesperson === 'object') {
        salespersonId = editingInvoice.salesperson.id || editingInvoice.salesperson._id;
      } else if (editingInvoice.salesperson && typeof editingInvoice.salesperson === 'string') {
        salespersonId = editingInvoice.salesperson;
      }

      // Fallback to logged-in user if no salesperson found (admin override or self-creation context)
      if (!salespersonId && user) {
        console.log('💾 Using logged-in user as salesperson fallback:', user);
        salespersonId = user.id || user._id;
      }

      if (!salespersonId) {
        toast.error('Unable to determine salesperson. Please ensure you are logged in correctly.');
        setLoading(false);
        return;
      }

      // Recalculate payment status based on new total and existing amount received
      const paidAmount = parseFloat(editingInvoice.amountReceived || editingInvoice.amount_received || selectedInvoice?.amountReceived || 0);
      const newPaymentStatus = calculatePaymentStatus(paidAmount, totalAmount);

      console.log('💰 Invoice Payment Status Recalculation:', {
        paidAmount,
        totalAmount,
        oldStatus: editingInvoice.paymentStatus,
        newStatus: newPaymentStatus
      });

      const updateData = {
        salesperson_id: salespersonId,
        customer: {
          name: editingCustomer.name,
          phone: editingCustomer.phone,
          email: editingCustomer.email,
          address: editingCustomer.address,
          address: editingCustomer.address,
          gstNumber: editingCustomer.gstNumber || editingInvoice.customer_gst || '',
          state: editingCustomer.state || ''
        },
        dispatched_from: editingInvoice.dispatched_from || '',
        delivered_to: editingInvoice.delivered_to || '',
        items: recalcItemsAmounts(itemsToSave),
        termsAndConditions: editingInvoice.termsAndConditions || '',
        materialDescription: editingInvoice.materialDescription || '',
        total_amount: totalAmount,
        paymentStatus: newPaymentStatus, // Include recalculated status
        discount_amount: itemsToSave.reduce((sum, item) => {
          const baseAmount = item.quantity * item.unit_price;
          return sum + (baseAmount * ((item.discount || 0) / 100));
        }, 0),
        taxable_amount: itemsToSave.reduce((sum, item) => {
          const baseAmount = item.quantity * item.unit_price;
          const discountAmount = baseAmount * ((item.discount || 0) / 100);
          return sum + (baseAmount - discountAmount);
        }, 0),
        gst_amount: itemsToSave.reduce((sum, item) => {
          const baseAmount = item.quantity * item.unit_price;
          const discountAmount = baseAmount * ((item.discount || 0) / 100);
          const taxableAmount = baseAmount - discountAmount;
          return sum + (taxableAmount * ((item.gst_rate || 0) / 100));
        }, 0),
        date: editingInvoice.date,
        isWoodWork: editingInvoice.isWoodWork || editingInvoice.is_woodwork || false
      };

      console.log('💾 Saving invoice edits:', updateData);

      const invoiceId = editingInvoice.id || editingInvoice._id;
      const response = await api.put(`/sales/invoices/${invoiceId}`, updateData);

      if (response.data) {
        toast.success('Invoice updated successfully');
        setShowEditInvoiceModal(false);

        // Refresh invoices list preserving page
        fetchInvoices(0, pagination.currentPage);

        // Update selected invoice view if strictly needed, or just close view modal
        // For now, let's close view modal to force refresh on re-open or update state
        if (selectedInvoice && (selectedInvoice.id === invoiceId || selectedInvoice._id === invoiceId)) {
          // Optionally fetch fresh details
          const updatedInvoice = {
            ...selectedInvoice,
            ...updateData,
            date: editingInvoice.date,
            paymentStatus: newPaymentStatus // Update local state status
          };
          setSelectedInvoice(updatedInvoice);
        }
      }
    } catch (error) {
      console.error('Failed to save invoice edits:', error);
      toast.error(error.response?.data?.message || 'Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  // Separate function for actual invoice creation logic
  const proceedWithInvoiceCreation = async (items) => {
    try {

      // Prepare invoice data with fallback for empty items
      const validItems = items.filter(item => {
        const category = categories.find(c => String(c.id) === String(item.category_id));
        const isOthers = item.isOthers || category?.isOthers;
        if (isOthers) {
          return item.category_id && (item.product_id || item.product_name) && item.quantity > 0;
        }
        return item.category_id && item.product_id && item.quantity > 0;
      });

      // If no valid items, create a default item
      const finalItems = validItems.length > 0 ? validItems : [{
        category_id: categories[0]?.id || '',
        product_id: products[0]?.id || '',
        quantity: 1,
        unit_price: 100,
        tax: 0,
        discount: 0
      }];

      // Calculate totals
      let totalAmount = 0;
      const processedItems = finalItems.map(item => {
        const quantity = parseFloat(item.quantity) || 1;
        const unitPrice = parseFloat(item.unit_price) || 100;
        const gstRate = parseFloat(item.gst_rate) || 0;
        const discount = showDiscount ? (parseFloat(item.discount) || 0) : 0;

        // Calculate subtotal for this item
        const baseAmount = quantity * unitPrice;
        const discountAmount = baseAmount * (discount / 100);
        const taxableAmount = baseAmount - discountAmount;
        const gstAmount = taxableAmount * (gstRate / 100);
        const subtotal = Math.max(taxableAmount + gstAmount, 0);

        totalAmount += subtotal;

        return {
          category_id: item.category_id,
          product_id: item.product_id,
          product_name: item.product_name || '',
          hsn_code: item.hsnCode || item.hsn_code || '',
          unit_type: item.unit_type || 'PCS',
          quantity: quantity,
          unit_price: unitPrice,
          gst_rate: gstRate,
          gst_amount: gstAmount,
          amount: taxableAmount,
          total_amount: subtotal,
          tax: item.tax || 0,
          discount: discount,
          subtotal: subtotal
        };
      });

      // Ensure totalAmount is never 0
      totalAmount = Math.max(totalAmount, 100);

      const invoiceData = {
        invoice_number: invoiceNumber,
        salesperson_id: formData.salesperson_id || user?.id,
        salesperson_name: user?.name,
        date: formData.date || new Date().toISOString().split('T')[0],
        customer_name: formData.customer.name?.trim() || 'Walk-in Customer',
        customer_phone: formData.customer.countryCode
          ? (formData.customer.countryCode.trim() + formData.customer.phone.trim())
          : (formData.customer.phone?.replace(/\D/g, '') || '9999999999'),
        customer_email: formData.customer.email?.trim() || '',
        customer_address: formData.customer.address?.trim() || '',
        customer_gst: formData.customer.gstNumber?.trim() || '',
        customer_state: formData.customer.state?.trim() || '',
        items: processedItems,
        totalAmount: totalAmount,
        total_amount: totalAmount,
        payment_type: paymentData.payment_type,
        payment_status: paymentData.payment_status,
        amount_received: parseFloat(paymentData.amount_received) || 0,
        dispatched_from: formData.dispatched_from || '',
        delivered_to: formData.delivered_to || '',
        termsAndConditions: includeTermsDirect ? (formData.termsAndConditions || '') : '',
        materialDescription: formData.materialDescription || '',
        status: 'invoice'
      };

      console.log('📤 Submitting invoice data:', invoiceData);

      if (isEditing && id) {
        // Update existing invoice
        await api.put(`/sales/invoices/${id}`, invoiceData);
        toast.success('Invoice updated successfully!');
      } else {
        // Create new invoice
        await api.post(`/sales/invoices`, invoiceData);
        toast.success('Invoice created successfully!');
      }

      // Refresh history and switch to history tab (reset to page 1 for new creation)
      await fetchInvoices(0, 1);
      setActiveTab('history');

    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to save invoice');
      throw error; // Re-throw to be handled by the calling function
    } finally {
      setLoading(false);
    }
  };

  // Convert Estimate to Invoice Functions
  const fetchEstimates = async () => {
    try {
      const response = await api.get(`/sales/estimates`);

      console.log('📋 Raw estimates response:', response.data);

      // Get all estimates and filter client-side for better control
      const allEstimates = response.data.estimates || response.data || [];
      console.log('📋 Total estimates received:', allEstimates.length);

      // Filter out converted estimates with comprehensive checks
      const unconvertedEstimates = allEstimates.filter(estimate => {
        const status = estimate.status || estimate.estimate_status || estimate.documentStatus;
        const isConverted = estimate.converted || estimate.is_converted || estimate.isConverted;
        const hasClosedOrderNumber = estimate.closed_order_number || estimate.closedOrderNumber;
        const hasInvoiceNumber = estimate.invoice_number || estimate.invoiceNumber;

        // Exclude if any conversion indicator is present
        const isConvertedEstimate =
          status === 'closed' ||
          status === 'converted' ||
          status === 'invoice' ||
          status === 'closed_order' ||
          status === 'sold' ||
          status === 'converted_to_invoice' ||
          isConverted === true ||
          hasClosedOrderNumber ||
          hasInvoiceNumber;

        return !isConvertedEstimate;
      });

      console.log('📋 Filtered unconverted estimates:', unconvertedEstimates.length);

      // Force state update with immediate refresh
      setEstimates([]);
      setFilteredEstimates([]);

      // Use setTimeout to ensure state is cleared before setting new data
      setTimeout(() => {
        setEstimates(unconvertedEstimates);
        setFilteredEstimates(unconvertedEstimates);
      }, 100);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error('Failed to load estimates');
    }
  };

  const handleOpenConvertModal = async () => {
    try {
      setLoading(true);
      console.log('🔄 Opening Convert Modal - Fetching latest unconverted estimates...');

      // Fetch fresh estimates data
      await fetchEstimates();

      setShowConvertModal(true);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error('Failed to load estimates for conversion');
    } finally {
      setLoading(false);
    }
  };

  const handleEstimateSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setEstimateSearchTerm(term);

    if (!term) {
      setFilteredEstimates(estimates);
    } else {
      const filtered = estimates.filter(estimate =>
        (estimate.estimate_number?.toLowerCase().includes(term)) ||
        (estimate.customer?.name?.toLowerCase().includes(term)) ||
        (estimate.customer_name?.toLowerCase().includes(term))
      );
      setFilteredEstimates(filtered);
    }
  };

  const handleSelectEstimate = async (estimate) => {
    try {
      console.log('🎯 Selected estimate for conversion:', estimate);
      setLoading(true);

      // Fetch next invoice number
      setInvoiceNumber('');
      // Load the next invoice number in the background so the modal opens immediately
      api.get('/sales/next-document-number?type=invoice')
        .then(res => { if (res.data?.nextNumber) setInvoiceNumber(prev => prev || res.data.nextNumber); })
        .catch(err => console.error('Failed to fetch next invoice number:', err));

      const estimateId = estimate.id || estimate._id;

      // Fetch full estimate details and form data in parallel for robust mapping
      const [response, fetchedFormData] = await Promise.all([
        api.get(`/sales/estimates/${estimateId}`),
        getFormData()
      ]);

      const fullEstimate = response.data.estimate || response.data;
      const fetchedCategories = fetchedFormData.categories || [];
      const fetchedProducts = fetchedFormData.products || [];

      // Update reference data if needed
      setCategories(fetchedCategories);
      setProducts(fetchedProducts);

      console.log('📋 Full estimate details received:', fullEstimate);

      const itemsWithIds = recalcItemsAmounts(mapItemsForEditing(fullEstimate.items || [], fetchedCategories, fetchedProducts));

      setSelectedEstimate({ ...fullEstimate, items: itemsWithIds });
      setEditingProducts(recalcItemsAmounts(itemsWithIds));
      setIsWoodWorkMode(!!fullEstimate.isWoodWork || !!fullEstimate.is_woodwork);
      setShowConvertModal(false);
      setShowEstimateDetailModal(true);

      // Pre-fill invoice data with estimate details
      setInvoiceData({
        paymentType: 'cash',
        amountReceived: fullEstimate.amount_received || fullEstimate.amountReceived || 0,
        invoiceDescription: fullEstimate.description || '',
        paymentStatus: fullEstimate.payment_status || 'pending',
        gstNumber: fullEstimate.customer?.gstNumber || fullEstimate.gst_number || '',
        dispatchedFrom: fullEstimate.dispatched_from || '',
        state: fullEstimate.customer?.state || fullEstimate.customer_state || fullEstimate.party_state_name || '',
        deliveredTo: fullEstimate.delivered_to || '',
        termsAndConditions: fullEstimate.termsAndConditions || '',
        materialDescription: fullEstimate.materialDescription || '',
        date: new Date().toISOString().split('T')[0]
      });

    } catch (error) {
      console.error('❌ Failed to fetch full estimate details:', error);
      toast.error('Failed to load estimate details');
    } finally {
      setLoading(false);
    }
  };

  const calculateInvoiceTotal = (items, buyerGST = '', includeGST = true) => {
    if (!items || items.length === 0) return 0;

    const processedItems = items.map(item => ({
      ...item,
      quantity: (item.isWoodWork || item.nos) ? computeWoodWorkQty(item.nos, item.length, item.breadth) : (item.quantity || 0)
    }));

    if (!includeGST) {
      // Return only subtotal (taxable amount)
      return processedItems.reduce((sum, item) => {
        const baseAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || 0));
        const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
        const taxableAmount = roundForCalculation(baseAmount - discountAmount);
        return sum + taxableAmount;
      }, 0);
    }

    // Use new GST calculation for total with GST
    const gstBreakdown = calculateTotalGSTBreakdown(processedItems, buyerGST, companySettings);
    return roundForCalculation(gstBreakdown.totalTaxableAmount + gstBreakdown.totalGST);
  };

  const handleProductEdit = (index, field, value) => {
    const updatedProducts = [...editingProducts];
    const item = { ...updatedProducts[index] };

    if (field === 'category_id') {
      const selectedCategory = categories.find(c => String(c.id || c._id) === String(value));
      const isOthers = selectedCategory?.isOthers || (selectedCategory?.name || '').toLowerCase() === 'others';

      item.category_id = value;
      item.isOthers = isOthers;
      item.product_id = '';
      item.product_name = '';
      item.unit_price = 0;
      item.unit_type = 'SFT';
      item.hsn_code = '';

      if (isWoodWorkMode) {
        item.nos = 1;
        item.length = 1;
        item.breadth = 1;
        item.quantity = 1;
      }
    } else {
      // Standardize numeric input handling matching EstimatesPage.js
      if (['quantity', 'unit_price', 'gst_rate', 'discount', 'nos', 'length', 'breadth'].includes(field)) {
        if (value === '') {
          item[field] = '';
        } else {
          item[field] = parseFloat(value) || 0;
        }

        // Auto-compute quantity if dimensions change
        if (['nos', 'length', 'breadth'].includes(field)) {
          const nos = field === 'nos' ? (value === '' ? 0 : parseFloat(value) || 0) : (item.nos || 1);
          const length = field === 'length' ? (value === '' ? 0 : parseFloat(value) || 0) : (item.length || 1);
          const breadth = field === 'breadth' ? (value === '' ? 0 : parseFloat(value) || 0) : (item.breadth || 1);
          item.quantity = computeWoodWorkQty(nos, length, breadth);
        }
      } else {
        item[field] = value;
      }
    }

    // Recalculate totals immediately
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discount = parseFloat(item.discount) || 0;
    const gstRate = parseFloat(item.gst_rate) || 0;

    const baseAmount = quantity * unitPrice;
    const discountAmount = (baseAmount * discount) / 100;
    const taxableAmount = baseAmount - discountAmount;
    const gstAmount = (taxableAmount * gstRate) / 100;
    const totalAmount = taxableAmount + gstAmount;

    item.amount = taxableAmount;
    item.gst_amount = gstAmount;
    item.total_amount = totalAmount;
    item.subtotal = totalAmount;

    updatedProducts[index] = item;
    setEditingProducts(updatedProducts);
  };

  // Apply every field of a picked product in one state update (calling
  // handleProductEdit once per field drops all but the last change).
  const handleProductSelect = (index, val, product) => {
    const updatedProducts = [...editingProducts];
    const item = {
      ...updatedProducts[index],
      product_id: val,
      product_name: product.name,
      unit_price: product.price || 0,
      gst_rate: product.gstRate || 18,
      hsn_code: product.hsnCode || '',
      unit_type: product.unit || 'SFT'
    };
    const quantity = parseFloat(item.quantity) || 0;
    const discount = parseFloat(item.discount) || 0;
    const baseAmount = quantity * (parseFloat(item.unit_price) || 0);
    const taxableAmount = baseAmount - (baseAmount * discount) / 100;
    const gstAmount = (taxableAmount * (parseFloat(item.gst_rate) || 0)) / 100;
    item.amount = taxableAmount;
    item.gst_amount = gstAmount;
    item.total_amount = taxableAmount + gstAmount;
    item.subtotal = taxableAmount + gstAmount;
    updatedProducts[index] = item;
    setEditingProducts(updatedProducts);
  };

  // Apply several field changes to one row in a single state update
  // (calling handleProductEdit repeatedly drops all but the last change).
  const handleProductFieldsEdit = (index, fields) => {
    const updatedProducts = [...editingProducts];
    const item = { ...updatedProducts[index], ...fields };
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discount = parseFloat(item.discount) || 0;
    const gstRate = parseFloat(item.gst_rate) || 0;
    const baseAmount = quantity * unitPrice;
    const taxableAmount = baseAmount - (baseAmount * discount) / 100;
    const gstAmount = (taxableAmount * gstRate) / 100;
    item.amount = taxableAmount;
    item.gst_amount = gstAmount;
    item.total_amount = taxableAmount + gstAmount;
    item.subtotal = taxableAmount + gstAmount;
    updatedProducts[index] = item;
    setEditingProducts(updatedProducts);
  };

  // Close Edit Products without saving and return to the modal it was opened from.
  const handleDiscardProductEdits = () => {
    setShowEditProductsModal(false);
    if (editModalContext === 'edit') {
      setShowEditInvoiceModal(true);
    } else if (editModalContext === 'convert_closed_order') {
      setShowClosedOrderDetailModal(true);
    } else {
      setShowEstimateDetailModal(true);
    }
  };

  const handleAddProduct = () => {
    const newItem = {
      product_id: '',
      category_id: '',
      product_name: '',
      quantity: 1,
      unit_price: 0,
      discount: 0,
      gst_rate: 18,
      hsn_code: '',
      unit_type: 'SFT',
      nos: 1,
      length: 1,
      breadth: 1
    };
    setEditingProducts([...editingProducts, newItem]);
  };

  const handleSaveProductEdits = () => {
    // Validate products before saving
    // Validate products before saving - Mode aware validation
    const hasEmptyProducts = editingProducts.some(p => {
      if (isWoodWorkMode) {
        return !p.categoryLabel || !p.product_name;
      }
      return !p.category_id || (!p.product_id && !p.product_name);
    });
    
    if (hasEmptyProducts) {
      toast.error('Please complete all product entries or remove empty ones');
      return;
    }

    // Transform edited products to ensure required fields are present (matching backend expectations)
    const transformedProducts = recalcItemsAmounts(editingProducts).map(item => ({
      ...item,
      productId: item.product_id,
      product_name: item.product_name
    }));

    if (editModalContext === 'edit') {
      setEditingInvoice({
        ...editingInvoice,
        items: transformedProducts
      });
      setShowEditProductsModal(false);
      setShowEditInvoiceModal(true);
    } else if (editModalContext === 'convert_closed_order') {
      if (selectedClosedOrder) {
        setSelectedClosedOrder({
          ...selectedClosedOrder,
          items: transformedProducts
        });
      }
      setShowEditProductsModal(false);
      setShowClosedOrderDetailModal(true);
    } else {
      // Default context - handle estimate conversion
      if (selectedEstimate) {
        setSelectedEstimate({
          ...selectedEstimate,
          items: transformedProducts
        });
      }
      setShowEditProductsModal(false);
      setShowEstimateDetailModal(true);
    }
    toast.success('Product changes applied locally');
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...editingProducts];
    updatedProducts.splice(index, 1);
    setEditingProducts(updatedProducts);
  };

  const handleInvoiceSubmit = async () => {
    setLoading(true);

    const items = selectedEstimate.items || [];

    // Basic validation first
    if (!selectedEstimate || (!selectedEstimate.id && !selectedEstimate._id)) {
      toast.error('Invalid estimate selected. Please try again.');
      setLoading(false);
      return;
    }

    // Check for zero quantity items (this is still blocking as it's a data integrity issue)
    const zeroQuantityItems = items.filter(item => !item.quantity || item.quantity <= 0);
    if (zeroQuantityItems.length > 0) {
      const itemNames = zeroQuantityItems.map(item => item.product_name || 'Unnamed Product');
      toast.error(`Cannot convert to invoice: Products with zero quantity: ${itemNames.join(', ')}`, {
        duration: 6000
      });
      setLoading(false);
      return;
    }

    // Proceed directly without stock validation

    // Proceed with conversion
    await proceedWithEstimateToInvoiceConversion();
  };
  // Separate function for actual estimate to invoice conversion
  const proceedWithEstimateToInvoiceConversion = async () => {
    try {
      // Close the estimate detail modal immediately to show loading state
      setShowEstimateDetailModal(false);
      setLoading(true);

      // Show loading overlay
      const loadingOverlay = document.createElement('div');
      loadingOverlay.id = 'conversion-loading-overlay';
      loadingOverlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]';
      loadingOverlay.innerHTML = `
        <div class="bg-white rounded-2xl p-8 flex flex-col items-center max-w-md">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mb-6"></div>
          <h3 class="text-xl font-bold text-slate-900 mb-2">Converting Estimate to Invoice</h3>
          <p class="text-slate-600 text-center">Please wait while we process your request...</p>
          <p class="text-slate-500 text-sm mt-4">This may take a few moments</p>
        </div>
      `;
      document.body.appendChild(loadingOverlay);

      // Validate required fields before conversion
      if (!selectedEstimate || (!selectedEstimate.id && !selectedEstimate._id)) {
        toast.error('Invalid estimate selected. Please try again.');
        setLoading(false);
        return;
      }

      // Check and update estimate status if needed before conversion
      const estimateId = selectedEstimate.id || selectedEstimate._id;

      // Skip status update to avoid 400 errors - proceed directly to conversion
      console.log('🔍 Selected Estimate for conversion:', selectedEstimate);
      console.log('🔍 Selected Estimate Items:', selectedEstimate.items);

      // Use items as-is, just like closed order conversion
      const items = recalcItemsAmounts(Array.isArray(selectedEstimate.items) ? selectedEstimate.items : []);

      console.log('📋 Items for conversion:', items);

      // Basic validation - just ensure we have items
      if (items.length === 0) {
        toast.error('No items found in estimate for conversion.');
        setLoading(false);
        return;
      }

      // Check for invalid quantities (same as closed order validation)
      const invalidQuantityItems = items.filter(item => !item.quantity || item.quantity <= 0);
      if (invalidQuantityItems.length > 0) {
        toast.error('Cannot convert: Some items have invalid quantities');
        setLoading(false);
        return;
      }

      const invoiceRequest = {
        invoice_number: invoiceNumber,
        payment_type: invoiceData.paymentType || 'cash',
        amount_received: parseFloat(invoiceData.amountReceived) || 0,
        invoice_description: invoiceData.invoiceDescription || '',
        customer_type: 'B2C',
        payment_status: invoiceData.paymentStatus || 'pending',
        gst_number: invoiceData.gstNumber,
        customer_state: invoiceData.state || '',
        dispatched_from: invoiceData.dispatchedFrom || '',
        delivered_to: invoiceData.deliveredTo || '',
        user_id: user?.id,
        user_role: user?.role,
        date: invoiceData.date || new Date().toISOString().split('T')[0],
        items: items,
        termsAndConditions: includeTermsEstimate ? (invoiceData.termsAndConditions || '') : '',
        materialDescription: invoiceData.materialDescription !== undefined ? invoiceData.materialDescription : (selectedEstimate.materialDescription || ''),
        isWoodWork: selectedEstimate.isWoodWork || selectedEstimate.is_woodwork || false
      };

      console.log('🔄 Sending invoice conversion request:');
      console.log('📋 Estimate ID:', estimateId);
      console.log('📋 Invoice Request:', invoiceRequest);
      console.log('📋 Processed Items:', invoiceRequest.items);

      // Send request to convert estimate to invoice
      const response = await api.put(
        `/sales/estimates/${estimateId}/invoice`,
        invoiceRequest
      );

      console.log('✅ Estimate converted to invoice successfully:', response.data);

      toast.success('Estimate converted to invoice successfully!');
      setSelectedEstimate(null);

      // Remove loading overlay
      const overlay = document.getElementById('conversion-loading-overlay');
      if (overlay) overlay.remove();

      // Refresh invoices list
      await fetchInvoices();

    } catch (error) {
      console.error('❌ Failed to convert estimate to invoice:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Full error object:', error);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to invoice';

      toast.error(`Conversion failed: ${errorMessage}`);

      // Remove loading overlay
      const overlay = document.getElementById('conversion-loading-overlay');
      if (overlay) overlay.remove();

      // Reopen the modal on error so user can retry
      setShowEstimateDetailModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Stock Confirmation Helper Functions
  const checkStockAvailability = async (items) => {
    try {
      console.log('🔍 Checking stock availability...');
      const stockResponse = await api.get(`/sales/products`);
      const inventoryProducts = stockResponse.data || [];

      const stockIssues = [];

      for (const item of items) {
        const productId = item.product_id;
        const requiredQuantity = item.quantity || 0;

        if (productId && requiredQuantity > 0) {
          const inventoryProduct = inventoryProducts.find(p =>
            (p._id === productId) || (p.id === productId)
          );

          if (!inventoryProduct) {
            const selectedProduct = products.find(p => p.id === productId);
            stockIssues.push({
              name: selectedProduct?.name || 'Unknown Product',
              requested: requiredQuantity,
              available: 0
            });
          } else {
            const availableStock = inventoryProduct.quantity || 0;
            if (availableStock < requiredQuantity) {
              const selectedProduct = products.find(p => p.id === productId);
              stockIssues.push({
                name: selectedProduct?.name || 'Unknown Product',
                requested: requiredQuantity,
                available: availableStock
              });
            }
          }
        }
      }

      return stockIssues;
    } catch (error) {
      console.error('❌ Error checking stock:', error);
      throw error;
    }
  };



  const handleCancelInvoice = () => {
    console.log('🔴 Cancel Invoice button clicked, opening modal');
    setShowCancellationModal(true);
  };

  const handleConfirmCancellation = async (cancellationData) => {
    try {
      setCancellationLoading(true);

      const invoiceId = selectedInvoice._id || selectedInvoice.id;
      console.log('🔴 handleConfirmCancellation received data:', cancellationData);
      console.log('🔴 Sending cancel request for invoice:', invoiceId, 'Data:', {
        reason: cancellationData.reason,
        paymentStatus: cancellationData.paymentStatus,
        cancelled_by: user?.id || user?.name || 'Unknown User'
      });

      const requestPayload = {
        cancellation_reason: cancellationData.reason,
        cancellation_payment_status: cancellationData.paymentStatus,
        cancelled_by: user?.id || user?.name || 'Unknown User'
      };
      console.log('🔴 Request payload being sent:', requestPayload);

      const response = await api.put(
        `/sales/invoices/${invoiceId}/cancel`,
        requestPayload
      );

      if (response.data.success) {
        console.log('🔴 Backend response:', response.data);
        console.log('🔴 Invoice from backend:', response.data.invoice);

        // Update the selected invoice with cancellation data from backend response
        const updatedInvoice = {
          ...selectedInvoice,
          status: response.data.invoice?.status || 'cancelled',
          cancellation_status: response.data.invoice?.cancellation_status || 'cancelled',
          cancellation_reason: response.data.invoice?.cancellation_reason || cancellationData.reason,
          cancellation_payment_status: response.data.invoice?.cancellation_payment_status || cancellationData.paymentStatus,
          cancelled_at: response.data.invoice?.cancelled_at || new Date().toISOString(),
          cancelled_by: response.data.invoice?.cancelled_by || user?.id || user?.name || 'Unknown User'
        };

        console.log('🔴 Updated invoice object:', updatedInvoice);

        setSelectedInvoice(updatedInvoice);

        // Refresh the invoices list
        fetchInvoices();

        toast.success('Invoice cancelled successfully');
        setShowCancellationModal(false);
      } else {
        throw new Error(response.data.message || 'Failed to cancel invoice');
      }
    } catch (error) {
      console.error('Failed to cancel invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel invoice');
    } finally {
      setCancellationLoading(false);
    }
  };

  // Helper function to check if refund is completed
  const isRefundCompleted = (invoice) => {
    const paymentStatus = invoice.payment_status ||
      invoice.paymentStatus ||
      invoice.cancellation_payment_status ||
      'pending';
    return paymentStatus === 'refund_completed';
  };

  // Handler to update payment status for cancelled invoices
  const handleUpdatePaymentStatus = async () => {
    if (!paymentStatusUpdate) {
      toast.error('Please select a payment status');
      return;
    }

    try {
      setPaymentStatusUpdateLoading(true);

      const invoiceId = selectedInvoice._id || selectedInvoice.id;
      const response = await api.put(
        `/sales/invoices/${invoiceId}/payment-status`,
        {
          cancellation_payment_status: paymentStatusUpdate
        }
      );

      if (response.data.success) {
        // Update the selected invoice with new payment status
        const updatedInvoice = {
          ...selectedInvoice,
          cancellation_payment_status: paymentStatusUpdate
        };

        setSelectedInvoice(updatedInvoice);

        // Refresh the invoices list
        fetchInvoices();

        toast.success('Payment status updated successfully');
        setShowPaymentStatusUpdateModal(false);
        setPaymentStatusUpdate('');
      } else {
        throw new Error(response.data.message || 'Failed to update payment status');
      }
    } catch (error) {
      console.error('Failed to update payment status:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment status');
    } finally {
      setPaymentStatusUpdateLoading(false);
    }
  };

  // Convert Closed Order to Invoice Functions
  const fetchClosedOrders = async () => {
    try {
      const response = await api.get(`/sales/closed-orders`);

      // Get all closed orders and filter client-side for better control
      const allClosedOrders = response.data.closedOrders || response.data || [];
      console.log('📋 Total closed orders received:', allClosedOrders.length);

      // Filter out converted and cancelled closed orders with comprehensive checks
      const unconvertedClosedOrders = allClosedOrders.filter(order => {
        const status = order.status || order.order_status || order.documentStatus;
        const isConverted = order.converted || order.is_converted || order.isConverted;
        const hasInvoiceNumber = order.invoice_number || order.invoiceNumber;
        const hasConvertedToInvoiceId = order.converted_to_invoice_id || order.convertedToInvoiceId;
        const conversionStatus = order.conversion_status || order.conversionStatus;
        const isCancelled = order.cancellation_status === 'cancelled' || status === 'cancelled';

        // Exclude if any conversion indicator is present or if cancelled
        const isConvertedOrder =
          status === 'converted_to_invoice' ||
          status === 'invoice' ||
          status === 'sold' ||
          isConverted === true ||
          hasInvoiceNumber ||
          hasConvertedToInvoiceId ||
          conversionStatus === 'converted_to_invoice';

        // Only include orders that are closed_order status and not converted and not cancelled
        return !isConvertedOrder && !isCancelled && (status === 'closed_order' || status === 'closed');
      });

      console.log('📋 Filtered unconverted closed orders:', unconvertedClosedOrders.length);

      // Force state update with immediate refresh
      setClosedOrders([]);
      setFilteredClosedOrders([]);

      // Use setTimeout to ensure state is cleared before setting new data
      setTimeout(() => {
        setClosedOrders(unconvertedClosedOrders);
        setFilteredClosedOrders(unconvertedClosedOrders);
      }, 100);
    } catch (error) {
      console.error('Failed to load closed orders:', error);
      toast.error('Failed to load closed orders');
    }
  };

  const handleOpenConvertClosedOrderModal = async () => {
    try {
      setLoading(true);
      console.log('🔄 Opening Convert Closed Order Modal - Fetching latest unconverted closed orders...');

      // Fetch fresh closed orders data
      await fetchClosedOrders();

      setShowConvertClosedOrderModal(true);
    } catch (error) {
      console.error('Failed to load closed orders:', error);
      toast.error('Failed to load closed orders for conversion');
    } finally {
      setLoading(false);
    }
  };

  const handleClosedOrderSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setClosedOrderSearchTerm(term);

    if (!term) {
      setFilteredClosedOrders(closedOrders);
    } else {
      const filtered = closedOrders.filter(order =>
        (order.closed_order_number?.toLowerCase().includes(term)) ||
        (order.order_number?.toLowerCase().includes(term)) ||
        (order.customer?.name?.toLowerCase().includes(term)) ||
        (order.customer_name?.toLowerCase().includes(term))
      );
      setFilteredClosedOrders(filtered);
    }
  };

  const handleSelectClosedOrder = async (closedOrder) => {
    try {
      console.log('🎯 Selected closed order for conversion:', closedOrder);
      console.log('📦 Initial closed order items:', closedOrder.items);

      setLoading(true);

      // Fetch next invoice number
      setInvoiceNumber('');
      // Load the next invoice number in the background so the modal opens immediately
      api.get('/sales/next-document-number?type=invoice')
        .then(res => { if (res.data?.nextNumber) setInvoiceNumber(prev => prev || res.data.nextNumber); })
        .catch(err => console.error('Failed to fetch next invoice number:', err));

      // Fetch full closed order details including items
      const closedOrderId = closedOrder.id || closedOrder._id;
      console.log('🔄 Fetching full closed order details for ID:', closedOrderId);

      const response = await api.get(`/sales/closed-orders/${closedOrderId}`);
      let fullClosedOrder = response.data;

      console.log('📋 Full closed order details received:', fullClosedOrder);
      console.log('📦 Full closed order items:', fullClosedOrder.items);

      // Ensure all items have complete product details using robust mapping
      if (fullClosedOrder.items && Array.isArray(fullClosedOrder.items)) {
        // Fetch categories and products for robust mapping
        const formData = await getFormData();
        const localCategories = formData.categories || [];
        const localProducts = formData.products || [];

        fullClosedOrder.items = recalcItemsAmounts(mapItemsForEditing(fullClosedOrder.items, localCategories, localProducts));
      }

      setSelectedClosedOrder(fullClosedOrder);
      setIsWoodWorkMode(!!fullClosedOrder.isWoodWork || !!fullClosedOrder.is_woodwork);
      setShowConvertClosedOrderModal(false);
      setShowClosedOrderDetailModal(true);

      // Initialize conversion data for comprehensive modal
      setConversionEditingCustomer({
        name: fullClosedOrder.customer_name || '',
        phone: fullClosedOrder.customer_phone || '',
        email: fullClosedOrder.customer_email || '',
        address: fullClosedOrder.customer_address || '',
        state: fullClosedOrder.customer_state || fullClosedOrder.party_state_name || fullClosedOrder.customer?.state || ''
      });
      setConversionPaymentData({
        payment_type: fullClosedOrder.payment_type || 'cash',
        gst_number: fullClosedOrder.customer_gst || '',
        description: fullClosedOrder.description || fullClosedOrder.order_description || '',
        amount_received: '', // Start empty for additional payment
        payment_status: fullClosedOrder.paymentStatus || fullClosedOrder.payment_status || 'pending',
        dispatched_from: fullClosedOrder.dispatched_from || '',
        delivered_to: fullClosedOrder.delivered_to || '',
        termsAndConditions: fullClosedOrder.termsAndConditions || '',
        date: new Date().toISOString().split('T')[0]
      });
      setEditMode(false);

      // Pre-fill invoice data with closed order details
      setInvoiceData({
        paymentType: fullClosedOrder.payment_type || 'cash',
        amountReceived: fullClosedOrder.amount_received || fullClosedOrder.amountReceived || 0,
        invoiceDescription: fullClosedOrder.description || fullClosedOrder.order_description || '',
        paymentStatus: fullClosedOrder.paymentStatus || fullClosedOrder.payment_status || 'pending',
        gstNumber: fullClosedOrder.customer?.gstNumber || fullClosedOrder.customer_gst || '',
        state: fullClosedOrder.customer_state || fullClosedOrder.party_state_name || fullClosedOrder.customer?.state || ''
      });

    } catch (error) {
      console.error('❌ Failed to fetch full closed order details:', error);
      toast.error('Failed to load closed order details');

      // Fallback to using the closed order as-is
      setSelectedClosedOrder(closedOrder);
      setIsWoodWorkMode(!!closedOrder.isWoodWork || !!closedOrder.is_woodwork);
      setShowConvertClosedOrderModal(false);
      setShowClosedOrderDetailModal(true);

      setConversionEditingCustomer({
        name: closedOrder.customer_name || '',
        phone: closedOrder.customer_phone || '',
        email: closedOrder.customer_email || '',
        address: closedOrder.customer_address || '',
        state: closedOrder.customer_state || closedOrder.party_state_name || closedOrder.customer?.state || ''
      });

      setConversionPaymentData({
        payment_type: closedOrder.payment_type || 'cash',
        gst_number: closedOrder.customer_gst || '',
        description: closedOrder.description || closedOrder.order_description || '',
        amount_received: '', // Start empty for additional payment
        payment_status: closedOrder.paymentStatus || closedOrder.payment_status || 'pending',
        termsAndConditions: closedOrder.termsAndConditions || '',
        materialDescription: closedOrder.materialDescription || '',
        date: new Date().toISOString().split('T')[0]
      });

      setInvoiceData({
        paymentType: closedOrder.payment_type || 'cash',
        amountReceived: closedOrder.amount_received || closedOrder.amountReceived || 0,
        invoiceDescription: closedOrder.description || closedOrder.order_description || '',
        paymentStatus: closedOrder.paymentStatus || closedOrder.payment_status || 'pending',
        gstNumber: closedOrder.customer?.gstNumber || closedOrder.customer_gst || '',
        state: closedOrder.customer_state || closedOrder.party_state_name || closedOrder.customer?.state || ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClosedOrderInvoiceSubmit = async () => {
    setLoading(true);

    const items = selectedClosedOrder.items || [];

    // Validate required fields before conversion
    if (!selectedClosedOrder || (!selectedClosedOrder.id && !selectedClosedOrder._id)) {
      toast.error('Invalid closed order selected. Please try again.');
      setLoading(false);
      return;
    }

    // Check for zero quantity items (this is still blocking as it's a data integrity issue)
    const zeroQuantityItems = items.filter(item => !item.quantity || item.quantity <= 0);
    if (zeroQuantityItems.length > 0) {
      const itemNames = zeroQuantityItems.map(item => item.product_name || 'Unnamed Product');
      toast.error(`Cannot convert to invoice: Products with zero quantity: ${itemNames.join(', ')}`, {
        duration: 6000
      });
      setLoading(false);
      return;
    }

    // Proceed directly without stock validation

    // Proceed with conversion
    await proceedWithClosedOrderToInvoiceConversion();
  };

  // Separate function for actual closed order to invoice conversion
  const proceedWithClosedOrderToInvoiceConversion = async () => {
    try {
      const closedOrderId = selectedClosedOrder.id || selectedClosedOrder._id;

      console.log('🔍 Selected Closed Order for conversion:', selectedClosedOrder);
      console.log('🔍 Selected Closed Order Items:', selectedClosedOrder.items);

      // Use items as-is, just like estimate conversion
      const items = recalcItemsAmounts(Array.isArray(selectedClosedOrder.items) ? selectedClosedOrder.items : []);

      console.log('📋 Items for conversion:', items);

      // Basic validation - just ensure we have items
      if (items.length === 0) {
        toast.error('No items found in closed order for conversion.');
        return;
      }

      const invoiceRequest = {
        invoice_number: invoiceNumber,
        payment_type: conversionPaymentData.payment_type || 'cash',
        amount_received: parseFloat(conversionPaymentData.amount_received) || 0, // Send NEW additional payment only (backend will add to previous payment)
        invoice_description: conversionPaymentData.description || '',
        customer_type: 'B2C',
        payment_status: 'pending',
        gst_number: conversionPaymentData.gst_number || '',
        dispatched_from: conversionPaymentData.dispatched_from || '',
        delivered_to: conversionPaymentData.delivered_to || '',
        user_id: user?.id,
        user_role: user?.role,
        date: conversionPaymentData.date || new Date().toISOString().split('T')[0],
        // Include customer data
        customer_name: conversionEditingCustomer.name || selectedClosedOrder.customer_name || 'Walk-in Customer',
        customer_phone: conversionEditingCustomer.phone || selectedClosedOrder.customer_phone || '',
        customer_email: conversionEditingCustomer.email || selectedClosedOrder.customer_email || '',
        customer_address: conversionEditingCustomer.address || selectedClosedOrder.customer_address || '',
        customer_state: conversionEditingCustomer.state || selectedClosedOrder.customer_state || selectedClosedOrder.party_state_name || selectedClosedOrder.customer?.state || '',
        items: items,
        termsAndConditions: includeTermsClosedOrder ? (conversionPaymentData.termsAndConditions || '') : '',
        materialDescription: conversionPaymentData.materialDescription !== undefined ? conversionPaymentData.materialDescription : (selectedClosedOrder.materialDescription || ''),
        isWoodWork: selectedClosedOrder.isWoodWork || selectedClosedOrder.is_woodwork || false
      };

      console.log('🔄 Sending closed order to invoice conversion request:');
      console.log('📋 Closed Order ID:', closedOrderId);
      console.log('📋 Invoice Request:', invoiceRequest);
      console.log('📋 Processed Items:', invoiceRequest.items);

      // Send request to convert closed order to invoice
      const response = await api.put(
        `/sales/closed-orders/${closedOrderId}/invoice`,
        invoiceRequest
      );

      console.log('✅ Closed order converted to invoice successfully:', response.data);

      const createdInvoiceNumber = response.data.invoice?.invoice_number || response.data.invoice_number || 'N/A';
      const createdInvoice = response.data.invoice || response.data;

      // Show success toast with proper message
      toast.success(`Closed Order converted to Invoice successfully! Invoice #${createdInvoiceNumber}`);

      // Close conversion modal
      setShowClosedOrderDetailModal(false);
      setSelectedClosedOrder(null);

      // Refresh invoices list
      await fetchInvoices();

      // Switch to history tab to show the newly created invoice
      setActiveTab('history');

    } catch (error) {
      console.error('❌ Failed to convert closed order to invoice:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Full error object:', error);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert closed order to invoice';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-4 md:py-8 min-h-screen">
      <div className="mx-auto bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 ease-in-out p-4 md:p-8" style={{ width: '98%', maxWidth: '1400px', margin: '10px auto' }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 bg-clip-text text-transparent mb-4">
            Invoices Management
          </h1>
          <p className="text-lg text-slate-600 font-medium">Create new invoices and manage existing ones</p>
        </div>

        {/* Navigation Tabs & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          {/* Left: Tabs */}
          <div className="flex space-x-1 bg-slate-100 p-1 rounded-2xl">
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'history'
                ? 'bg-[#0A5F38] text-white shadow-md'
                : 'text-slate-600 hover:text-[#0A5F38]'
                }`}
            >
              <Receipt className="h-5 w-5 inline mr-2" />
              Invoices History
            </button>
            <button
              onClick={handleNewInvoice}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'form'
                ? 'bg-[#0A5F38] text-white shadow-md'
                : 'text-slate-600 hover:text-[#0A5F38]'
                }`}
            >
              <Plus className="h-5 w-5 inline mr-2" />
              {isEditing ? 'Edit Invoice' : 'New Invoice'}
            </button>
          </div>

          {/* Right: Convert Actions (Visible only in History tab) */}
          {activeTab === 'history' && (
            <div className="flex flex-row gap-4">
              <button
                onClick={handleOpenConvertModal}
                className="px-6 py-3 bg-white border border-[#C5A57D] text-[#0A5F38] font-semibold rounded-xl hover:bg-[#C5A57D]/10 transition-colors flex items-center h-full text-sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Convert Estimate
              </button>
              <button
                onClick={handleOpenConvertClosedOrderModal}
                className="px-6 py-3 bg-white border border-[#C5A57D] text-[#0A5F38] font-semibold rounded-xl hover:bg-[#C5A57D]/10 transition-colors flex items-center h-full text-sm"
              >
                <FileText className="h-4 w-4 mr-2" />
                Convert Closed Order
              </button>
            </div>
          )}
        </div>

        {/* Content based on active tab */}
        {activeTab === 'history' ? (
          <div className="space-y-6">
            {/* Filters Row */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              {/* Search - Placed Left */}
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#2D5A27] h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 w-full border border-[#E5E5E0] rounded-2xl focus:outline-none focus:border-[#2D5A27] focus:ring-1 focus:ring-[#2D5A27] bg-[#F9F9F7] shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] placeholder-[#A0A090] placeholder:italic transition-all duration-200 text-slate-700"
                />
              </div>
            </div>
            
            <AdvancedFilters
              filters={filters}
              setFilters={setFilters}
              onApply={() => fetchInvoices(0, 1)}
              onClear={() => {
                setFilters({
                  dateRange: 'all',
                  status: 'all',
                  paymentStatus: 'all',
                  startDate: '',
                  endDate: '',
                  minAmount: '',
                  maxAmount: '',
                  isWoodWork: 'all',
                  salesperson_id: 'all'
                });
                setTimeout(() => fetchInvoices(0, 1), 0);
              }}
              showSalesperson={false} // Hidden for employees
              showPaymentStatus={true}
              statusOptions={[
                { value: 'all', label: 'All Status' },
                { value: 'sold', label: 'Active Invoices' },
                { value: 'cancelled', label: 'Cancelled' }
              ]}
            />

            {/* Invoices Table */}
            <div className="bg-[#E8DCC4] p-4 md:p-[38px] rounded-3xl">
              <div className="bg-[#F9F6F0] rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/60 overflow-hidden">
                <div className="overflow-x-auto">
                  {filteredInvoices.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-900 mb-2">No Invoices Found</h3>
                      <p className="text-slate-500">
                        {searchTerm
                          ? 'No invoices match your search criteria.'
                          : 'No invoices found. Convert an estimate or create your first invoice.'}
                      </p>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-[#005C2D]">
                        <tr>
                          <th
                            className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                            onClick={() => handleSort('date')}
                          >
                            <div className="flex items-center justify-center space-x-1">
                              <span>Date</span>
                              {sortConfig.key === 'date' && (
                                <span className="text-white">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                            Invoice Number
                          </th>
                          <th
                            className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                            onClick={() => handleSort('customer')}
                          >
                            <div className="flex items-center justify-center space-x-1">
                              <span>Customer /<br />Company Name</span>
                              {sortConfig.key === 'customer' && (
                                <span className="text-white">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                            onClick={() => handleSort('amount')}
                          >
                            <div className="flex items-center justify-center space-x-1">
                              <span>Amount</span>
                              {sortConfig.key === 'amount' && (
                                <span className="text-white">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                            Invoice Status
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                            Payment Status
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-100">
                        {filteredInvoices.map((invoice, index) => (
                          <tr key={invoice.id} className={`transition-colors duration-200 ${index % 2 === 0
                            ? 'bg-white hover:bg-[#F3EFE0]/50'
                            : 'bg-[#F3EFE0] hover:bg-[#E8DCC4]/50'
                            }`}>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                              {invoice.date ?
                                new Date(invoice.date).toLocaleDateString('en-IN') :
                                invoice.createdAt ?
                                  new Date(invoice.created_at).toLocaleDateString('en-IN') :
                                  'Invalid Date'
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="text-sm font-semibold text-slate-900">
                                {(invoice.invoice_number || invoice._id?.slice(-8) || 'INV' + invoice.id).replace(/-CANCEL-\d+/g, ' (Cancelled)')}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-normal break-words max-w-[250px] text-center">
                              <div className="text-sm font-medium text-slate-900 break-words">
                                {invoice.customer_name || 'Walk-in Customer'}
                              </div>
                              <div className="text-sm text-slate-500">
                                {invoice.customer_phone || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="text-sm font-semibold text-slate-900">
                                ₹{(invoice.totalAmount || invoice.total_amount || 0).toFixed(1)}
                              </div>
                              {invoice.amount_received > 0 && (
                                <div className="text-xs text-slate-500">
                                  Paid: ₹{invoice.amount_received?.toFixed(1)}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {invoice.cancellation_status === 'cancelled' || invoice.status === 'cancelled' ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                  Cancelled
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  Active
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {invoice.cancellation_status === 'cancelled' || invoice.status === 'cancelled' ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                                  {invoice.cancellation_payment_status === 'refund_initiated' && 'Refund Initiated'}
                                  {invoice.cancellation_payment_status === 'refund_completed' && 'Refund Completed'}
                                  {invoice.cancellation_payment_status === 'no_payment' && 'No Payment'}
                                  {!invoice.cancellation_payment_status && 'Cancelled'}
                                </span>
                              ) : (
                                getPaymentStatusBadge(invoice)
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                              <div className="flex justify-center space-x-1">
                                <button
                                  onClick={() => handleViewInvoice(invoice)}
                                  className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4 text-[#0A5F38]" />
                                </button>
                                <DocumentHistoryButton docId={invoice.id || invoice._id} />
                                <button
                                  onClick={async () => {
                                    try {
                                      // Use the invoice data directly from the table (it already has all the info)
                                      console.log('📄 PDF Download - Invoice Data:', invoice);
                                      // Ensure all fields are present for PDF
                                      const pdfData = {
                                        ...invoice,
                                        dispatched_from: invoice.dispatched_from || '',
                                        delivered_to: invoice.delivered_to || invoice.deliveredTo || ''
                                      };

                                      await generatePDF(pdfData, 'invoice', companySettings);
                                    } catch (error) {
                                      console.error('Failed to download PDF:', error);
                                      toast.error('Failed to download PDF');
                                    }
                                  }}
                                  className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                  title="Download PDF"
                                >
                                  <Download className="h-4 w-4 text-[#0A5F38]" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (!invoice?.shareToken) {
                                      toast.error('Share token not available yet.');
                                      return;
                                    }
                                    const guestLink = `${window.location.origin}/view/document/${invoice.shareToken}`;
                                    navigator.clipboard.writeText(guestLink)
                                      .then(() => toast.success('Guest link copied to clipboard!'))
                                      .catch(() => toast.error('Failed to copy link'));
                                  }}
                                  className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                  title="Copy Guest Link"
                                >
                                  <Link2 className="h-4 w-4 text-[#0A5F38]" />
                                </button>
                                {user?.role === 'admin' && (
                                  <button
                                    onClick={() => handleDeleteInvoice(invoice)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete Invoice"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>


                {/* Pagination */}
                {pagination.totalItems > 0 && (
                  <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-slate-200">
                    <div className="text-sm text-slate-600">
                      Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                      {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                      {pagination.totalItems} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => fetchInvoices(0, pagination.currentPage - 1)}
                        disabled={!pagination.hasPreviousPage}
                        className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => fetchInvoices(0, pagination.currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Form Content - Invoice Creation Form
          <div className="bg-[#F8F5E6] rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-4 md:p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading Form Data...</h3>
                <p className="text-slate-600">Please wait while we fetch categories, products, and employees.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Form Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-[#0A5F38] mb-2">
                    {isEditing ? 'Edit Invoice' : 'Create New Invoice'}
                  </h3>
                  <p className="text-slate-600">Fill in the details below to create an invoice</p>
                  
                  {/* AI Upload Action */}
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setShowPOUploadModal(true)}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-bold rounded-2xl shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5 transition-all duration-300 group"
                    >
                      <Paperclip className="h-5 w-5 mr-2 group-hover:rotate-12 transition-transform" />
                      📎 Upload PO (AI Auto-Creation)
                      <span className="ml-2 px-2 py-0.5 bg-indigo-500/50 rounded-lg text-[10px] uppercase tracking-wider">Beta</span>
                    </button>
                  </div>
                </div>

                {/* Salesperson Selection */}
                <div className="bg-white rounded-2xl p-6">
                  <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide mb-4">Invoice Information</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Assigned Salesperson</label>
                      <div className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 font-medium">
                        {user?.name || 'Not logged in'}
                      </div>
                    </div>
                    <ValidatedInput
                      label="Invoice Number"
                      type="text"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      validationType="required"
                      required={true}
                      placeholder="Generating..."
                      className="input-field"
                      showErrors={formTouched}
                    />
                    <ValidatedInput
                      label="Invoice Date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      validationType="required"
                      required={true}
                      className="input-field"
                      showErrors={formTouched}
                    />
                  </div>
                </div>

                {/* Customer Information */}
                <div className="bg-white rounded-2xl p-4 md:p-6 overflow-hidden">
                  <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide mb-4">Customer Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ValidatedInput
                      label={<div className="flex items-center">Customer Name {aiFilledFields.has('customer.name') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200 animate-pulse">✦ AI</span>}</div>}
                      type="text"
                      value={formData.customer.name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          customer: { ...formData.customer, name: e.target.value }
                        });
                        clearAiHighlight('customer.name');
                      }}
                      validationType="name"
                      required={true}
                      placeholder="Enter customer name"
                      className={`input-field ${aiFilledFields.has('customer.name') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : ''}`}
                      showErrors={formTouched}
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.customer.countryCode || ''}
                          onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, countryCode: e.target.value } })}
                          placeholder="+91"
                          maxLength={5}
                          className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] text-sm"
                        />
                        <input
                          type="tel"
                          value={formData.customer.phone || ''}
                          onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, phone: e.target.value.replace(/\D/g, '') } })}
                          placeholder={formData.customer.countryCode ? 'Mobile number' : '10-digit mobile number'}
                          maxLength={formData.customer.countryCode ? 11 : 10}
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] text-sm"
                        />
                      </div>
                      {formTouched && validationErrors.customerPhone && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.customerPhone}</p>
                      )}
                    </div>
                    <ValidatedInput
                      label={<div className="flex items-center">Email {aiFilledFields.has('customer.email') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200">✦ AI</span>}</div>}
                      type="email"
                      value={formData.customer.email}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          customer: { ...formData.customer, email: e.target.value }
                        });
                        clearAiHighlight('customer.email');
                      }}
                      validationType="email"
                      required={false}
                      placeholder="Enter email address"
                      className={`input-field ${aiFilledFields.has('customer.email') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : ''}`}
                      showErrors={formTouched}
                    />
                    <ValidatedInput
                      label={<div className="flex items-center">Address {aiFilledFields.has('customer.address') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200">✦ AI</span>}</div>}
                      type="text"
                      value={formData.customer.address}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          customer: { ...formData.customer, address: e.target.value }
                        });
                        clearAiHighlight('customer.address');
                      }}
                      validationType="address"
                      required={false}
                      placeholder="Enter address"
                      className={`input-field ${aiFilledFields.has('customer.address') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : ''}`}
                      showErrors={formTouched}
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                        State {aiFilledFields.has('customer.state') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200">✦ AI</span>}
                      </label>
                      <select
                        value={formData.customer.state || ''}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            customer: { ...formData.customer, state: e.target.value }
                          });
                          clearAiHighlight('customer.state');
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white ${aiFilledFields.has('customer.state') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                      >
                        <option value="">Select State</option>
                        {stateOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <ValidatedInput
                      label={<div className="flex items-center">GST Number {aiFilledFields.has('customer.gstNumber') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200">✦ AI</span>}</div>}
                      type="text"
                      value={formData.customer.gstNumber}
                      onChange={(e) => {
                        handleGSTFieldChange(e.target.value,
                          (gstValue) => setFormData({
                            ...formData,
                            customer: { ...formData.customer, gstNumber: gstValue }
                          }),
                          (stateCallback) => setFormData(prev => ({
                            ...prev,
                            customer: { ...prev.customer, state: stateCallback }
                          }))
                        );
                        clearAiHighlight('customer.gstNumber');
                      }}
                      validationType="gstNumber"
                      required={false}
                      placeholder="Enter 15-digit GST number"
                      className={`input-field ${aiFilledFields.has('customer.gstNumber') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : ''}`}
                      showErrors={formTouched}
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                        Dispatched from {aiFilledFields.has('dispatched_from') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200">✦ AI</span>}
                      </label>
                      <input
                        type="text"
                        value={formData.dispatched_from}
                        onChange={(e) => {
                          setFormData({ ...formData, dispatched_from: e.target.value });
                          clearAiHighlight('dispatched_from');
                        }}
                        placeholder="Enter dispatch location"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${aiFilledFields.has('dispatched_from') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                        Delivered to {aiFilledFields.has('delivered_to') && <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md border border-indigo-200">✦ AI</span>}
                      </label>
                      <input
                        type="text"
                        value={formData.delivered_to}
                        onChange={(e) => {
                          setFormData({ ...formData, delivered_to: e.target.value });
                          clearAiHighlight('delivered_to');
                        }}
                        placeholder="Enter delivery location"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${aiFilledFields.has('delivered_to') ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Items Section - Table Layout */}
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide">Items</h4>
                    <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                      <span className="text-sm font-medium text-slate-700">Apply Discount</span>
                      <button
                        type="button"
                        onClick={() => setShowDiscount(!showDiscount)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${showDiscount ? 'bg-[#C5A57D]' : 'bg-slate-200'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showDiscount ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Items Table - Responsive Layout */}
                  <div className="w-full">
                    {/* Desktop & Tablet Table View */}
                    <div className="hidden md:block">
                      <div className="overflow-x-auto border border-slate-300 rounded-lg bg-white">
                        <table className="border-collapse min-w-[1000px] w-full">
                          <thead>
                            <tr className="bg-white border-b border-slate-300">
                              <th className="border-r border-slate-300 px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '50px', minWidth: '50px' }}>S.No.</th>
                              <th className="border-r border-slate-300 px-3 py-3 text-left text-sm font-semibold text-slate-700" style={{ width: '180px', minWidth: '180px' }}>CATEGORY*</th>
                              <th className="border-r border-slate-300 px-3 py-3 text-left text-sm font-semibold text-slate-700" style={{ width: '250px', minWidth: '250px' }}>PRODUCT*</th>
                              <th className="border-r border-slate-300 px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '100px', minWidth: '100px' }}>HSN CODE</th>
                              <th className="border-r border-slate-300 px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '90px', minWidth: '90px' }}>UNIT</th>
                              <th className="border-r border-slate-300 px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '80px', minWidth: '80px' }}>QTY*</th>
                              <th className="border-r border-slate-300 px-3 py-3 text-right text-sm font-semibold text-slate-700" style={{ width: '110px', minWidth: '110px' }}>PRICE*</th>
                              {showDiscount && <th className="border-r border-slate-300 px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '80px', minWidth: '80px' }}>DISC%</th>}
                              <th className="border-r border-slate-300 px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '80px', minWidth: '80px' }}>GST%</th>
                              <th className="px-3 py-3 text-right text-sm font-semibold text-slate-700" style={{ width: '120px', minWidth: '120px' }}>AMOUNT</th>
                              <th className="px-3 py-3 text-center text-sm font-semibold text-slate-700" style={{ width: '80px', minWidth: '80px' }}>ACTION</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => {
                              // Calculate per-item amounts
                              const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                              const discountAmount = (rawAmount * (item.discount || 0)) / 100;
                              const taxableAmount = rawAmount - discountAmount;
                              const gstAmount = (taxableAmount * (item.gst_rate || 0)) / 100;
                              const itemTotal = taxableAmount + gstAmount;

                              return (
                                <tr key={index} className="border-b border-slate-200 hover:bg-white">
                                  <td className="border-r border-slate-300 px-3 py-3 text-center text-sm font-medium text-slate-700" style={{ width: '50px', minWidth: '50px' }}>
                                    {index + 1}
                                  </td>
                                  <td className="border-r border-slate-300 px-3 py-3" style={{ width: '180px', minWidth: '180px' }}>
                                    <div className="relative">
                                      <select
                                        value={item.category_id}
                                        onChange={(e) => {
                                          const newItems = [...items];
                                          const selectedCategory = categories.find(c => String(c.id) === String(e.target.value));
                                          const isOthers = selectedCategory?.isOthers || false;
                                          newItems[index].category_id = e.target.value;
                                          newItems[index].isOthers = isOthers;
                                          newItems[index].product_id = '';
                                          newItems[index].product_name = '';
                                          newItems[index].unit_type = isOthers ? 'PCS' : 'SFT';
                                          newItems[index].unit_price = 0;
                                          newItems[index].gst_rate = '';
                                          setItems(newItems);
                                          clearAiHighlight(`item-${index}-category`);
                                        }}
                                        className={`w-full px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-500 bg-white ${aiFilledFields.has(`item-${index}-category`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                        required
                                      >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                      </select>
                                      {aiFilledFields.has(`item-${index}-category`) && (
                                        <span className="absolute -top-2 -right-2 text-[8px] bg-indigo-600 text-white px-1 rounded-full animate-bounce">AI</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="border-r border-slate-300 px-3 py-3" style={{ width: '250px', minWidth: '250px' }}>
                                    <div className="relative">
                                      {item.isOthers ? (
                                        <input
                                          type="text"
                                          value={item.product_name}
                                          onChange={(e) => {
                                            const newItems = [...items];
                                            newItems[index].product_name = e.target.value;
                                            setItems(newItems);
                                            clearAiHighlight(`item-${index}-product`);
                                          }}
                                          placeholder="Enter product name"
                                          className={`w-full px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-500 ${aiFilledFields.has(`item-${index}-product`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                        />
                                      ) : item.product_id ? (
                                        <div className="flex items-center gap-1">
                                          <input
                                            type="text"
                                            value={item.product_name}
                                            onChange={(e) => {
                                              const newItems = [...items];
                                              newItems[index].product_name = e.target.value;
                                              setItems(newItems);
                                              clearAiHighlight(`item-${index}-product`);
                                            }}
                                            placeholder="Enter product name"
                                            className={`w-full px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-500 ${aiFilledFields.has(`item-${index}-product`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newItems = [...items];
                                              newItems[index].product_id = '';
                                              newItems[index].product_name = '';
                                              setItems(newItems);
                                            }}
                                            className="p-1 text-slate-400 hover:text-red-500"
                                            title="Change product"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <select
                                          value={item.product_id}
                                          onChange={(e) => {
                                            const newItems = [...items];
                                            const prod = products.find(p => String(p.id) === String(e.target.value));
                                            newItems[index].product_id = e.target.value;
                                            newItems[index].product_name = prod ? prod.name : '';
                                            newItems[index].hsn_code = prod ? prod.hsn_code : '';
                                            newItems[index].unit_price = prod ? prod.price : 0;
                                            newItems[index].gst_rate = prod ? prod.gst_rate : '';
                                            setItems(newItems);
                                            clearAiHighlight(`item-${index}-product`);
                                          }}
                                          className={`w-full px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-500 bg-white ${aiFilledFields.has(`item-${index}-product`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                          disabled={!item.category_id}
                                        >
                                          <option value="">Select Product</option>
                                          {products
                                            .filter(p => String(p.category_id) === String(item.category_id))
                                            .map(p => (
                                              <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                      )}
                                      {aiFilledFields.has(`item-${index}-product`) && (
                                        <span className="absolute -top-2 -right-2 text-[8px] bg-indigo-600 text-white px-1 rounded-full">AI</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="border-r border-slate-300 px-3 py-3 text-center" style={{ width: '100px', minWidth: '100px' }}>
                                    <input
                                      type="text"
                                      value={item.hsnCode || ''}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[index].hsnCode = e.target.value;
                                        setItems(newItems);
                                      }}
                                      className="w-full px-2 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      placeholder="-"
                                    />
                                  </td>
                                  <td className="border-r border-slate-300 px-3 py-3 text-center" style={{ width: '90px', minWidth: '90px' }}>
                                    <select
                                      value={item.unit_type || 'SFT'}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[index].unit_type = e.target.value;
                                        setItems(newItems);
                                      }}
                                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                    >
                                      <option value="SFT">SFT</option>
                                      <option value="RFT">RFT</option>
                                      <option value="PCS">PCS</option>
                                      <option value="KG">KG</option>
                                      <option value="LTR">LTR</option>
                                      <option value="MTR">MTR</option>
                                      <option value="LUMPSUM">LUMPSUM</option>
                                      <option value="NOS">NOS</option>
                                      <option value="SQ.M">SQ.M</option>
                                    </select>
                                  </td>
                                  <td className="border-r border-slate-300 px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      step="any"
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[index].quantity = parseFloat(e.target.value) || 0;
                                        setItems(newItems);
                                        clearAiHighlight(`item-${index}-quantity`);
                                      }}
                                      className={`w-full px-1 py-1 border rounded text-center text-xs focus:ring-1 focus:ring-indigo-500 ${aiFilledFields.has(`item-${index}-quantity`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                    />
                                  </td>
                                  <td className="border-r border-slate-300 px-3 py-3 text-right" style={{ width: '110px', minWidth: '110px' }}>
                                    <input
                                      type="number"
                                      step="any"
                                      value={item.unit_price}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[index].unit_price = parseFloat(e.target.value) || 0;
                                        setItems(newItems);
                                        clearAiHighlight(`item-${index}-price`);
                                      }}
                                      className={`w-full px-1 py-1 border rounded text-right text-xs focus:ring-1 focus:ring-indigo-500 ${aiFilledFields.has(`item-${index}-price`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                    />
                                  </td>
                                  {showDiscount && (
                                    <td className="border-r border-slate-300 px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="any"
                                        value={item.discount === '' ? '' : (item.discount || 0)}
                                        onChange={(e) => {
                                          const newItems = [...items];
                                          const val = e.target.value;
                                          newItems[index].discount = val === '' ? '' : parseFloat(val);
                                          setItems(newItems);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                          }
                                        }}
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full px-3 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                    </td>
                                  )}
                                  <td className="border-r border-slate-300 px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                    <input
                                      type="number"
                                      step="any"
                                      value={item.gst_rate}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[index].gst_rate = parseFloat(e.target.value) || 0;
                                        setItems(newItems);
                                        clearAiHighlight(`item-${index}-gst`);
                                      }}
                                      className={`w-full px-1 py-1 border rounded text-center text-xs focus:ring-1 focus:ring-indigo-500 ${aiFilledFields.has(`item-${index}-gst`) ? 'ring-2 ring-indigo-400 border-indigo-400 bg-indigo-50/30' : 'border-slate-300'}`}
                                    />
                                  </td>
                                  <td className="px-3 py-3 text-right font-semibold text-slate-900 text-sm" style={{ width: '120px', minWidth: '120px' }}>
                                    ₹{itemTotal.toFixed(1)}
                                  </td>
                                  <td className="px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                    <button
                                      type="button"
                                      onClick={() => setItems(items.filter((_, i) => i !== index))}
                                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                      title="Remove item"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Add New Item Row */}
                            <tr className="border-t-2 border-slate-300 bg-white">
                              <td colSpan="11" className="px-3 py-4">
                                <button
                                  type="button"
                                  onClick={() => setItems([...items, {
                                    category_id: '',
                                    product_id: '',
                                    product_name: '',
                                    unit_type: 'SFT',
                                    quantity: 1,
                                    unit_price: 0,
                                    discount: 0,
                                    gst_rate: 0,
                                    gst_amount: 0,
                                    amount: 0,
                                    total_amount: 0,
                                    unitPrice: 0,
                                    tax: 0,
                                    subtotal: 0
                                  }])}
                                  className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add New Item
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="md:hidden space-y-4">
                      {items.map((item, index) => {
                        // Calculate per-item amounts
                        const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                        const discountAmount = (rawAmount * (item.discount || 0)) / 100;
                        const taxableAmount = rawAmount - discountAmount;
                        const gstAmount = (taxableAmount * (item.gst_rate || 0)) / 100;
                        const itemTotal = taxableAmount + gstAmount;

                        return (
                          <div key={index} className="bg-white border border-slate-300 rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-semibold text-slate-900">Item {index + 1}</h4>
                              <button
                                type="button"
                                onClick={() => setItems(items.filter((_, i) => i !== index))}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                title="Remove item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                                <select
                                  value={item.category_id}
                                  onChange={(e) => {
                                    const newItems = [...items];
                                    const selectedCategory = categories.find(c => String(c.id) === String(e.target.value));
                                    const isOthers = selectedCategory?.isOthers || false;
                                    newItems[index].category_id = e.target.value;
                                    newItems[index].isOthers = isOthers;
                                    newItems[index].product_id = '';
                                    newItems[index].product_name = '';
                                    newItems[index].unit_type = isOthers ? 'PCS' : 'SFT';
                                    newItems[index].unit_price = 0;
                                    newItems[index].gst_rate = '';
                                    newItems[index].product_instructions = '';
                                    setItems(newItems);
                                  }}
                                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                  required
                                >
                                  <option value="">Select ▼</option>
                                  {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                      {cat.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Product *</label>
                                {item.isOthers ? (
                                  (() => {
                                    const productsInCategory = products.filter(prod => {
                                      const selectedCat = String(item.category_id);
                                      const productCat = String(prod.categoryId || prod.category_id || '');
                                      return productCat === selectedCat;
                                    });

                                    if (productsInCategory.length === 0 || item.showFreeText) {
                                      return (
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={item.product_name || ''}
                                            onChange={(e) => {
                                              const newItems = [...items];
                                              newItems[index].product_name = e.target.value;
                                              setItems(newItems);
                                            }}
                                            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            placeholder="Enter product name"
                                            required
                                          />
                                          {productsInCategory.length > 0 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newItems = [...items];
                                                newItems[index].showFreeText = false;
                                                newItems[index].product_id = '';
                                                newItems[index].product_name = '';
                                                setItems(newItems);
                                              }}
                                              className="px-3 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                              title="Show existing products"
                                            >
                                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                              </svg>
                                            </button>
                                          )}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <select
                                          value={item.product_id}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const newItems = [...items];
                                            if (val === 'ADD_NEW_OTHER') {
                                              newItems[index].showFreeText = true;
                                              newItems[index].product_id = '';
                                              newItems[index].product_name = '';
                                            } else {
                                              const selectedProduct = products.find(p => String(p.id) === String(val));
                                              newItems[index].product_id = val;
                                              newItems[index].product_name = selectedProduct?.name || '';
                                              newItems[index].unit_price = selectedProduct?.sellingPrice || 0;
                                              newItems[index].unit_type = selectedProduct?.unit_type || 'PCS';
                                              const gstRate = selectedProduct?.product?.gstPercentage || selectedProduct?.gstPercentage;
                                              newItems[index].gst_rate = (gstRate !== undefined && gstRate !== null) ? gstRate : 18;
                                              const hsnCode = selectedProduct?.product?.hsnCode || selectedProduct?.hsnCode;
                                              newItems[index].hsnCode = hsnCode || '';
                                            }
                                            setItems(newItems);
                                          }}
                                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                          required
                                        >
                                          <option value="">Select Option</option>
                                          <option value="ADD_NEW_OTHER" className="font-bold text-indigo-600">+ Add Other</option>
                                          {productsInCategory.map(prod => (
                                            <option key={prod.id} value={prod.id}>
                                              {prod.name}
                                            </option>
                                          ))}
                                        </select>
                                      );
                                    }
                                  })()
                                ) : item.product_id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={item.product_name || ''}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[index].product_name = e.target.value;
                                        setItems(newItems);
                                      }}
                                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      placeholder="Edit product name"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newItems = [...items];
                                        newItems[index].product_id = '';
                                        newItems[index].product_name = '';
                                        setItems(newItems);
                                      }}
                                      className="px-3 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                      title="Change product"
                                    >
                                      <Plus className="h-5 w-5 rotate-45" />
                                    </button>
                                  </div>
                                ) : (
                                  <select
                                    value={item.product_id}
                                    onChange={(e) => {
                                      const newItems = [...items];
                                      const selectedProduct = products.find(p => p.id === e.target.value);
                                      newItems[index].product_id = e.target.value;
                                      newItems[index].product_name = selectedProduct?.name || '';
                                      newItems[index].unit_price = selectedProduct?.sellingPrice || 0;

                                      // Robust GST population matching EstimatesPage.js
                                      const gstRate = selectedProduct?.product?.gstPercentage || selectedProduct?.gstPercentage;
                                      newItems[index].gst_rate = (gstRate !== undefined && gstRate !== null) ? gstRate : 18;

                                      // Robust HSN and Unit Type population
                                      const hsnCode = selectedProduct?.product?.hsnCode || selectedProduct?.hsnCode;
                                      newItems[index].hsnCode = hsnCode || '';

                                      const unitType = selectedProduct?.product?.dimensions?.unit || selectedProduct?.unit_type;
                                      newItems[index].unit_type = unitType || 'SFT';

                                      setItems(newItems);
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                    required
                                    disabled={!item.category_id}
                                  >
                                    <option value="">
                                      {!item.category_id ? 'Select Category ▼' : 'Select ▼'}
                                    </option>
                                    {item.category_id && products
                                      .filter(prod => {
                                        const selectedCat = String(item.category_id);
                                        const productCat = String(prod.categoryId || prod.category_id || '');
                                        return productCat === selectedCat;
                                      })
                                      .map(prod => (
                                        <option key={prod.id} value={prod.id}>
                                          {prod.name}
                                        </option>
                                      ))}
                                  </select>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">HSN Code</label>
                                <input
                                  type="text"
                                  value={item.hsnCode || ''}
                                  onChange={(e) => {
                                    const newItems = [...items];
                                    newItems[index].hsnCode = e.target.value;
                                    setItems(newItems);
                                  }}
                                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  placeholder="Enter HSN code"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                                  <select
                                    value={item.unit_type || 'SFT'}
                                    onChange={(e) => {
                                      const newItems = [...items];
                                      newItems[index].unit_type = e.target.value;
                                      setItems(newItems);
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                  >
                                    <option value="SFT">SFT</option>
                                    <option value="RFT">RFT</option>
                                    <option value="PCS">PCS</option>
                                    <option value="KG">KG</option>
                                    <option value="LTR">LTR</option>
                                    <option value="MTR">MTR</option>
                                    <option value="LUMPSUM">LUMPSUM</option>
                                    <option value="NOS">NOS</option>
                                    <option value="SQ.M">SQ.M</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Qty *</label>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={item.quantity === '' ? '' : (item.quantity || 1)}
                                    onChange={(e) => {
                                      const newItems = [...items];
                                      const val = e.target.value;
                                      newItems[index].quantity = val === '' ? '' : (parseInt(val) || 1);
                                      setItems(newItems);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                      }
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full px-3 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Price *</label>
                                <input
                                  type="number"
                                  step="any"
                                  value={item.unit_price === '' ? '' : item.unit_price}
                                  onChange={(e) => {
                                    const newItems = [...items];
                                    const val = e.target.value;
                                    newItems[index].unit_price = val === '' ? '' : parseFloat(val);
                                    setItems(newItems);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                    }
                                  }}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-full px-3 py-2 text-sm text-right border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div className={`grid ${showDiscount ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                                {showDiscount && (
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Disc %</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="any"
                                      value={item.discount === '' ? '' : (item.discount || 0)}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        const val = e.target.value;
                                        newItems[index].discount = val === '' ? '' : parseFloat(val);
                                        setItems(newItems);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                          e.preventDefault();
                                        }
                                      }}
                                      onWheel={(e) => e.target.blur()}
                                      className="w-full px-3 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                  </div>
                                )}
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">GST %</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="any"
                                    value={item.gst_rate === '' ? '' : (item.gst_rate || 0)}
                                    onChange={(e) => {
                                      const newItems = [...items];
                                      const val = e.target.value;
                                      newItems[index].gst_rate = val === '' ? '' : parseFloat(val);
                                      setItems(newItems);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                      }
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full px-3 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                              </div>

                              <div className="bg-white rounded-md p-3">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-slate-700">Amount:</span>
                                  <span className="font-bold text-slate-900">₹{itemTotal.toFixed(1)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Add New Item Button - Mobile */}
                      <div className="bg-white border border-slate-300 rounded-lg p-4 shadow-sm border-dashed">
                        <button
                          type="button"
                          onClick={() => setItems([...items, {
                            category_id: '',
                            product_id: '',
                            product_name: '',
                            unit_type: 'SFT',
                            quantity: 1,
                            unit_price: 0,
                            discount: 0,
                            gst_rate: 18,
                            gst_amount: 0,
                            amount: 0,
                            total_amount: 0,
                            unitPrice: 0,
                            tax: 0,
                            subtotal: 0,
                            hsnCode: ''
                          }])}
                          className="w-full flex items-center justify-center px-4 py-3 text-indigo-600 hover:text-indigo-700 font-medium text-sm border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Add New Item
                        </button>
                      </div>
                    </div>

                    {/* Invoice Summary Section */}
                    <div className="bg-white border border-slate-300 rounded-lg p-6 mt-6">
                      <h4 className="text-lg font-semibold text-slate-900 mb-6">INVOICE SUMMARY</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Left Column - Summary */}
                        <div className="space-y-3">
                          {(() => {
                            // Calculate totals
                            const subtotal = items.reduce((sum, item) => {
                              const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                              return sum + rawAmount;
                            }, 0);

                            const totalDiscount = items.reduce((sum, item) => {
                              if (!showDiscount) return sum;
                              const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                              const discountAmount = (rawAmount * (item.discount || 0)) / 100;
                              return sum + discountAmount;
                            }, 0);

                            const taxableAmount = subtotal - totalDiscount;

                            // Group GST by rate
                            const gstByRate = {};
                            items.forEach(item => {
                              const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                              const discountAmount = showDiscount ? ((rawAmount * (item.discount || 0)) / 100) : 0;
                              const itemTaxableAmount = rawAmount - discountAmount;
                              const gstRate = item.gst_rate || 0;
                              const gstAmount = (itemTaxableAmount * gstRate) / 100;

                              if (gstRate > 0) {
                                if (!gstByRate[gstRate]) {
                                  gstByRate[gstRate] = 0;
                                }
                                gstByRate[gstRate] += gstAmount;
                              }
                            });

                            const totalGst = Object.values(gstByRate).reduce((a, b) => a + b, 0);

                            return (
                              <GSTSummary
                                items={items}
                                buyerGST={formData.customer.gstNumber}
                                companySettings={companySettings}
                                subtotal={calculateInvoiceTotal(items, formData.customer.gstNumber, false)}
                                className="space-y-2"
                              />
                            );
                          })()}
                        </div>

                        {/* Right Column - Calculation Details */}
                        <div className="bg-white rounded-lg p-4">
                          <h5 className="font-semibold text-slate-900 mb-3">CALCULATION DETAILS</h5>
                          <div className="space-y-2 text-sm">
                            {items.map((item, index) => {
                              const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                              const discountAmount = (rawAmount * (item.discount || 0)) / 100;
                              const taxableAmount = rawAmount - discountAmount;
                              const gstAmount = (taxableAmount * (item.gst_rate || 0)) / 100;
                              const itemTotal = taxableAmount + gstAmount;

                              return (
                                <div key={index} className="text-slate-600">
                                  <div className="font-medium">Item {index + 1}: {item.product_name || 'Unnamed'}</div>
                                  <div className="text-xs pl-2">
                                    Qty: {item.quantity || 0} × ₹{(item.unit_price || 0).toFixed(1)} = ₹{rawAmount.toFixed(1)}
                                  </div>
                                  {(item.discount || 0) > 0 && (
                                    <div className="text-xs pl-2 text-red-600">
                                      Discount: -{item.discount}% = -₹{discountAmount.toFixed(1)}
                                    </div>
                                  )}
                                  <div className="text-xs pl-2">
                                    Taxable: ₹{taxableAmount.toFixed(1)}
                                  </div>
                                  {(item.gst_rate || 0) > 0 && (
                                    <div className="text-xs pl-2 text-green-600">
                                      GST: +{item.gst_rate}% = +₹{gstAmount.toFixed(1)}
                                    </div>
                                  )}
                                  <div className="text-xs pl-2 font-semibold">
                                    Total: ₹{itemTotal.toFixed(1)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Section - Enhanced for Invoices */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                    Payment Information
                  </h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Payment Type
                      </label>
                      <select
                        value={paymentData.payment_type}
                        onChange={(e) => setPaymentData({ ...paymentData, payment_type: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="upi">UPI</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                    <ValidatedInput
                      label="Amount Received"
                      type="number"
                      value={paymentData.amount_received}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const amount = parseFloat(inputValue) || 0;

                        // Calculate grand total using the same logic as Remaining Balance
                        const totalAmount = items.reduce((total, item) => {
                          const quantity = parseFloat(item.quantity) || 0;
                          const price = parseFloat(item.unit_price) || 0;
                          const discount = showDiscount ? (parseFloat(item.discount) || 0) : 0;
                          const gstRate = parseFloat(item.gst_rate) || 0;

                          const rawAmount = quantity * price;
                          const discountAmount = (rawAmount * discount) / 100;
                          const taxableAmount = rawAmount - discountAmount;
                          const gstAmount = (taxableAmount * gstRate) / 100;

                          return total + (taxableAmount + gstAmount);
                        }, 0);

                        // Auto-update payment status
                        let status = 'pending';
                        if (amount === 0) status = 'pending';
                        else if (amount >= totalAmount) status = 'completed';
                        else status = 'partially_paid';

                        // Update state with raw input value (not converted to number)
                        setPaymentData({
                          ...paymentData,
                          amount_received: inputValue, // Keep as string to preserve empty state
                          payment_status: status
                        });
                      }}
                      validationType="currency"
                      required={false}
                      min="0"
                      step="any"
                      placeholder="Enter amount received"
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      className="input-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      showErrors={formTouched}
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Payment Status
                      </label>
                      <div className="px-3 py-2 bg-white border border-slate-300 rounded-lg">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${paymentData.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                          paymentData.payment_status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {paymentData.payment_status === 'completed' ? 'Completed' :
                            paymentData.payment_status === 'partially_paid' ? 'Partially Paid' :
                              'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Remaining Balance */}
                  <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-600">Remaining Balance:</span>
                      <span className="text-lg font-bold text-slate-900">
                        ₹{Math.max(0, items.reduce((total, item) => {
                          const quantity = parseFloat(item.quantity) || 0;
                          const price = parseFloat(item.unit_price) || 0;
                          const discount = showDiscount ? (parseFloat(item.discount) || 0) : 0;
                          const gstRate = parseFloat(item.gst_rate) || 0;

                          const rawAmount = quantity * price;
                          const discountAmount = (rawAmount * discount) / 100;
                          const taxableAmount = rawAmount - discountAmount;
                          const gstAmount = (taxableAmount * gstRate) / 100;

                          return total + (taxableAmount + gstAmount);
                        }, 0) - (paymentData.amount_received || 0)).toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div>
                      <h4 className="text-sm font-bold text-[#0A5F38]">Include Terms & Conditions</h4>
                      <p className="text-xs text-slate-500">Enable if you wish to add terms and conditions to this invoice</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const nextState = !includeTermsDirect;
                        setIncludeTermsDirect(nextState);
                        if (!nextState) {
                          setFormData(prev => ({ ...prev, termsAndConditions: '' }));
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        includeTermsDirect ? 'bg-[#0A5F38]' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          includeTermsDirect ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {includeTermsDirect && (
                    <TermsAndConditionsInput
                      value={formData.termsAndConditions}
                      onChange={(value) => setFormData({ ...formData, termsAndConditions: value })}
                    />
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setActiveTab('history')}
                    className="px-6 py-3 border border-slate-300 text-slate-700 rounded-2xl hover:bg-white transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all font-semibold shadow-lg shadow-green-200/50 hover:shadow-xl hover:shadow-green-300/50 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2 inline" />
                    {loading
                      ? (isEditing ? 'Updating...' : 'Creating...')
                      : (isEditing ? 'Update Invoice' : 'Create Invoice')
                    }
                  </button>
                </div>
              </form>
            )}
          </div>
        )
        }

        {/* View Modal */}
        {
          showViewModal && selectedInvoice && (
            <AppModal
              open
              onClose={() => setShowViewModal(false)}
              title="Invoice Details"
              size="lg" compactFooter
              footer={(
                <>
                      {selectedInvoice?.cancellation_status === 'cancelled' || selectedInvoice?.status === 'cancelled' ? (
                        <>
                          <div className="px-6 py-2 bg-red-100 text-red-800 rounded-lg font-medium">
                            Invoice Cancelled
                          </div>
                          {isRefundCompleted(selectedInvoice) ? (
                            <div className="px-6 py-2 bg-green-100 text-green-800 rounded-lg font-medium border border-green-300 flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4" />
                              <span>Refund Completed</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setShowPaymentStatusUpdateModal(true);
                              }}
                              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
                            >
                              <DollarSign className="h-4 w-4" />
                              <span>Update Payment Status</span>
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Edit Products Button - Only visible to Admin */}
                          {user && user.role === 'admin' && selectedInvoice?.cancellation_status !== 'cancelled' && selectedInvoice?.status !== 'cancelled' && (
                            <button
                              onClick={async () => {
                                // Fetch data locally to avoid state staleness issues during mapping
                                let localCategories = categories;
                                let localProducts = products;

                                if (!localCategories.length || !localProducts.length) {
                                  try {
                                    const data = await getFormData();
                                    localCategories = data.categories || [];
                                    localProducts = data.products || [];
                                    setCategories(localCategories);
                                    setProducts(localProducts);
                                  } catch (err) {
                                    console.error("Failed to load products/categories", err);
                                  }
                                }

                                // Set up editing state
                                setEditingInvoice(selectedInvoice);
                                setEditingCustomer({
                                  name: selectedInvoice.customer_name || '',
                                  phone: selectedInvoice.customer_phone || '',
                                  email: selectedInvoice.customer_email || '',
                                  address: selectedInvoice.customer_address || '',
                                  gstNumber: selectedInvoice.customer_gst || ''
                                });

                                // Prepare products for editing
                                const items = selectedInvoice.items || [];
                                const itemsWithIds = items.map((item, idx) => {
                                  // Robust category ID extraction
                                  let catId = item.category_id ||
                                    item.categoryId ||
                                    item.category_name ||
                                    item.categoryName ||
                                    (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name : item.category) ||
                                    '';

                                  // Robust product ID extraction
                                  let prodId = item.product_id ||
                                    item.productId ||
                                    (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
                                    '';

                                  console.log(`📦 Invoice Item [${idx}] raw:`, { catId, prodId, itemCategory: item.category, productName: item.product_name });

                                  // CRITICAL: If catId is still missing but we have a product_id, look up the product's category
                                  if (!catId && prodId) {
                                    const foundProd = localProducts.find(p => String(p.id || p._id) === String(prodId));
                                    if (foundProd) {
                                      catId = foundProd.category_id || foundProd.categoryId;
                                      console.log(`✅ [${idx}] Resolved category from product ID:`, catId);
                                    }
                                  }

                                  // If catId is a name or missing, try to find the actual ID
                                  if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
                                    const foundCat = localCategories.find(c =>
                                      (c.name || '').toLowerCase() === String(catId || '').toLowerCase() ||
                                      (c.categoryName || '').toLowerCase() === String(catId || '').toLowerCase() ||
                                      (c.id || c._id) === String(catId)
                                    );
                                    if (foundCat) {
                                      catId = foundCat.id || foundCat._id;
                                      console.log(`✅ [${idx}] Resolved category name/ID "${item.category || item.category_id}" to ID: ${catId}`);
                                    } else if (item.product_name && !prodId) {
                                      // Infer Others if name exists but no ID
                                      const othersCat = localCategories.find(c =>
                                        c.isOthers ||
                                        (c.name || '').toLowerCase() === 'others' ||
                                        (c.name || '').toLowerCase() === 'other'
                                      );
                                      if (othersCat) {
                                        catId = othersCat.id || othersCat._id;
                                        console.log(`ℹ️ [${idx}] Inferred "Others" category for product "${item.product_name}"`);
                                      }
                                    }
                                  }

                                  const selectedCategory = localCategories.find(c => String(c.id || c._id) === String(catId));
                                  const isOthers = !!(
                                    selectedCategory?.isOthers ||
                                    (selectedCategory?.name || '').toLowerCase() === 'others' ||
                                    (selectedCategory?.name || '').toLowerCase() === 'other' ||
                                    item.isOthers ||
                                    (item.product_name && !prodId) // FORCE isOthers if custom name and no product ID
                                  );

                                  return {
                                    ...item,
                                    category_id: catId,
                                    product_id: prodId,
                                    isOthers: isOthers,
                                    showFreeText: isOthers && !prodId, // Show free text if it's "Other" category and no product_id
                                    unit_type: item.unit_type || 'SFT',
                                    hsn_code: item.hsn_code || (item.productId?.product?.hsnCode) || ''
                                  };
                                });
                                console.log('✨ Mapped items for edit modal:', itemsWithIds);
                                setEditingProducts(recalcItemsAmounts(itemsWithIds));
                                // Detect Wood Work mode
                                setIsWoodWorkMode(!!(selectedInvoice?.isWoodWork || selectedInvoice?.is_woodwork || selectedInvoice?.is_wood_work));
                                setEditModalContext('edit');
                                setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));

                                // Close view modal and open edit modal
                                setShowViewModal(false);
                                setShowEditInvoiceModal(true);
                              }}
                              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
                            >
                              <FileText className="h-4 w-4" />
                              <span>Edit</span>
                            </button>
                          )}
                          {!isPaymentCompleted(selectedInvoice) ? (
                            <button
                              onClick={() => {
                                setShowViewModal(false);
                                handleUpdatePayment(selectedInvoice);
                              }}
                              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                            >
                              Update Payment
                            </button>
                          ) : (
                            <div className="px-6 py-2 bg-green-100 text-green-800 rounded-lg font-medium">
                              Payment Completed
                            </div>
                          )}
                          <button
                            onClick={handleCancelInvoice}
                            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Cancel Invoice
                          </button>
                        </>
                      )}
                      <button
                        onClick={async () => {
                          setShowViewModal(false);
                          // Ensure all fields are present for PDF
                          const pdfData = {
                            ...selectedInvoice,
                            dispatched_from: selectedInvoice.dispatched_from || '',
                            delivered_to: selectedInvoice.delivered_to || selectedInvoice.deliveredTo || ''
                          };
                          await generatePDF(pdfData, 'invoice', companySettings);
                        }}
                        className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Download PDF
                      </button>
                      <button
                        onClick={() => {
                          if (!selectedInvoice?.shareToken) {
                            toast.error('Share token not available yet. Please reopen this modal.');
                            return;
                          }
                          const guestLink = `${window.location.origin}/view/document/${selectedInvoice.shareToken}`;
                          navigator.clipboard.writeText(guestLink)
                            .then(() => toast.success('Guest link copied to clipboard!'))
                            .catch(() => toast.error('Failed to copy link'));
                        }}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                      >
                        <Link2 className="h-4 w-4" />
                        <span>Copy Guest Link</span>
                      </button>
                      <button
                        onClick={() => setShowViewModal(false)}
                        className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors"
                      >
                        Close
                      </button>
                </>
              )}
            >
              <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>

                  {/* PDF-Style Layout */}
                  <div className="border-2 border-black bg-white rounded-xl min-w-[760px]">
                    {/* Header Section */}
                    <div className="border-b-2 border-black p-6 bg-white">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-[#C5A57D] rounded-lg flex items-center justify-center overflow-hidden">
                            <img
                              src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                              alt="Company Logo"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <span className="text-white font-bold text-xl" style={{ display: 'none' }} id="logo-fallback">IT</span>
                          </div>
                          <div>
                            <h1 className="text-2xl font-bold text-slate-900">{companySettings?.companyName || 'IDEAL TRENDZ INTERIORS'}</h1>
                            <div className="text-sm text-slate-600 mt-1">
                              <div>{companySettings?.addressLine1 || '# Vertex Privilege, Villa No. 32, Nizampet Road'}</div>
                              {companySettings?.addressLine2 && <div>{companySettings.addressLine2}</div>}
                              <div>{companySettings?.city || 'HYDERNAGAR'}, {companySettings?.state || 'TELANGANA'} - {companySettings?.pincode || '500072'}</div>
                              <div>Email: {companySettings?.emailId || 'idealtrendz.int@gmail.com'}</div>
                              <div>Contact No. {companySettings?.contactNumber || '9014814901'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <div><strong>GST:</strong> {companySettings?.gstNumber || 'N/A'}</div>
                          <div><strong>PAN:</strong> {companySettings?.panNumber || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Document Title */}
                    <div className="border-b-2 border-black p-4 text-center bg-white">
                      <h2 className="text-xl font-bold text-[#0A5F38]">TAX INVOICE</h2>
                    </div>

                    {/* Customer and Document Info */}
                    <div className="border-b-2 border-black p-6 bg-white">
                      <div className="grid grid-cols-2 gap-8">
                        {/* Left Side - Customer Info */}
                        <div>
                          <div className="mb-4">
                            <div className="font-semibold text-slate-900 mb-2">To</div>
                            <div className="font-bold text-lg">{selectedInvoice.customer_name || 'WALK-IN CUSTOMER'}</div>
                            <div className="text-slate-600">{selectedInvoice.customer_address || selectedInvoice.customer_city || ''}</div>
                            {selectedInvoice.customer_phone && (
                              <div className="text-slate-600 mt-1">Phone: {selectedInvoice.customer_phone}</div>
                            )}
                            {selectedInvoice.customer_email && (
                              <div className="text-slate-600 mt-1">Email: {selectedInvoice.customer_email}</div>
                            )}
                          </div>
                        </div>

                        {/* Right Side - Document Info with Fixed Alignment - Shifted Right */}
                        <div className="ml-auto pr-0">
                          <div className="space-y-2">
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">DATE:</span>
                              <span className="flex-1 break-words">{selectedInvoice.date ? new Date(selectedInvoice.date).toLocaleDateString('en-GB') : selectedInvoice.createdAt ? new Date(selectedInvoice.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">INVOICE No.:</span>
                              <span className="flex-1 break-words">{(selectedInvoice.invoice_number || selectedInvoice._id?.slice(-8) || 'INV001').replace(/-CANCEL-\d+/g, ' (Cancelled)')}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">PARTY PAN No.:</span>
                              <span className="flex-1 break-words">{selectedInvoice.party_pan_no || ''}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">PARTY GST No.:</span>
                              <span className="flex-1 break-words">{selectedInvoice.customer_gst || selectedInvoice.party_gst_no || ''}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">PARTY STATE NAME:</span>
                              <span className="flex-1 break-words">{selectedInvoice.customer_state || selectedInvoice.party_state_name || ''}</span>
                            </div>
                            {selectedInvoice.dispatched_from && (
                              <div className="flex gap-4">
                                <span className="font-semibold w-40">DISPATCHED FROM:</span>
                                <span className="flex-1 break-words">{selectedInvoice.dispatched_from}</span>
                              </div>
                            )}
                            {selectedInvoice.delivered_to && (
                              <div className="flex gap-4">
                                <span className="font-semibold w-40">DELIVERED TO:</span>
                                <span className="flex-1 break-words">{selectedInvoice.delivered_to}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cancellation Details - Show if invoice is cancelled */}
                    {(selectedInvoice.cancellation_status === 'cancelled' || selectedInvoice.status === 'cancelled') && (
                      <div className="border-b border-slate-300 p-6 bg-red-50">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 mb-4">
                            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                            <h3 className="font-semibold text-red-900">Cancellation Details</h3>
                          </div>
                          {selectedInvoice.cancellation_reason && (
                            <div>
                              <span className="font-semibold text-slate-700">Reason:</span>
                              <span className="text-slate-600 ml-2">{selectedInvoice.cancellation_reason}</span>
                            </div>
                          )}
                          {selectedInvoice.cancellation_payment_status && (
                            <div>
                              <span className="font-semibold text-slate-700">Payment Status:</span>
                              <span className="text-slate-600 ml-2">
                                {selectedInvoice.cancellation_payment_status === 'refund_initiated' && 'Refund Initiated – Refund process started'}
                                {selectedInvoice.cancellation_payment_status === 'refund_completed' && 'Refund Completed – Refund processed successfully'}
                                {selectedInvoice.cancellation_payment_status === 'no_payment' && 'Cancelled (No Payment) – No refund needed'}
                              </span>
                            </div>
                          )}
                          {selectedInvoice.cancelled_at && (
                            <div>
                              <span className="font-semibold text-slate-700">Cancelled At:</span>
                              <span className="text-slate-600 ml-2">{new Date(selectedInvoice.cancelled_at).toLocaleDateString('en-IN')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Product Table */}
                    {(() => {
                      // Check if any item has a discount
                      const hasDiscounts = selectedInvoice.items?.some(item => (item.discount || 0) > 0) || false;

                      return (
                        <div className="border-b-2 border-black overflow-x-auto">
                          <table className="w-full">
                            {isWoodWorkMode ? (
                              /* ══ WOOD WORK VIEW MODAL TABLE ══ */
                              <>
                                 <thead>
                                   <tr className="bg-[#0A5F38] border-b border-black">
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '50px' }}>SI</th>
                                     <th className="border-r border-black p-3 text-left text-xs font-bold text-white uppercase">Type</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '100px' }}>Rate (₹)</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '65px' }}>Nos</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>L</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>B</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '90px' }}>Qty</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>GST %</th>
                                     <th className="p-3 text-right text-xs font-bold text-white uppercase" style={{ width: '120px' }}>Amount (₹)</th>
                                   </tr>
                                 </thead>
                                <tbody>
                                  {(() => {
                                    const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                                    const rows = [];
                                    const items = selectedInvoice.items || [];

                                    // Build ordered list of unique categories
                                    const categoryOrder = [];
                                    items.forEach(item => {
                                      const cat = item.categoryLabel || item.category_label || item.template_category || '';
                                      if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                                    });

                                    categoryOrder.forEach((catLabel) => {
                                      const catIdx = categoryOrder.indexOf(catLabel);
                                      const catEntries = items.filter(item => (item.categoryLabel || item.category_label || item.template_category || '') === catLabel);

                                      // Category header row
                                      rows.push(
                                        <tr key={'cat-view-' + catLabel} style={{ backgroundColor: '#BDD7EE' }}>
                                          <td className="border-r border-black px-3 py-2 text-center font-bold text-slate-900 border-b border-black">
                                            {CAT_LETTERS[catIdx] || (catIdx + 1)}
                                          </td>
                                          <td colSpan="7" className="border-r border-black px-3 py-2 font-bold text-slate-900 uppercase tracking-wider border-b border-black">
                                            {catLabel || 'Section'}
                                          </td>
                                          <td className="px-3 py-2 border-b border-black"></td>
                                        </tr>
                                      );

                                      // Item rows
                                      catEntries.forEach((item, siNo) => {
                                        const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                        const rate = item.unit_price || 0;
                                        const amount = item.amount || (qty * rate);

                                        rows.push(
                                         <tr key={'item-view-' + (item._id || siNo)} className="border-b border-black hover:bg-slate-50 transition-colors">
                                           <td className="border-r border-black px-2 py-2 text-center text-slate-600">{siNo + 1}</td>
                                           <td className="border-r border-black px-3 py-2 font-medium break-words max-w-[300px]">
                                             {item.product_name || 'Unnamed Item'}
                                           </td>
                                           <td className="border-r border-black px-2 py-2 text-center text-slate-600">
                                             {rate.toFixed(1)}
                                           </td>
                                           <td className="border-r border-black px-2 py-2 text-center text-slate-600">{item.nos || 0}</td>
                                           <td className="border-r border-black px-2 py-2 text-center text-slate-600">{item.length || 0}</td>
                                           <td className="border-r border-black px-2 py-2 text-center text-slate-600">{item.breadth || 0}</td>
                                           <td className="border-r border-black px-2 py-2 text-center text-slate-900 font-semibold">{qty}</td>
                                           <td className="border-r border-black px-2 py-2 text-center font-bold bg-slate-50">{item.gst_rate || 0}%</td>
                                           <td className="px-3 py-2 text-right text-slate-900 font-bold italic">
                                             ₹{amount.toFixed(1)}
                                           </td>
                                         </tr>
                                        );
                                      });
                                    });

                                    return rows;
                                  })()}
                                </tbody>
                              </>
                            ) : (
                              /* ══ STANDARD RETAIL VIEW MODAL TABLE ══ */
                              <>
                                 <thead>
                                   <tr className="bg-[#0A5F38] border-b border-black">
                                     <th className="border-r border-black p-3 text-left text-xs font-semibold text-white uppercase">S.No</th>
                                     <th className="border-r border-black p-3 text-left text-xs font-semibold text-white uppercase">Product Description</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">HSN Code</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Unit</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">QTY</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Rate</th>
                                     {hasDiscounts && (
                                       <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Discount %</th>
                                     )}
                                     <th className="border-r border-black p-3 text-right text-xs font-semibold text-white uppercase">Amount</th>
                                     <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">GST %</th>
                                     <th className="border-r border-black p-3 text-right text-xs font-semibold text-white uppercase">GST Amount</th>
                                     <th className="p-3 text-right text-xs font-semibold text-white uppercase">Total Amount</th>
                                   </tr>
                                 </thead>
                                <tbody>
                                  {selectedInvoice.items && selectedInvoice.items.length > 0 ? (
                                    selectedInvoice.items.map((item, index) => {
                                      // Robust calculation logic for row display - ensure non-zero values if quantity and rate exist
                                      const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
                                      const discountAmount = baseAmount * ((item.discount || 0) / 100);
                                      const taxableAmount = item.amount || (baseAmount - discountAmount);
                                      const gstRate = item.gst_rate || 0;
                                      const gstAmount = item.gst_amount || (taxableAmount * gstRate) / 100;
                                      const totalAmount = item.total_amount || (taxableAmount + gstAmount);

                                      return (
                                         <tr key={index} className="border-b border-black hover:bg-slate-50 transition-colors">
                                           <td className="border-r border-black p-3 text-center text-xs font-bold text-slate-400">{index + 1}</td>
                                           <td className="border-r border-black p-3 text-xs font-semibold">{item.product_name || `Product #${index + 1}`}</td>
                                           <td className="border-r border-black p-3 text-center text-xs">{item.hsn_code || '-'}</td>
                                           <td className="border-r border-black p-3 text-center text-xs">{item.unit_type || 'PCS'}</td>
                                           <td className="border-r border-black p-3 text-center text-xs font-bold">{item.quantity || 0}</td>
                                           <td className="border-r border-black p-3 text-center text-xs font-semibold">{(item.unit_price || 0).toFixed(1)}</td>
                                           {hasDiscounts && (
                                             <td className="border-r border-black p-3 text-center text-xs font-bold text-red-600">{item.discount || 0}%</td>
                                           )}
                                           <td className="border-r border-black p-3 text-right text-xs">₹{taxableAmount.toFixed(1)}</td>
                                           <td className="border-r border-black p-3 text-center text-xs font-bold bg-slate-50">{gstRate}%</td>
                                           <td className="border-r border-black p-3 text-right text-xs text-slate-500 font-medium">₹{gstAmount.toFixed(1)}</td>
                                           <td className="p-3 text-right text-xs font-black italic">₹{totalAmount.toFixed(1)}</td>
                                         </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td colSpan="11" className="p-6 text-center text-slate-500">No items found</td>
                                    </tr>
                                  )}
                                </tbody>
                              </>
                            )}
                          </table>
                        </div>
                      );
                    })()}

                    {/* Summary Section */}
                    <div className="p-6">
                      <div className="flex justify-between">
                        {/* Left Side - Bank Details */}
                        <div className="w-1/2">
                          <h3 className="font-semibold text-slate-900 mb-3">OUR BANK DETAILS</h3>
                          <div className="text-sm text-slate-600 space-y-1">
                            <div><strong>Bank Name:</strong> {companySettings?.bankName || 'Not Configured'}</div>
                            <div><strong>A/C No:</strong> {companySettings?.accountNumber || 'Not Configured'}</div>
                            <div><strong>IFSC:</strong> {companySettings?.ifscCode || 'Not Configured'}</div>
                            <div><strong>Branch:</strong> {companySettings?.branchName || 'Not Configured'}</div>
                          </div>

                        </div>

                        {/* Right Side - Totals */}
                        <div className="w-1/3">
                          <div className="space-y-3">
                            <GSTSummary
                              items={selectedInvoice.items || []}
                              buyerGST={selectedInvoice.customer_gst || ''}
                              companySettings={companySettings}
                              subtotal={calculateInvoiceTotal(selectedInvoice.items || [], selectedInvoice.customer_gst || '', false)}
                              className="space-y-2"
                            />

                            {/* Outstanding Amount */}
                            <div className="flex justify-between py-2 font-bold text-lg">
                              <span>Total Outstanding:</span>
                              <span>₹{(calculateInvoiceTotal(selectedInvoice.items || [], selectedInvoice.customer_gst || '') - (selectedInvoice.amount_received || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Terms and Conditions & Material Description */}
                    {(selectedInvoice.termsAndConditions || selectedInvoice.materialDescription) && (
                      <div className="border-t border-slate-300 p-6 space-y-4">
                        {selectedInvoice.termsAndConditions && (
                          <div className="mb-2">
                            <div className="font-semibold text-slate-900">Terms and Conditions:</div>
                            <div className="text-slate-700 mt-1 whitespace-pre-wrap text-sm break-words">{selectedInvoice.termsAndConditions}</div>
                          </div>
                        )}
                        {selectedInvoice.materialDescription && (
                          <div className="mb-2">
                            <div className="font-semibold text-slate-900">Material Description:</div>
                            <div className="text-slate-700 mt-1 whitespace-pre-wrap text-sm break-words">{selectedInvoice.materialDescription}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
              </div>
            </AppModal>
          )
        }

        {/* Enhanced Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          record={selectedInvoice}
          onPaymentUpdate={handlePaymentUpdate}
          paymentHistory={selectedInvoice?.payments || []}
          companySettings={companySettings}
        />

        {/* Convert Estimate to Invoice Modal */}
        {
          showConvertModal && (
            <AppModal
              open
              onClose={() => setShowConvertModal(false)}
              title="Convert Estimate to Invoice"
              size="md"
              footer={(
                <button
                  onClick={() => setShowConvertModal(false)}
                  className={modalButton.secondary}
                >
                  Cancel
                </button>
              )}
            >
                {/* Search Estimates */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Search estimates by number or customer name..."
                      value={estimateSearchTerm}
                      onChange={handleEstimateSearch}
                      className="pl-10 pr-4 py-3 w-full border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Estimates List */}
                <div className="mb-6 max-h-96 overflow-y-auto">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Select an Estimate</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={fetchEstimates}
                        className="text-xs bg-blue-50 text-[#0A5F38] px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                        title="Refresh estimate list"
                      >
                        🔄 Refresh
                      </button>
                      <div className="text-xs text-slate-500 bg-white px-2 py-1 rounded">
                        Only showing unconverted estimates
                      </div>
                    </div>
                  </div>
                  {filteredEstimates.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg">
                      <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-600 font-medium">No unconverted estimates available</p>
                      <p className="text-slate-500 text-sm mt-1">All estimates have already been converted to closed orders or invoices</p>
                      <button
                        onClick={fetchEstimates}
                        className="mt-3 text-sm bg-blue-50 text-[#0A5F38] px-3 py-1 rounded hover:bg-blue-100 transition-colors"
                      >
                        🔄 Refresh List
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredEstimates.map(estimate => (
                        <div
                          key={estimate.id || estimate._id}
                          className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer transition-colors"
                          onClick={() => handleSelectEstimate(estimate)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className="font-semibold text-slate-900">
                                  {estimate.estimate_number || `EST-${estimate._id?.slice(-6)}`}
                                </h4>
                                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Available for Conversion
                                </span>
                              </div>
                              <p className="text-slate-600 mb-1">
                                Customer: {estimate.customer?.name || estimate.customer_name || 'N/A'}
                              </p>
                              <p className="text-slate-500 text-sm">
                                Date: {estimate.date ? new Date(estimate.date).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-900">
                                ₹{(estimate.total_amount || estimate.totalAmount || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </AppModal>
          )
        }

        {/* Estimate Detail Modal for Invoice Conversion */}
        {
          showEstimateDetailModal && selectedEstimate && (
            <AppModal
              open
              onClose={() => setShowEstimateDetailModal(false)}
              title="Convert to Invoice"
              size="xl"
              footer={(
                <>
                  <button
                    onClick={() => setShowEstimateDetailModal(false)}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInvoiceSubmit}
                    disabled={loading}
                    className={modalButton.primary}
                  >
                    {loading ? 'Converting...' : 'Convert to Invoice'}
                  </button>
                </>
              )}
            >
                {/* Estimate Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Estimate Summary</h3>
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Estimate Number</div>
                      <div className="font-semibold">
                        #{selectedEstimate.estimate_number || selectedEstimate._id?.slice(-8) || 'EST' + selectedEstimate.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Estimate Date</div>
                      <div className="font-semibold">
                        {selectedEstimate.date ? new Date(selectedEstimate.date).toLocaleDateString('en-IN') : selectedEstimate.createdAt ? new Date(selectedEstimate.createdAt).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Total Amount</div>
                      <div className="font-semibold">
                        ₹{(selectedEstimate.totalAmount || selectedEstimate.total_amount || 0).toLocaleString()}
                      </div>
                    </div>
                    {selectedEstimate.converted_by_name && (
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Converted By</div>
                        <div className="font-semibold">
                          {selectedEstimate.converted_by_name}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
                      <input
                        type="date"
                        value={invoiceData.date || ''}
                        onChange={(e) => setInvoiceData({ ...invoiceData, date: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm font-semibold"
                      />
                    </div>
                  </div>
                </div>

                {/* Invoice Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Invoice Information</h3>
                  <div className="bg-white rounded-lg p-4 border-2 border-black">
                    <div className="max-w-md">
                      <InvoiceNumberField
                        value={invoiceNumber}
                        onChange={setInvoiceNumber}
                        label="Invoice Number"
                        placeholder="Generating..."
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                    <button
                      onClick={() => {
                        if (!editMode) {
                          setEditingCustomer({
                            name: selectedEstimate.customer_name || '',
                            phone: selectedEstimate.customer_phone || '',
                            email: selectedEstimate.customer_email || '',
                            address: selectedEstimate.customer_address || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editMode ? (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={editingCustomer.name || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                        <input
                          type="text"
                          value={editingCustomer.phone || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={editingCustomer.email || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                        <textarea
                          value={editingCustomer.address || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <select
                          value={editingCustomer.state || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, state: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                        <input
                          type="text"
                          value={invoiceData.gstNumber || ''}
                          onChange={(e) => handleGSTFieldChange(e.target.value,
                            (gstValue) => setInvoiceData({ ...invoiceData, gstNumber: gstValue }),
                            (stateName) => setEditingCustomer(prev => ({ ...prev, state: stateName }))
                          )}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          placeholder="Enter GST Number"
                          maxLength="15"
                        />
                        {invoiceData.gstNumber && invoiceData.gstNumber.length > 0 && invoiceData.gstNumber.length < 15 && (
                          <div className="text-orange-500 text-sm mt-1">
                            GST number should be 15 characters long if provided
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                        <div className="font-semibold">{editingCustomer.name || selectedEstimate.customer_name || 'Walk-in Customer'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                        <div className="font-semibold">{editingCustomer.phone || selectedEstimate.customer_phone || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                        <div className="font-semibold">{editingCustomer.email || selectedEstimate.customer_email || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                        <div className="font-semibold">{editingCustomer.address || selectedEstimate.customer_address || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                        <div className="font-semibold">{editingCustomer.state || selectedEstimate.customer?.state || selectedEstimate.party_state_name || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">GST Number</div>
                        <div className="font-semibold">{invoiceData.gstNumber || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Delivery Information</h3>
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Dispatched From</label>
                      <input
                        type="text"
                        value={invoiceData.dispatchedFrom || ''}
                        onChange={(e) => setInvoiceData({ ...invoiceData, dispatchedFrom: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Enter dispatch location"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Delivered To</label>
                      <input
                        type="text"
                        value={invoiceData.deliveredTo || ''}
                        onChange={(e) => setInvoiceData({ ...invoiceData, deliveredTo: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Enter delivery location"
                      />
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                    <div className="space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            console.log('🔄 Loading categories and products for Edit...');
                            const formData = await getFormData();

                            if (!formData.categories || formData.categories.length === 0) {
                              toast.error('No categories found. Please add categories first.');
                              return;
                            }
                            setCategories(formData.categories || []);
                            setProducts(formData.products || []);

                            const itemsWithIds = (selectedEstimate.items || []).map(item => ({
                              ...item,
                              category_id: item.category_id || '',
                              product_id: item.product_id || '',
                              hsn_code: item.hsn_code || item.productId?.product?.hsnCode || ''
                            }));

                            setEditingProducts(recalcItemsAmounts(itemsWithIds));
                            setEditModalContext('convert');
                            setShowEditProductsModal(true);
                          } catch (error) {
                            console.error('❌ Failed to load categories and products:', error);
                            toast.error('Failed to load product data');
                          }
                        }}
                        className="px-3 py-1 text-sm bg-blue-50 text-[#0A5F38] rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const formData = await getFormData();
                            setCategories(formData.categories || []);
                            setProducts(formData.products || []);

                            const itemsWithIds = (selectedEstimate.items || []).map(item => ({
                              ...item,
                              category_id: item.category_id || '',
                              product_id: item.product_id || ''
                            }));

                            itemsWithIds.push({
                              category_id: '',
                              product_id: '',
                              product_name: '',
                              quantity: 1,
                              unit_price: 0,
                              gst_rate: 18,
                              discount: 0,
                              unit_type: 'SFT',
                              hsn_code: ''
                            });

                            setEditingProducts(recalcItemsAmounts(itemsWithIds));
                            const hasDiscount = itemsWithIds.some(item => (item.discount || 0) > 0);
                            setEditingApplyDiscount(hasDiscount);
                            setEditModalContext('convert');
                            setShowEditProductsModal(true);
                          } catch (error) {
                            console.error('❌ Failed to load categories and products:', error);
                            toast.error('Failed to load product data');
                          }
                        }}
                        className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Add Product
                      </button>
                    </div>
                  </div>
                    {isWoodWorkMode ? (
                      /* ══ WOOD WORK MODE TABLE ══ */
                      <div className="space-y-6">
                        {(() => {
                          const categories = [...new Set(selectedEstimate.items.map(item => item.categoryLabel || 'General'))];
                          return categories.map((catLabel, catIdx) => {
                            const catItems = selectedEstimate.items.filter(item => (item.categoryLabel || 'General') === catLabel);
                            return (
                              <div key={catIdx} className="bg-white border border-[#E6DFC6] rounded-xl overflow-hidden">
                                <div className="bg-[#F8F5E6] px-4 py-2 border-b border-[#E6DFC6] flex justify-between items-center">
                                  <h4 className="font-bold text-[#0A5F38] uppercase text-sm tracking-wider">{catLabel}</h4>
                                  <span className="text-xs font-medium text-slate-500">{catItems.length} Items</span>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className={modalTable.table}>
                                          <thead className={modalTable.thead}>
                                            <tr>
                                              <th className={modalTable.th}>Product</th>
                                              <th className={modalTable.thCenter}>Nos</th>
                                              <th className={modalTable.thCenter}>L</th>
                                              <th className={modalTable.thCenter}>B</th>
                                              <th className={modalTable.thCenter}>Qty</th>
                                              <th className={modalTable.thRight}>Rate</th>
                                              <th className={modalTable.thCenter}>GST %</th>
                                              <th className={modalTable.thRight}>Total</th>
                                            </tr>
                                          </thead>
                                    <tbody className={`${modalTable.tbody} italic`}>
                                    {catItems.map((item, idx) => {
                                      const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                      const baseAmount = roundForCalculation(qty * (item.unit_price || 0));
                                      const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                                      const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                                      const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                                      const totalAmount = roundForCalculation(taxableAmount + gstAmount);

                                      return (
                                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                          <td className="px-4 py-2 text-sm font-bold text-slate-900">{item.product_name}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.nos || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.length || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.breadth || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm font-black text-[#0A5F38]">{qty}</td>
                                          <td className="px-4 py-2 text-right text-sm text-slate-900 font-bold">₹{(item.unit_price || 0).toLocaleString()}</td>
                                          <td className="px-4 py-2 text-center text-sm text-slate-600 font-bold">{item.gst_rate || 0}%</td>
                                          <td className="px-4 py-2 text-right text-sm font-black text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      /* ══ STANDARD MODE TABLE ══ */
                      <div className={modalTable.wrap}>
                      <table className={modalTable.table}>
                        <thead className={modalTable.thead}>
                          <tr>
                            <th className={modalTable.th}>Category</th>
                            <th className={modalTable.th}>Product</th>
                            <th className={modalTable.thCenter}>Units</th>
                            <th className={modalTable.thCenter}>Quantity</th>
                            <th className={modalTable.thRight}>Unit Price</th>
                            {/* Only show Discount column if any item has a discount */}
                            {(selectedEstimate.items && selectedEstimate.items.some(item => (item.discount || 0) > 0)) && (
                              <th className={modalTable.thCenter}>Disc %</th>
                            )}
                            <th className={modalTable.thRight}>Amount</th>
                            <th className={modalTable.thCenter}>GST %</th>
                            <th className={modalTable.thRight}>GST Amount</th>
                            <th className={modalTable.thRight}>Total</th>
                          </tr>
                        </thead>
                        <tbody className={modalTable.tbody}>
                          {selectedEstimate.items && selectedEstimate.items.length > 0 ? selectedEstimate.items.map((item, index) => {
                            // Extract category name from nested structure or fallback
                            let categoryName = 'N/A';

                            // Try multiple possible category sources
                            if (item.productId?.product?.categoryId?.name) {
                              categoryName = item.productId.product.categoryId.name;
                            } else if (item.category_name) {
                              categoryName = item.category_name;
                            } else if (categories.length > 0) {
                              const category = categories.find(cat =>
                                String(cat.id || cat._id) === String(item.category_id || item.productId?.product?.categoryId?._id)
                              );
                              categoryName = category?.name || 'N/A';
                            }

                            // Extract product name from nested structure or fallback
                            const productName = item.product_name ||
                              item.productId?.product?.name ||
                              item.name ||
                              'Unknown Product';

                            const baseAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || 0));
                            const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                            const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                            const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                            const totalAmount = roundForCalculation(taxableAmount + gstAmount);

                            return (
                              <tr key={index} className={modalTable.tr}>
                                <td className={modalTable.td}>{categoryName}</td>
                                <td className={`${modalTable.td} font-medium`}>{productName}</td>
                                <td className={modalTable.tdCenter}>{item.unit_type || 'SFT'}</td>
                                <td className={modalTable.tdCenter}>{item.quantity || 0}</td>
                                <td className={modalTable.tdRight}>₹{parseFloat(item.unit_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                {(selectedEstimate.items && selectedEstimate.items.some(i => (i.discount || 0) > 0)) && (
                                  <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                                )}
                                <td className={modalTable.tdRight}>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                <td className={`${modalTable.tdCenter} font-medium`}>{item.gst_rate || 0}%</td>
                                <td className={modalTable.tdRight}>₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                              </tr>
                            );
                          }) : (
                            <tr>
                              <td colSpan="10" className="px-4 py-8 text-center text-slate-500">
                                <div className="flex flex-col items-center">
                                  <FileText className="h-8 w-8 text-slate-300 mb-2" />
                                  <p className="font-medium">No products found</p>
                                  <p className="text-sm">This estimate doesn't have any products</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className={modalTable.tfoot}>
                          <tr>
                            <td colSpan={selectedEstimate.items?.some(i => (i.discount || 0) > 0) ? 9 : 8} className="px-3 py-2.5 text-right text-slate-700">Total:</td>
                            <td className="px-3 py-2.5 text-right font-bold text-[#0A5F38] whitespace-nowrap">
                              ₹{calculateInvoiceTotal(selectedEstimate.items).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                      </div>
                    )}
                </div>

                {/* GST Breakdown (Same State) */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">GST Breakdown</h3>
                  <div className="bg-white rounded-lg p-4">
                    {companySettings ? (
                      <GSTBreakdown
                        items={selectedEstimate.items || []}
                        buyerGST={invoiceData.gstNumber}
                        companySettings={companySettings}
                      />
                    ) : (
                      <div className="text-slate-500">Loading GST breakdown...</div>
                    )}
                  </div>
                </div>

                {/* Payment & Invoice Details */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[#0A5F38] mb-4 flex items-center">
                    <DollarSign className="h-6 w-6 mr-2" />
                    Payment & Invoice Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Payment Summary Card */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-md">
                      <h4 className="text-md font-bold text-[#0A5F38] mb-4 flex items-center">
                        <DollarSign className="h-5 w-5 mr-1" />
                        Payment Summary
                      </h4>
                      {(() => {
                        const totalAmount = calculateInvoiceTotal(selectedEstimate.items, invoiceData.gstNumber);
                        const alreadyPaid = 0; // Estimates usually don't have pre-payments
                        const additionalPayment = parseFloat(invoiceData.amountReceived) || 0;
                        const finalBalance = Math.max(0, totalAmount - (alreadyPaid + additionalPayment));

                        return (
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Total Invoice Amount:</span>
                              <span className="font-semibold text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between text-sm text-green-600">
                              <span>Previously Paid:</span>
                              <span className="font-semibold">- ₹{alreadyPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="pt-2 border-t border-slate-100 flex justify-between text-md font-bold">
                              <span className="text-slate-800">Remaining Balance:</span>
                              <span className="text-[#0A5F38]">₹{(totalAmount - alreadyPaid).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>

                            {additionalPayment > 0 && (
                              <div className="flex justify-between text-sm text-indigo-600 pt-1">
                                <span>Additional Payment:</span>
                                <span className="font-semibold">- ₹{additionalPayment.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              </div>
                            )}

                            <div className="pt-2 border-t-2 border-slate-200 flex justify-between text-lg font-black mt-2">
                              <span className="text-slate-900">Final Balance:</span>
                              <span className={finalBalance > 0 ? "text-red-600" : "text-green-600"}>
                                ₹{finalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* New Payment Entry Form */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-md">
                      <h4 className="text-md font-bold text-slate-900 mb-4">New Payment Entry</h4>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2 font-bold">Payment Mode</label>
                          <select
                            value={invoiceData.paymentType}
                            onChange={(e) => setInvoiceData({ ...invoiceData, paymentType: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none transition-all"
                          >
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="phonepe">PhonePe</option>
                            <option value="google_pay">Google Pay</option>
                            <option value="paytm">Paytm</option>
                            <option value="net_banking">Net Banking</option>
                            <option value="card">Card</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2 font-bold">
                            Amount
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-slate-500 font-bold">₹</span>
                            <input
                              type="number"
                              value={invoiceData.amountReceived}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || parseFloat(val) >= 0) {
                                  const amount = parseFloat(val) || 0;
                                  const totalAmount = calculateInvoiceTotal(selectedEstimate.items, invoiceData.gstNumber);

                                  // Validation: Cannot exceed total amount
                                  if (amount > totalAmount) {
                                    toast.error(`Amount cannot exceed the total payable amount (₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })})`);
                                    return;
                                  }

                                  // Auto-calculate payment status
                                  let status = 'pending';
                                  const tolerance = 0.01;

                                  if (amount === 0) {
                                    status = 'pending';
                                  } else if (Math.abs(amount - totalAmount) < tolerance) {
                                    status = 'paid';
                                  } else if (amount > 0 && amount < totalAmount) {
                                    status = 'partial';
                                  }

                                  setInvoiceData({
                                    ...invoiceData,
                                    amountReceived: val,
                                    paymentStatus: status
                                  });
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                }
                              }}
                              onWheel={(e) => e.target.blur()}
                              className="w-full pl-8 pr-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none transition-all font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions & Material Description */}
                <div className="mb-6 space-y-6">
                  <TermsAndConditionsInput
                    value={invoiceData.termsAndConditions}
                    onChange={(value) => setInvoiceData({ ...invoiceData, termsAndConditions: value })}
                    label="Terms and Conditions"
                  />
                  
                  {selectedEstimate?.isWoodWork && (
                    <TermsAndConditionsInput
                      value={invoiceData.materialDescription}
                      onChange={(value) => setInvoiceData({ ...invoiceData, materialDescription: value })}
                      label="Material Description"
                      placeholder="Enter material specifications..."
                    />
                  )}
                </div>
            </AppModal>
          )
        }

        {/* Edit Products Modal - Standardized WoodWork Implementation */}
        {
          showEditProductsModal && (
            <AppModal
              open
              onClose={handleDiscardProductEdits}
              title="Edit Products"
              size="full"
              layer="stacked"
              footer={(
                <>
                  {editingProducts.length > 0 && (
                    <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-6 gap-y-2">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-slate-400 uppercase italic">Total Items</span>
                        <span className="text-base font-black text-[#0A5F38]">{editingProducts.length}</span>
                      </div>
                      <div className="flex flex-col border-l border-slate-200 pl-6">
                        <span className="text-[10px] font-bold text-slate-400 uppercase italic">Taxable Amt</span>
                        <span className="text-base font-black text-[#0A5F38]">₹{editingProducts.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                      </div>
                      <div className="flex flex-col border-l border-slate-200 pl-6">
                        <span className="text-[10px] font-bold text-slate-400 uppercase italic">Grand Total</span>
                        <span className="text-xl font-black text-[#0A5F38]">
                          ₹{editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={handleDiscardProductEdits}
                    className={modalButton.secondary}
                  >
                    DISCARD
                  </button>
                  <button
                    onClick={handleSaveProductEdits}
                    className={modalButton.primary}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                    SAVE PRODUCT EDITS
                  </button>
                </>
              )}
            >

                {/* Apply Discount Toggle */}
                {!isWoodWorkMode && (
                  <div className="mb-4 flex items-center justify-end">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-700">Apply Discount</span>
                      <button
                        onClick={() => {
                          const newDiscountState = !editingApplyDiscount;
                          setEditingApplyDiscount(newDiscountState);
                          // If turning off, reset all discounts to 0
                          if (!newDiscountState) {
                            const updatedProducts = editingProducts.map(item => ({
                              ...item,
                                discount: 0
                            }));
                            setEditingProducts(updatedProducts);
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${editingApplyDiscount ? 'bg-[#C5A57D]' : 'bg-slate-200'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editingApplyDiscount ? 'translate-x-6' : 'translate-x-1'}`}
                        />
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  {editingProducts.length > 0 ? (
                    <div className="space-y-8">
                      {isWoodWorkMode ? (
                        /* ══ WOOD WORK PREMIUM CONTINUOUS TABLE EDIT LAYOUT ══ */
                        <div className={modalTable.wrap}>
                          <table className={modalTable.table}>
                            <thead className={modalTable.thead}>
                              <tr>
                                <th className={modalTable.thCenter} style={{ width: '50px' }}>SI</th>
                                <th className={modalTable.th}>Type</th>
                                <th className={modalTable.thCenter} style={{ width: '100px' }}>Rate (₹)</th>
                                <th className={modalTable.thCenter} style={{ width: '60px' }}>Nos</th>
                                <th className={modalTable.thCenter} style={{ width: '70px' }}>L</th>
                                <th className={modalTable.thCenter} style={{ width: '70px' }}>B</th>
                                <th className={modalTable.thCenter} style={{ width: '80px' }}>Qty</th>
                                <th className={modalTable.thCenter} style={{ width: '80px' }}>GST %</th>
                                <th className={modalTable.thRight} style={{ width: '120px' }}>Amount (₹)</th>
                                <th className={modalTable.thCenter} style={{ width: '50px' }}>Act</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                                const categoryOrder = [];
                                editingProducts.forEach(item => {
                                  const cat = item.categoryLabel || 'Section';
                                  if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                                });

                                const rows = [];
                                categoryOrder.forEach((catLabel, catIdx) => {
                                  const catEntries = editingProducts
                                    .map((item, idx) => ({ item, idx }))
                                    .filter(({ item }) => (item.categoryLabel || 'Section') === catLabel);

                                  // Category Header Row
                                  rows.push(
                                    <tr key={'edit-cat-header-' + catLabel} className="bg-[#BDD7EE] border-b border-black">
                                      <td className="p-3 text-center font-bold border-r border-black">{CAT_LETTERS[catIdx] || (catIdx + 1)}</td>
                                      <td colSpan="8" className="p-3 font-bold uppercase tracking-widest text-[#0A5F38] border-r border-black">
                                        <div className="flex justify-between items-center">
                                          <span>{catLabel}</span>
                                          <div className="flex gap-2">
                                             <button 
                                               onClick={() => {
                                                 const newLabel = prompt("Enter new section name:", catLabel);
                                                 if (newLabel) {
                                                   const updated = editingProducts.map(item => 
                                                     (item.categoryLabel || 'Section') === catLabel ? { ...item, categoryLabel: newLabel } : item
                                                   );
                                                   setEditingProducts(updated);
                                                 }
                                               }}
                                               className="text-[10px] bg-white/50 px-2 py-1 rounded hover:bg-white transition-colors"
                                             >
                                               Rename
                                             </button>
                                             <button 
                                               onClick={() => {
                                                 if (window.confirm(`Delete entire section "${catLabel}"?`)) {
                                                   const updated = editingProducts.filter(item => (item.categoryLabel || 'Section') !== catLabel);
                                                   setEditingProducts(updated);
                                                 }
                                               }}
                                               className="text-[10px] bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                                             >
                                               Delete Section
                                             </button>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-3 border-b border-black"></td>
                                    </tr>
                                  );

                                  // Item Rows
                                  catEntries.forEach(({ item, idx }, siNo) => {
                                    rows.push(
                                      <tr key={'edit-row-' + idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                                        <td className="p-2 text-center text-xs font-medium text-slate-400 border-r border-slate-100">{siNo + 1}</td>
                                        <td className="p-2 border-r border-slate-100">
                                          <input
                                            type="text"
                                            value={item.product_name}
                                            onChange={(e) => handleProductEdit(idx, 'product_name', e.target.value)}
                                            className="w-full bg-transparent border-0 focus:ring-0 text-sm font-medium py-1 px-0"
                                            placeholder="Enter item description..."
                                          />
                                        </td>
                                        <td className="p-2 border-r border-slate-100">
                                          <input
                                            type="number"
                                            value={item.unit_price}
                                            onChange={(e) => handleProductEdit(idx, 'unit_price', e.target.value)}
                                            className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm font-semibold focus:ring-1 focus:ring-[#C5A57D]"
                                            onWheel={(e) => e.target.blur()}
                                          />
                                        </td>
                                        <td className="p-2 border-r border-slate-100">
                                          <input
                                            type="number"
                                            value={item.nos}
                                            onChange={(e) => handleProductEdit(idx, 'nos', e.target.value)}
                                            className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                            onWheel={(e) => e.target.blur()}
                                          />
                                        </td>
                                        <td className="p-2 border-r border-slate-100">
                                          <input
                                            type="number"
                                            value={item.length}
                                            onChange={(e) => handleProductEdit(idx, 'length', e.target.value)}
                                            className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                            onWheel={(e) => e.target.blur()}
                                          />
                                        </td>
                                        <td className="p-2 border-r border-slate-100">
                                          <input
                                            type="number"
                                            value={item.breadth}
                                            onChange={(e) => handleProductEdit(idx, 'breadth', e.target.value)}
                                            className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                            onWheel={(e) => e.target.blur()}
                                          />
                                        </td>
                                        <td className="p-2 text-center text-sm font-bold text-slate-700 bg-slate-50 border-r border-slate-100">
                                          {computeWoodWorkQty(item.nos, item.length, item.breadth)}
                                        </td>
                                        <td className="p-2 border-r border-slate-100">
                                          <input
                                            type="text"
                                            value={item.gst_rate === 0 ? '' : item.gst_rate}
                                            onChange={(e) => handleProductEdit(idx, 'gst_rate', e.target.value)}
                                            className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                            placeholder="0"
                                          />
                                        </td>
                                        <td className="p-2 text-right text-sm font-black text-[#0A5F38] bg-slate-50 pr-4 border-r border-slate-100">
                                          ₹{((item.amount || 0) + (item.gst_amount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                        </td>
                                        <td className="p-2 text-center">
                                          <button
                                            onClick={() => handleRemoveProduct(idx)}
                                            className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  });

                                  // Add Row for this Category
                                  rows.push(
                                    <tr key={'add-row-' + catLabel} className="bg-white border-b border-black">
                                       <td colSpan="10" className="p-2">
                                          <button
                                            onClick={() => {
                                              const newItem = {
                                                categoryLabel: catLabel,
                                                product_name: '',
                                                unit_price: 1000,
                                                nos: 1,
                                                length: 1,
                                                breadth: 1,
                                                quantity: 1,
                                                gst_rate: 0,
                                                amount: 1000,
                                                total_amount: 1000,
                                                unit_type: 'SFT'
                                              };
                                              setEditingProducts([...editingProducts, newItem]);
                                            }}
                                            className="flex items-center gap-2 text-[10px] font-bold text-[#0A5F38] hover:bg-[#0A5F38] hover:text-white transition-all px-4 py-1 rounded border border-[#0A5F38]"
                                          >
                                            <Plus className="w-3 h-3" />
                                            ADD ITEM TO {catLabel}
                                          </button>
                                       </td>
                                    </tr>
                                  );
                                });

                                return rows;
                              })()}
                            </tbody>
                            <tfoot className={modalTable.tfoot}>
                              <tr className="border-t-2 border-black">
                                 <td colSpan="8" className="p-4 text-right font-bold text-slate-700 uppercase">Grand Total:</td>
                                 <td className="p-4 text-right font-black text-[#0A5F38] text-xl italic">
                                   ₹{editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                 </td>
                                 <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <div className={modalTable.wrap}>
                          <table className={modalTable.table}>
                            <thead className={modalTable.thead}>
                              <tr>
                                <th className={`${modalTable.th} w-[170px]`}>Category</th>
                                <th className={`${modalTable.th} min-w-[240px]`}>Product</th>
                                <th className={`${modalTable.thCenter} w-[100px]`}>Units</th>
                                <th className={`${modalTable.thCenter} w-[90px]`}>Qty</th>
                                <th className={`${modalTable.thRight} w-[120px]`}>Rate</th>
                                <th className={`${modalTable.thCenter} w-[100px]`}>HSN Code</th>
                                {editingApplyDiscount && <th className={`${modalTable.thCenter} w-[80px]`}>Disc %</th>}
                                <th className={`${modalTable.thCenter} w-[90px]`}>GST %</th>
                                <th className={`${modalTable.thRight} w-[130px]`}>Total</th>
                                <th className={`${modalTable.thCenter} w-[48px]`}><span className="sr-only">Actions</span></th>
                              </tr>
                            </thead>
                            <tbody className={modalTable.tbody}>
                              {editingProducts.map((item, index) => {
                                const productsInCategory = products.filter(p => String(p.categoryId || p.category_id) === String(item.category_id));
                                return (
                                  <tr key={index} className={modalTable.tr}>
                                    {/* Category */}
                                    <td className={modalTable.td}>
                                      <select
                                        value={item.category_id || ''}
                                        onChange={(e) => handleProductEdit(index, 'category_id', e.target.value)}
                                        className={modalTable.input}
                                      >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                          <option key={cat.id || cat._id} value={cat.id || cat._id}>{cat.name}</option>
                                        ))}
                                      </select>
                                    </td>

                                    {/* Product */}
                                    <td className={modalTable.td}>
                                      {item.isOthers && !item.product_id ? (
                                        /* "Others" category: free-text name or pick an existing product (merged from the old duplicate editor) */
                                        (() => {
                                          const othersProducts = products.filter(prod => {
                                            const selectedCat = String(item.category_id);
                                            const productCat = String(prod.categoryId || prod.category_id || '');
                                            return productCat === selectedCat;
                                          });

                                          if (othersProducts.length === 0 || item.showFreeText) {
                                            return (
                                              <div className="flex items-center gap-1">
                                                <input
                                                  type="text"
                                                  value={item.product_name || ''}
                                                  onChange={(e) => {
                                                    const updatedProducts = [...editingProducts];
                                                    updatedProducts[index].product_name = e.target.value;
                                                    setEditingProducts(updatedProducts);
                                                  }}
                                                  className={modalTable.input}
                                                  placeholder="Enter product name"
                                                  required
                                                />
                                                {othersProducts.length > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const updatedProducts = [...editingProducts];
                                                      updatedProducts[index].showFreeText = false;
                                                      updatedProducts[index].product_id = '';
                                                      updatedProducts[index].product_name = '';
                                                      setEditingProducts(updatedProducts);
                                                    }}
                                                    className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                                                    title="Show existing products"
                                                  >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                    </svg>
                                                  </button>
                                                )}
                                              </div>
                                            );
                                          }
                                          return (
                                            <select
                                              value={item.product_id || ''}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === 'ADD_NEW_OTHER') {
                                                  const updatedProducts = [...editingProducts];
                                                  updatedProducts[index] = {
                                                    ...updatedProducts[index],
                                                    showFreeText: true,
                                                    product_id: '',
                                                    product_name: ''
                                                  };
                                                  setEditingProducts(updatedProducts);
                                                  return;
                                                }
                                                const product = products.find(p => String(p.id || p._id) === String(val));
                                                if (product) {
                                                  handleProductSelect(index, val, product);
                                                }
                                              }}
                                              className={modalTable.input}
                                              required
                                            >
                                              <option value="">Select Option</option>
                                              <option value="ADD_NEW_OTHER" className="font-bold text-indigo-600">+ Add Other</option>
                                              {othersProducts.map(prod => (
                                                <option key={prod.id || prod._id} value={prod.id || prod._id}>
                                                  {prod.name}
                                                </option>
                                              ))}
                                            </select>
                                          );
                                        })()
                                      ) : item.showFreeText || item.product_id ? (
                                        <div className="flex items-center gap-1">
                                          <input
                                            type="text"
                                            value={item.product_name || ''}
                                            onChange={(e) => handleProductEdit(index, 'product_name', e.target.value)}
                                            className={modalTable.input}
                                            placeholder="Enter Product Name"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => handleProductFieldsEdit(index, {
                                              product_id: '',
                                              product_name: '',
                                              showFreeText: false
                                            })}
                                            className="p-1 text-slate-400 hover:text-red-500"
                                            title="Change product"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <select
                                          value={item.product_id || ''}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const product = products.find(p => String(p.id || p._id) === String(val));
                                            if (product) {
                                              handleProductSelect(index, val, product);
                                            }
                                          }}
                                          className={modalTable.input}
                                          disabled={!item.category_id}
                                        >
                                          <option value="">Select Product Type</option>
                                          {productsInCategory.map(prod => (
                                            <option key={prod.id || prod._id} value={prod.id || prod._id}>{prod.name}</option>
                                          ))}
                                        </select>
                                      )}
                                    </td>

                                    {/* Units */}
                                    <td className={modalTable.td}>
                                      <select
                                        value={item.unit_type || 'SFT'}
                                        onChange={(e) => handleProductEdit(index, 'unit_type', e.target.value)}
                                        className={`${modalTable.input} text-center`}
                                      >
                                        <option value="SFT">SFT</option>
                                        <option value="RFT">RFT</option>
                                        <option value="PCS">PCS</option>
                                        <option value="KG">KG</option>
                                        <option value="LTR">LTR</option>
                                        <option value="MTR">MTR</option>
                                        <option value="LUMPSUM">LUMPSUM</option>
                                        <option value="NOS">NOS</option>
                                        <option value="SQ.M">SQ.M</option>
                                      </select>
                                    </td>

                                    {/* Qty */}
                                    <td className={modalTable.td}>
                                      <input
                                        type="number"
                                        value={item.quantity || ''}
                                        onChange={(e) => handleProductEdit(index, 'quantity', e.target.value)}
                                        className={`${modalTable.input} text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                        placeholder="0"
                                      />
                                    </td>

                                    {/* Rate */}
                                    <td className={modalTable.td}>
                                      <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                                        <input
                                          type="number"
                                          value={item.unit_price || ''}
                                          onChange={(e) => handleProductEdit(index, 'unit_price', e.target.value)}
                                          className={`${modalTable.input} text-right pl-5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                          placeholder="0"
                                        />
                                      </div>
                                    </td>

                                    {/* HSN Code (merged from the old duplicate editor) */}
                                    <td className={modalTable.td}>
                                      <input
                                        type="text"
                                        value={item.hsn_code || ''}
                                        onChange={(e) => handleProductEdit(index, 'hsn_code', e.target.value)}
                                        className={`${modalTable.input} text-center`}
                                        placeholder="HSN"
                                      />
                                    </td>

                                    {/* Disc % */}
                                    {editingApplyDiscount && (
                                      <td className={modalTable.td}>
                                        <input
                                          type="number"
                                          value={item.discount || 0}
                                          onChange={(e) => handleProductEdit(index, 'discount', e.target.value)}
                                          className={`${modalTable.input} text-center text-amber-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                          placeholder="0"
                                        />
                                      </td>
                                    )}

                                    {/* GST % */}
                                    <td className={modalTable.td}>
                                      <select
                                        value={item.gst_rate || 0}
                                        onChange={(e) => handleProductEdit(index, 'gst_rate', e.target.value)}
                                        className={`${modalTable.input} text-center cursor-pointer`}
                                      >
                                        {[0, 5, 12, 18, 28].map(rate => (
                                          <option key={rate} value={rate}>{rate}%</option>
                                        ))}
                                      </select>
                                    </td>

                                    {/* Total */}
                                    <td className={modalTable.tdRight}>
                                      <div className="text-sm font-bold text-slate-900">
                                        ₹{(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        GST: ₹{(item.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                      </div>
                                    </td>

                                    {/* Actions */}
                                    <td className={modalTable.tdCenter}>
                                      <button
                                        onClick={() => handleRemoveProduct(index)}
                                        className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                                        title="Remove Product"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-20 bg-white border-2 border-dashed border-slate-200 rounded-2xl italic">
                      <div className="text-slate-400 mb-4 font-bold">No products found for this order</div>
                      <button
                        onClick={handleAddProduct}
                        className="px-6 py-2 bg-[#0A5F38] text-white rounded-xl font-bold hover:shadow-lg transition-all"
                      >
                        + Add First Product
                      </button>
                    </div>
                  )}
                </div>

                {/* Modal Footer Actions - Sectional UI for Wood Work */}
                <div className="mt-8 mb-4">
                  {isWoodWorkMode ? (
                    <div className="flex flex-col gap-4">
                      <div className="flex gap-4 p-4 bg-[#C5A57D]/5 rounded-2xl border-2 border-dashed border-[#C5A57D]/20">
                        <div className="flex-1">
                          <label className="block text-[10px] font-black text-[#C5A57D] uppercase mb-1 ml-1">New Section Heading</label>
                          <input
                            type="text"
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            className="w-full bg-white border border-[#C5A57D]/30 rounded-xl py-3 px-4 text-sm font-bold focus:ring-2 focus:ring-[#C5A57D] placeholder:font-medium uppercase"
                            placeholder="e.g., MASTER BEDROOM WARDROBE"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => {
                              if (!newSectionName.trim()) {
                                toast.error('Please enter a section name');
                                return;
                              }
                              const headerLabel = newSectionName.trim().toUpperCase();
                              const newItem = {
                                categoryLabel: headerLabel,
                                product_name: '',
                                unit_price: 1000,
                                nos: 1,
                                length: 1,
                                breadth: 1,
                                quantity: 1,
                                gst_rate: 0,
                                amount: 1000,
                                total_amount: 1000,
                                unit_type: 'SFT'
                              };
                              setEditingProducts([...editingProducts, newItem]);
                              setNewSectionName('');
                              toast.success('Section Added');
                            }}
                            className="bg-[#C5A57D] text-white px-8 py-3 rounded-xl font-black text-sm hover:shadow-lg hover:bg-[#B8985E] transition-all flex items-center gap-2 h-[46px]"
                          >
                            <Plus className="w-5 h-5" />
                            ADD NEW SECTION
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleAddProduct}
                      className="w-full px-4 py-4 border-2 border-dashed border-[#0A5F38]/20 bg-[#0A5F38]/5 rounded-xl text-[#0A5F38] hover:border-[#0A5F38] hover:bg-[#0A5F38]/10 hover:shadow-md transition-all font-bold flex items-center justify-center gap-2 group"
                    >
                      <Plus className="h-5 w-5 group-hover:scale-110 transition-transform" />
                      ADD PROJECT ITEM
                    </button>
                  )}
                </div>
            </AppModal>
          )
        }

        {/* Convert Closed Order to Invoice Modal */}
        {
          showConvertClosedOrderModal && (
            <AppModal
              open
              onClose={() => setShowConvertClosedOrderModal(false)}
              title="Convert Closed Order to Invoice"
              size="md"
              footer={(
                <button
                  onClick={() => setShowConvertClosedOrderModal(false)}
                  className={modalButton.secondary}
                >
                  Cancel
                </button>
              )}
            >
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#0A5F38]">Select a Closed Order</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={fetchClosedOrders}
                        className="text-xs bg-blue-50 text-[#0A5F38] px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                        title="Refresh closed order list"
                      >
                        🔄 Refresh
                      </button>
                      <div className="text-xs text-slate-500 bg-white px-2 py-1 rounded">
                        Only showing unconverted closed orders
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search closed orders..."
                      value={closedOrderSearchTerm}
                      onChange={handleClosedOrderSearch}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                    />
                  </div>

                  {filteredClosedOrders.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg">
                      <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-600 font-medium">No unconverted closed orders available</p>
                      <p className="text-slate-500 text-sm mt-1">All closed orders have already been converted to invoices</p>
                      <button
                        onClick={fetchClosedOrders}
                        className="mt-3 text-sm bg-blue-50 text-[#0A5F38] px-3 py-1 rounded hover:bg-blue-100 transition-colors"
                      >
                        🔄 Refresh List
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredClosedOrders.map(order => (
                        <div
                          key={order.id || order._id}
                          className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer transition-colors"
                          onClick={() => handleSelectClosedOrder(order)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-semibold text-slate-900">
                                #{order.closed_order_number || order.order_number || order._id?.slice(-8) || 'CO' + order.id}
                              </div>
                              <div className="text-sm text-slate-600">
                                Customer: {order.customer_name || 'Walk-in Customer'}
                              </div>
                              <div className="text-sm text-slate-600">
                                Date: {order.date ? new Date(order.date).toLocaleDateString('en-IN') : 'N/A'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-slate-900">
                                ₹{(order.totalAmount || order.total_amount || 0).toLocaleString()}
                              </div>
                              <div className="mt-2">
                                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Available for Conversion
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </AppModal>
          )
        }

        {/* Comprehensive Closed Order to Invoice Conversion Modal */}
        {
          showClosedOrderDetailModal && selectedClosedOrder && (
            <AppModal
              open
              onClose={() => setShowClosedOrderDetailModal(false)}
              title="Convert Closed Order to Invoice"
              size="xl"
              footer={(
                <>
                  <button
                    onClick={() => setShowClosedOrderDetailModal(false)}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClosedOrderInvoiceSubmit}
                    disabled={loading}
                    className={modalButton.primary}
                  >
                    {loading ? 'Creating Invoice...' : 'Create Invoice'}
                  </button>
                </>
              )}
            >
                {/* Closed Order Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#0A5F38] mb-3">Closed Order Summary</h3>
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Closed Order Number</div>
                      <div className="font-semibold">
                        {selectedClosedOrder.closed_order_number || selectedClosedOrder.order_number || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Closed Order Date</div>
                      <div className="font-semibold">
                        {selectedClosedOrder.date ? new Date(selectedClosedOrder.date).toLocaleDateString('en-IN') : selectedClosedOrder.createdAt ? new Date(selectedClosedOrder.createdAt).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Total Amount</div>
                      <div className="font-semibold">
                        ₹{(selectedClosedOrder.totalAmount || selectedClosedOrder.total_amount || 0).toFixed(1)}
                      </div>
                    </div>

                    {selectedClosedOrder.converted_by_name && (
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Converted By</div>
                        <div className="font-semibold">
                          {selectedClosedOrder.converted_by_name}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
                      <input
                        type="date"
                        value={conversionPaymentData.date || ''}
                        onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, date: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm font-semibold"
                      />
                    </div>
                  </div>
                </div>

                {/* Invoice Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#0A5F38] mb-3">Invoice Information</h3>
                  <div className="bg-white rounded-lg p-4 border-2 border-black">
                    <div className="max-w-md">
                      <InvoiceNumberField
                        value={invoiceNumber}
                        onChange={setInvoiceNumber}
                        label="Invoice Number"
                        placeholder="Generating..."
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-[#0A5F38]">Customer Information</h3>
                    <button
                      onClick={() => {
                        if (!editMode) {
                          setConversionEditingCustomer({
                            name: selectedClosedOrder.customer_name || '',
                            phone: selectedClosedOrder.customer_phone || '',
                            email: selectedClosedOrder.customer_email || '',
                            address: selectedClosedOrder.customer_address || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editMode ? (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={conversionEditingCustomer.name || ''}
                          onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                        <input
                          type="text"
                          value={conversionEditingCustomer.phone || ''}
                          onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={conversionEditingCustomer.email || ''}
                          onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, email: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                        <textarea
                          value={conversionEditingCustomer.address || ''}
                          onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, address: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <select
                          value={conversionEditingCustomer.state || ''}
                          onChange={(e) => setConversionEditingCustomer({ ...conversionEditingCustomer, state: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                        <div className="font-semibold">{conversionEditingCustomer.name || selectedClosedOrder.customer_name || 'Walk-in Customer'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                        <div className="font-semibold">{conversionEditingCustomer.phone || selectedClosedOrder.customer_phone || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                        <div className="font-semibold">{conversionEditingCustomer.email || selectedClosedOrder.customer_email || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                        <div className="font-semibold">{conversionEditingCustomer.address || selectedClosedOrder.customer_address || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                        <div className="font-semibold">{conversionEditingCustomer.state || selectedClosedOrder.customer?.state || selectedClosedOrder.party_state_name || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#0A5F38] mb-3">Delivery Information</h3>
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Dispatched From</label>
                      <input
                        type="text"
                        value={conversionPaymentData.dispatched_from || ''}
                        onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, dispatched_from: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        placeholder="Enter dispatch location"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Delivered To</label>
                      <input
                        type="text"
                        value={conversionPaymentData.delivered_to || ''}
                        onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, delivered_to: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        placeholder="Enter delivery location"
                      />
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-[#0A5F38]">Product Details</h3>
                    <div className="space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            console.log('🔄 Loading categories and products for Edit (Closed Order)...');
                            const formData = await getFormData();

                            if (!formData.categories || formData.categories.length === 0) {
                              toast.error('No categories found. Please add categories first.');
                              return;
                            }

                            if (!formData.products || formData.products.length === 0) {
                              toast.error('No products found. Please add products first.');
                              return;
                            }

                            // Update state with loaded data
                            setCategories(formData.categories || []);
                            setProducts(formData.products || []);

                            // Prepare items with proper IDs
                            const itemsWithIds = (selectedClosedOrder.items || []).map(item => ({
                              ...item,
                              category_id: item.category_id || '',
                              product_id: item.product_id || ''
                            }));

                            console.log('🔍 Prepared closed order items for editing:', itemsWithIds);

                            setEditingProducts(recalcItemsAmounts(itemsWithIds));
                            // Detect Wood Work mode
                            setIsWoodWorkMode(!!selectedClosedOrder?.isWoodWork);
                            setEditModalContext('convert_closed_order');
                            // Check if any item has discount > 0 to set initial toggle state
                            const hasDiscount = itemsWithIds.some(item => (item.discount || 0) > 0);
                            setEditingApplyDiscount(hasDiscount);
                            setShowEditProductsModal(true);
                          } catch (error) {
                            console.error('❌ Failed to load categories and products:', error);
                            toast.error('Failed to load product data: ' + error.message);
                          }
                        }}
                        className="px-3 py-1 text-sm bg-blue-50 text-[#0A5F38] rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            console.log('🔄 Loading categories and products for Add Product (Closed Order)...');
                            const formData = await getFormData();

                            if (!formData.categories || formData.categories.length === 0) {
                              toast.error('No categories found. Please add categories first.');
                              return;
                            }

                            if (!formData.products || formData.products.length === 0) {
                              toast.error('No products found. Please add products first.');
                              return;
                            }

                            // Update state with loaded data
                            setCategories(formData.categories || []);
                            setProducts(formData.products || []);

                            // Prepare existing items with proper IDs and add a new empty item
                            const itemsWithIds = (selectedClosedOrder.items || []).map(item => ({
                              ...item,
                              category_id: item.category_id || '',
                              product_id: item.product_id || ''
                            }));

                            // Add a new empty item for adding products
                            itemsWithIds.push({
                              category_id: '',
                              product_id: '',
                              product_name: '',
                              quantity: 1,
                              unit_price: 0,
                              gst_rate: 18,
                              discount: 0,
                              unit_type: 'SFT',
                              hsn_code: ''
                            });

                            console.log('🔍 Prepared closed order items for editing (with new item):', itemsWithIds);

                            setEditingProducts(recalcItemsAmounts(itemsWithIds));
                            // Detect Wood Work mode from the invoice itself
                            setIsWoodWorkMode(!!selectedClosedOrder?.isWoodWork);
                            setEditModalContext('convert_closed_order');
                            // Check if any item has discount > 0 to set initial toggle state
                            const hasDiscount = itemsWithIds.some(item => (item.discount || 0) > 0);
                            setEditingApplyDiscount(hasDiscount);
                            setShowEditProductsModal(true);
                          } catch (error) {
                            console.error('❌ Failed to load categories and products:', error);
                            toast.error('Failed to load product data: ' + error.message);
                          }
                        }}
                        className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Add Product
                      </button>
                    </div>
                  </div>
                    {isWoodWorkMode ? (
                      /* ══ WOOD WORK MODE TABLE ══ */
                      <div className="space-y-6">
                        {(() => {
                          const categories = [...new Set((selectedClosedOrder.items || []).map(item => item.categoryLabel || 'General'))];
                          return categories.map((catLabel, catIdx) => {
                            const catItems = (selectedClosedOrder.items || []).filter(item => (item.categoryLabel || 'General') === catLabel);
                            return (
                              <div key={catIdx} className="bg-white border border-[#E6DFC6] rounded-xl overflow-hidden">
                                <div className="bg-[#F8F5E6] px-4 py-2 border-b border-[#E6DFC6] flex justify-between items-center">
                                  <h4 className="font-bold text-[#0A5F38] uppercase text-sm tracking-wider">{catLabel}</h4>
                                  <span className="text-xs font-medium text-slate-500">{catItems.length} Items</span>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className={modalTable.table}>
                                          <thead className={modalTable.thead}>
                                            <tr>
                                              <th className={modalTable.th}>Product</th>
                                              <th className={modalTable.thCenter}>Nos</th>
                                              <th className={modalTable.thCenter}>L</th>
                                              <th className={modalTable.thCenter}>B</th>
                                              <th className={modalTable.thCenter}>Qty</th>
                                              <th className={modalTable.thRight}>Rate</th>
                                              <th className={modalTable.thCenter}>GST %</th>
                                              <th className={modalTable.thRight}>Total</th>
                                            </tr>
                                          </thead>
                                    <tbody className={`${modalTable.tbody} italic`}>
                                    {catItems.map((item, idx) => {
                                      const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                      const baseAmount = roundForCalculation(qty * (item.unit_price || 0));
                                      const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                                      const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                                      const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                                      const totalAmount = roundForCalculation(taxableAmount + gstAmount);

                                      return (
                                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                          <td className="px-4 py-2 text-sm font-bold text-slate-900">{item.product_name}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.nos || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.length || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.breadth || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm font-black text-[#0A5F38]">{qty}</td>
                                          <td className="px-4 py-2 text-right text-sm text-slate-900 font-bold">₹{(item.unit_price || 0).toLocaleString()}</td>
                                          <td className="px-4 py-2 text-center text-sm text-slate-600 font-bold">{item.gst_rate || 0}%</td>
                                          <td className="px-4 py-2 text-right text-sm font-black text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <div className={modalTable.wrap}>
                      <table className={modalTable.table}>
                        <thead className={modalTable.thead}>
                          <tr>
                            <th className={modalTable.th}>Category</th>
                            <th className={modalTable.th}>Product</th>
                            <th className={modalTable.thCenter}>Units</th>
                            <th className={modalTable.thCenter}>Quantity</th>
                            <th className={modalTable.thRight}>Rate</th>
                            <th className={modalTable.thCenter}>Disc %</th>
                            <th className={modalTable.thRight}>Taxable</th>
                            <th className={modalTable.thCenter}>GST %</th>
                            <th className={modalTable.thRight}>GST Rs</th>
                            <th className={modalTable.thRight}>Total</th>
                          </tr>
                        </thead>
                        <tbody className={modalTable.tbody}>
                          {selectedClosedOrder.items && selectedClosedOrder.items.map((item, index) => {
                            const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id || item.categoryId));
                            const categoryName = item.category_name || category?.name || 'N/A';
                            const baseAmount = item.quantity * item.unit_price;
                            const discountAmount = baseAmount * ((item.discount || 0) / 100);
                            const taxableAmount = baseAmount - discountAmount;
                            const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                            const totalAmount = taxableAmount + gstAmount;

                            return (
                              <tr key={index} className={modalTable.tr}>
                                <td className={modalTable.td}>{categoryName}</td>
                                <td className={modalTable.td}>
                                  <div className="font-medium text-slate-900">{item.product_name || 'N/A'}</div>
                                  {item.hsn_code && <div className="text-xs text-slate-500">HSN: {item.hsn_code}</div>}
                                </td>
                                <td className={modalTable.tdCenter}>{item.unit_type || 'SFT'}</td>
                                <td className={`${modalTable.tdCenter} font-medium`}>{item.quantity || 0}</td>
                                <td className={modalTable.tdRight}>₹{(item.unit_price || 0).toLocaleString()}</td>
                                <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                                <td className={modalTable.tdRight}>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className={modalTable.tdCenter}>{item.gst_rate || 0}%</td>
                                <td className={modalTable.tdRight}>₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    )}
                </div>

                {/* GST Number */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#0A5F38] mb-3">GST Number</h3>
                  <div className="bg-white rounded-lg p-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Customer GST Number</label>
                      <input
                        type="text"
                        value={conversionPaymentData.gst_number}
                        onChange={(e) => handleGSTFieldChange(e.target.value,
                          (gstValue) => setConversionPaymentData({ ...conversionPaymentData, gst_number: gstValue }),
                          (stateName) => setConversionEditingCustomer(prev => ({ ...prev, state: stateName }))
                        )}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        placeholder="Enter customer GST number"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        GST number affects tax calculation (Same State vs Inter State)
                      </p>
                    </div>
                  </div>
                </div>

                {/* GST Breakdown (Same State) */}
                <div className="mb-6">
                  <div className="bg-white rounded-lg p-4">
                    {companySettings ? (
                      <GSTBreakdown
                        items={selectedClosedOrder.items || []}
                        buyerGST={conversionPaymentData.gst_number}
                        companySettings={companySettings}
                      />
                    ) : (
                      <div className="text-slate-500">Loading GST breakdown...</div>
                    )}
                  </div>
                </div>

                {/* Payment & Invoice Details */}
                <div className="bg-[#FDFCEB] p-5 rounded-xl border border-[#0A5F38]/20 shadow-sm space-y-6">
                  <h3 className="text-lg font-bold text-[#0A5F38] flex items-center">
                    <Calculator className="w-5 h-5 mr-2" />
                    Payment Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Card: Payment Summary */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-[#0A5F38] font-bold mb-4 flex items-center text-sm">
                        <DollarSign className="w-4 h-4 mr-2" /> Payment Summary
                      </h4>
                      {(() => {
                        let totalAmount = 0;
                        try {
                          if (companySettings && selectedClosedOrder.items) {
                            const gstBreakdown = calculateTotalGSTBreakdown(
                              selectedClosedOrder.items,
                              conversionPaymentData.gst_number,
                              companySettings
                            );
                            totalAmount = Number(gstBreakdown?.grandTotal || 0);
                          }
                        } catch (error) {
                          totalAmount = 0;
                        }

                        const alreadyPaid = selectedClosedOrder.amountReceived || selectedClosedOrder.amount_received || 0;
                        const additionalPayment = parseFloat(conversionPaymentData.amount_received) || 0;
                        const finalBalance = Math.max(0, totalAmount - (alreadyPaid + additionalPayment));

                        return (
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Total Invoice Amount:</span>
                              <span className="font-semibold text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-[#16a34a] font-medium">Previously Paid:</span>
                              <span className="text-[#16a34a]">- ₹{alreadyPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="border-t border-slate-100 pt-3 flex justify-between text-sm">
                              <span className="font-bold text-slate-900">Remaining Balance:</span>
                              <span className="font-bold text-slate-900">₹{(totalAmount - alreadyPaid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>

                            <div className="flex justify-between text-base pt-1">
                              <span className="font-bold text-slate-900">Final Balance:</span>
                              <span className="font-bold text-[#dc2626]">
                                ₹{finalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Right Card: New Payment Entry */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-slate-900 font-bold mb-4 text-sm">New Payment Entry</h4>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Mode</label>
                          <select
                            value={conversionPaymentData.payment_type}
                            onChange={(e) => setConversionPaymentData({ ...conversionPaymentData, payment_type: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                          >
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="phonepe">PhonePe</option>
                            <option value="google_pay">Google Pay</option>
                            <option value="paytm">Paytm</option>
                            <option value="net_banking">Net Banking</option>
                            <option value="card">Card</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                            Amount
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-slate-400 text-sm">₹</span>
                            <input
                              type="number"
                              value={conversionPaymentData.amount_received || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || parseFloat(val) >= 0) {
                                  const additionalAmount = parseFloat(val) || 0;
                                  const alreadyPaid = selectedClosedOrder.amountReceived || selectedClosedOrder.amount_received || 0;

                                  // Calculate total
                                  let totalAmount = 0;
                                  if (selectedClosedOrder.items && selectedClosedOrder.items.length > 0) {
                                    const gstBreakdown = calculateTotalGSTBreakdown(
                                      selectedClosedOrder.items,
                                      conversionPaymentData.gst_number,
                                      companySettings
                                    );
                                    totalAmount = Number(gstBreakdown?.grandTotal || 0);
                                  }

                                  const remainingAmount = Math.max(0, totalAmount - alreadyPaid);

                                  // Validate
                                  if (additionalAmount > Number(remainingAmount.toFixed(2))) {
                                    toast.error(`Additional payment cannot exceed remaining amount of ₹${remainingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                                    return;
                                  }

                                  const cumulativePayment = alreadyPaid + additionalAmount;
                                  let status = 'pending';
                                  const tolerance = 0.01;

                                  if (cumulativePayment === 0) {
                                    status = 'pending';
                                  } else if (Math.abs(cumulativePayment - totalAmount) < tolerance || cumulativePayment > totalAmount) {
                                    status = 'completed';
                                  } else if (cumulativePayment > 0 && cumulativePayment < totalAmount) {
                                    status = 'partial';
                                  }

                                  setConversionPaymentData({
                                    ...conversionPaymentData,
                                    amount_received: val,
                                    payment_status: status
                                  });
                                }
                              }}
                              className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                              placeholder="0.00"
                              step="any"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                {/* Terms and Conditions & Material Description */}
                <div className="mt-6 space-y-6">
                  <TermsAndConditionsInput
                    value={conversionPaymentData.termsAndConditions}
                    onChange={(value) => setConversionPaymentData({ ...conversionPaymentData, termsAndConditions: value })}
                    label="Terms and Conditions"
                  />
                  
                  {selectedClosedOrder?.isWoodWork && (
                    <TermsAndConditionsInput
                      value={conversionPaymentData.materialDescription}
                      onChange={(value) => setConversionPaymentData({ ...conversionPaymentData, materialDescription: value })}
                      label="Material Description"
                      placeholder="Enter material specifications..."
                    />
                  )}
                </div>
            </AppModal>
          )
        }



        {/* Cancellation Modal */}
        <CancellationModal
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
          onConfirm={handleConfirmCancellation}
          title="Cancel Invoice"
          itemType="invoice"
          itemNumber={selectedInvoice?.invoice_number}
          loading={cancellationLoading}
          currentPaymentStatus={selectedInvoice?.paymentStatus || selectedInvoice?.payment_status}
        />

        {/* Payment Status Update Modal for Cancelled Invoices */}
        {
          showPaymentStatusUpdateModal && (
            <AppModal
              open
              onClose={() => {
                setShowPaymentStatusUpdateModal(false);
                setPaymentStatusUpdate('');
              }}
              title="Update Payment Status"
              size="sm"
              layer="top"
              footer={(
                <>
                  <button
                    onClick={() => {
                      setShowPaymentStatusUpdateModal(false);
                      setPaymentStatusUpdate('');
                    }}
                    disabled={paymentStatusUpdateLoading}
                    className={`${modalButton.secondary} disabled:opacity-50`}
                  >
                    Close
                  </button>
                  <button
                    onClick={handleUpdatePaymentStatus}
                    disabled={paymentStatusUpdateLoading || !paymentStatusUpdate}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    {paymentStatusUpdateLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </>
                    ) : (
                      'Update Status'
                    )}
                  </button>
                </>
              )}
            >
                <div className="mb-6">
                  <p className="text-slate-700 mb-4">
                    Update the payment status for this cancelled invoice.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Payment Status
                    </label>
                    <select
                      value={paymentStatusUpdate}
                      onChange={(e) => setPaymentStatusUpdate(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select payment status...</option>
                      <option value="refund_initiated">Refund Initiated – Refund process started</option>
                      <option value="refund_completed">Refund Completed – Refund processed successfully</option>
                      <option value="no_payment">Cancelled (No Payment) – No refund needed</option>
                    </select>
                  </div>

                  {selectedInvoice?.cancellation_payment_status && (
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <p className="text-sm text-slate-600">
                        <strong>Current Status:</strong> {
                          selectedInvoice.cancellation_payment_status === 'refund_initiated' && 'Refund Initiated – Refund process started'
                        }
                        {selectedInvoice.cancellation_payment_status === 'refund_completed' && 'Refund Completed – Refund processed successfully'}
                        {selectedInvoice.cancellation_payment_status === 'no_payment' && 'Cancelled (No Payment) – No refund needed'}
                      </p>
                    </div>
                  )}
                </div>
            </AppModal>
          )
        }

        {/* Edit Invoice Modal */}
        {
          showEditInvoiceModal && editingInvoice && (
            <AppModal
              open
              onClose={() => setShowEditInvoiceModal(false)}
              title="Edit Invoice"
              size="lg"
              footer={(
                <>
                  <button
                    onClick={() => setShowEditInvoiceModal(false)}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveInvoiceEdits}
                    disabled={loading}
                    className={modalButton.primary}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </>
              )}
            >
                {/* Invoice Summary */}
                <div className="mb-6 p-4 bg-white rounded-lg border-2 border-black shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide">Invoice Summary</h3>
                    <button
                      onClick={() => setEditSummaryMode(!editSummaryMode)}
                      className="text-[#0A5F38] hover:text-[#084B2C] text-sm font-bold flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      {editSummaryMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Invoice Number</div>
                      <div className="font-semibold text-gray-800">
                        {editingInvoice.invoice_number || editingInvoice.invoiceNumber || editingInvoice._id?.slice(-8) || 'INV' + editingInvoice.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Date</div>
                      {editSummaryMode ? (
                        <input
                          type="date"
                          value={editingInvoice.date ? new Date(editingInvoice.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                          onChange={(e) => setEditingInvoice({ ...editingInvoice, date: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#C5A57D] rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm bg-white"
                        />
                      ) : (
                        <div className="font-semibold text-gray-800">
                          {editingInvoice.date ? new Date(editingInvoice.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Created By</div>
                      <div className="font-semibold text-gray-800">
                        {editingInvoice.salesperson?.name || editingInvoice.salesperson_name || (user && user.role === 'admin' ? user.name : 'N/A')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                    <button
                      onClick={() => {
                        if (!editMode) {
                          setEditingCustomer({
                            name: editingInvoice.customer_name || editingInvoice.customer?.name || '',
                            phone: editingInvoice.customer_phone || editingInvoice.customer?.phone || '',
                            email: editingInvoice.customer_email || editingInvoice.customer?.email || '',
                            address: editingInvoice.customer_address || editingInvoice.customer?.address || '',
                            gstNumber: editingInvoice.customer_gst || editingInvoice.customer?.gstNumber || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className="px-4 py-2 text-sm bg-[#0A5F38] text-white rounded-lg hover:bg-[#0d7a4a] transition-colors font-medium"
                    >
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editMode ? (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Customer Name</label>
                        <input
                          type="text"
                          value={editingCustomer.name || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                        <input
                          type="text"
                          value={editingCustomer.phone || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={editingCustomer.email || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                        <input
                          type="text"
                          value={editingCustomer.gstNumber || ''}
                          onChange={(e) => handleGSTFieldChange(e.target.value,
                            (gstValue) => setEditingCustomer({ ...editingCustomer, gstNumber: gstValue }),
                            (stateName) => setEditingCustomer(prev => ({ ...prev, state: stateName }))
                          )}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          placeholder="e.g. 29ABCDE1234F1Z5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <select
                          value={editingCustomer.state || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, state: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                        <textarea
                          value={editingCustomer.address || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Dispatched from</label>
                        <input
                          type="text"
                          value={editingInvoice.dispatched_from || ''}
                          onChange={(e) => setEditingInvoice({ ...editingInvoice, dispatched_from: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          placeholder="Enter dispatch location"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Delivered to</label>
                        <input
                          type="text"
                          value={editingInvoice.delivered_to || ''}
                          onChange={(e) => setEditingInvoice({ ...editingInvoice, delivered_to: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          placeholder="Enter delivery location"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                        <div className="font-semibold">{editingCustomer.name || editingInvoice.customer_name || 'Walk-in Customer'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                        <div className="font-semibold">{editingCustomer.phone || editingInvoice.customer_phone || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                        <div className="font-semibold">{editingCustomer.email || editingInvoice.customer_email || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                        <div className="font-semibold">{editingCustomer.address || editingInvoice.customer_address || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                        <div className="font-semibold">{editingCustomer.state || editingInvoice.customer?.state || editingInvoice.buyerStateName || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Dispatched from</div>
                        <div className="font-semibold">{editingInvoice.dispatched_from || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Delivered to</div>
                        <div className="font-semibold">{editingInvoice.delivered_to || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                    {user && user.role === 'admin' && (
                      <button
                        onClick={async () => {
                          // Fetch data locally to avoid state staleness issues during mapping
                          let localCategories = categories;
                          let localProducts = products;

                          if (!localCategories.length || !localProducts.length) {
                            try {
                              const data = await getFormData();
                              localCategories = data.categories || [];
                              localProducts = data.products || [];
                              setCategories(localCategories);
                              setProducts(localProducts);
                            } catch (err) {
                              console.error("Failed to load products/categories", err);
                            }
                          }

                          setEditModalContext('edit');

                          // Prepare products for editing using robust mapping
                          const itemsToMap = (editingInvoice ? editingInvoice.items : []) || [];
                          const itemsWithIds = recalcItemsAmounts(mapItemsForEditing(itemsToMap, localCategories, localProducts));

                          console.log('✨ Mapped items for edit modal:', itemsWithIds);
                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                          setIsWoodWorkMode(!!(editingInvoice?.isWoodWork || editingInvoice?.is_woodwork || editingInvoice?.is_wood_work));
                          setEditModalContext('edit');
                          setShowEditProductsModal(true);
                        }}
                        className="px-4 py-2 text-sm bg-[#E67E22] text-white rounded-lg hover:bg-[#d5731e] transition-colors font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                    {isWoodWorkMode ? (
                      /* ══ WOOD WORK EDIT MODAL TABLE ══ */
                      <div className="space-y-6">
                        {(() => {
                          const items = (editingProducts.length > 0 ? editingProducts : editingInvoice.items || []);
                          const categories = [...new Set(items.map(item => item.categoryLabel || item.category_label || item.template_category || 'General'))];
                          
                          return categories.map((catLabel, catIdx) => {
                            const catItems = items.filter(item => (item.categoryLabel || item.category_label || item.template_category || 'General') === catLabel);
                            return (
                              <div key={catIdx} className="bg-white border border-[#E6DFC6] rounded-xl overflow-hidden">
                                <div className="bg-[#F8F5E6] px-4 py-2 border-b border-[#E6DFC6] flex justify-between items-center">
                                  <h4 className="font-bold text-[#0A5F38] uppercase text-sm tracking-wider">{catLabel}</h4>
                                  <span className="text-xs font-medium text-slate-500">{catItems.length} Items</span>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className={modalTable.table}>
                                          <thead className={modalTable.thead}>
                                            <tr>
                                              <th className={modalTable.th}>Product</th>
                                              <th className={modalTable.thCenter}>Nos</th>
                                              <th className={modalTable.thCenter}>L</th>
                                              <th className={modalTable.thCenter}>B</th>
                                              <th className={modalTable.thCenter}>Qty</th>
                                              <th className={modalTable.thRight}>Rate</th>
                                              <th className={modalTable.thCenter}>GST %</th>
                                              <th className={modalTable.thRight}>Total</th>
                                            </tr>
                                          </thead>
                                    <tbody className={`${modalTable.tbody} italic`}>
                                    {catItems.map((item, idx) => {
                                      const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                      const rate = item.unit_price || 0;
                                      const baseAmount = roundForCalculation(qty * rate);
                                      const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                                      const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                                      const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                                      const totalAmount = roundForCalculation(taxableAmount + gstAmount);

                                      return (
                                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                          <td className="px-4 py-2 text-sm font-bold text-slate-900">{item.product_name}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.nos || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.length || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm text-slate-600 font-bold">{item.breadth || 0}</td>
                                          <td className="px-2 py-2 text-center text-sm font-black text-[#0A5F38]">{qty}</td>
                                          <td className="px-4 py-2 text-right text-sm text-slate-900 font-bold">₹{rate.toLocaleString()}</td>
                                          <td className="px-4 py-2 text-center text-sm text-slate-600 font-bold">{item.gst_rate || 0}%</td>
                                          <td className="px-4 py-2 text-right text-sm font-black text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      /* ══ STANDARD MODE TABLE ══ */
                      <div className={modalTable.wrap}>
                      <table className={modalTable.table}>
                        <thead className={modalTable.thead}>
                          <tr>
                            <th className={modalTable.th}>Category</th>
                            <th className={modalTable.th}>Product</th>
                            <th className={modalTable.thCenter}>Units</th>
                            <th className={modalTable.thCenter}>Quantity</th>
                            <th className={modalTable.thRight}>Unit Price</th>
                            {(editingProducts.length > 0 ? editingProducts : editingInvoice.items || []).some(i => (i.discount || 0) > 0) && (
                              <th className={modalTable.thCenter}>Disc %</th>
                            )}
                            <th className={modalTable.thRight}>Amount</th>
                            <th className={modalTable.thCenter}>GST %</th>
                            <th className={modalTable.thRight}>Total</th>
                          </tr>
                        </thead>
                        <tbody className={modalTable.tbody}>
                          {(editingProducts.length > 0 ? editingProducts : editingInvoice.items || []).map((item, index) => {
                            const baseAmount = item.quantity * item.unit_price;
                            const discountAmount = baseAmount * ((item.discount || 0) / 100);
                            const taxableAmount = baseAmount - discountAmount;
                            const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                            const totalAmount = taxableAmount + gstAmount;
                            const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id || item.categoryId));
                            const categoryName = category?.name || item.category_name || item.productId?.product?.categoryId?.name || 'N/A';
                            const unitLabel = item.unit_type || item.unit || item.units || 'SFT';

                            return (
                              <tr key={index} className={modalTable.tr}>
                                <td className={modalTable.td}>{categoryName}</td>
                                <td className={modalTable.td}>
                                  <div className="font-medium text-slate-900 whitespace-pre-wrap">{item.product_name}</div>
                                </td>
                                <td className={modalTable.tdCenter}>{unitLabel}</td>
                                <td className={modalTable.tdCenter}>{item.quantity}</td>
                                <td className={modalTable.tdRight}>₹{parseFloat(item.unit_price).toLocaleString()}</td>
                                {(editingProducts.length > 0 ? editingProducts : editingInvoice.items || []).some(i => (i.discount || 0) > 0) && (
                                  <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                                )}
                                <td className={modalTable.tdRight}>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className={`${modalTable.tdCenter} font-medium`}>{item.gst_rate || 0}%</td>
                                <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    )}
                </div>

                {/* Terms and Conditions */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Terms and Conditions</h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-indigo-100">
                    <textarea
                      value={editingInvoice.termsAndConditions || ''}
                      onChange={(e) => setEditingInvoice({ ...editingInvoice, termsAndConditions: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                      placeholder="Enter terms and conditions..."
                    />
                  </div>
                </div>

                {/* Total Summary */}
                <div className="mb-6 bg-white rounded-lg p-4">
                  <div className="flex justify-end">
                    <div className="w-64">
                      <div className="flex justify-between py-2 text-lg font-bold border-t border-slate-300">
                        <span>Grand Total:</span>
                        <span>₹{calculateInvoiceTotal(editingProducts.length > 0 ? editingProducts : editingInvoice.items || []).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
            </AppModal>
          )
        }

        {/* Delete Confirmation Modal */}
        {
          showDeleteModal && deletingInvoice && (
            <AppModal
              open
              onClose={() => { setShowDeleteModal(false); setDeletingInvoice(null); }}
              title="Delete Invoice"
              size="sm"
              layer="top"
              footer={(
                <>
                  <button
                    onClick={() => { setShowDeleteModal(false); setDeletingInvoice(null); }}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteInvoice}
                    className={modalButton.danger}
                  >
                    Delete
                  </button>
                </>
              )}
            >
              <div className="text-center py-2">
                <p className="text-slate-900 font-medium mb-2">
                  Are you sure you want to delete invoice <span className="font-bold">{deletingInvoice.invoice_number}</span>?
                </p>
                <p className="text-slate-600">
                  This action cannot be undone.
                </p>
              </div>
            </AppModal>
          )
        }
        {/* PO Upload Modal */}
        {showPOUploadModal && (
          <AppModal
            open
            onClose={() => setShowPOUploadModal(false)}
            title="AI Purchase Order Reader"
            subtitle="Upload a PO to auto-fill the invoice"
            icon={<Upload className="h-5 w-5" />}
            size="md"
            layer="top"
            footer={(
              <button
                onClick={handlePOUpload}
                disabled={!poFile || poExtracting}
                className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-colors ${
                  !poFile || poExtracting
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {poExtracting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    AI is reading your PO...
                  </>
                ) : (
                  <>
                    <Calculator className="h-5 w-5" />
                    Extract & Auto-Fill Invoice
                  </>
                )}
              </button>
            )}
          >
                <div 
                  className={`border-4 border-dashed rounded-3xl p-10 text-center transition-all duration-300 ${
                    poFile ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                  }`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) setPoFile(file);
                  }}
                >
                  <input
                    type="file"
                    id="po-upload"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setPoFile(e.target.files[0])}
                  />
                  
                  {poFile ? (
                    <div className="space-y-4">
                      <div className="bg-white p-4 rounded-2xl shadow-sm border border-indigo-100 flex items-center space-x-3 inline-block mx-auto max-w-xs">
                        <FileText className="h-6 w-6 text-indigo-600" />
                        <span className="text-sm font-semibold text-slate-700 truncate">{poFile.name}</span>
                        <button onClick={() => setPoFile(null)} className="text-red-500 hover:text-red-700">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">Ready to extract data</p>
                    </div>
                  ) : (
                    <label htmlFor="po-upload" className="cursor-pointer space-y-4 block">
                      <div className="bg-indigo-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <Upload className="h-8 w-8 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-slate-800">Drag & Drop PO here</p>
                        <p className="text-sm text-slate-500">or click to browse from files</p>
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-widest pt-4">
                        Supported: PDF, PNG, JPG (Max 10MB)
                      </div>
                    </label>
                  )}
                </div>
                <p className="mt-4 text-center text-[11px] text-slate-400 italic">
                  AI extraction may take 10-15 seconds. Review all fields after extraction.
                </p>
          </AppModal>
        )}
      </div>
    </div>

  );
};

export default InvoicesPage;





